import {
	createTransaction as createTransactionRemote,
	patchTransaction as patchTransactionRemote,
	buildTransactionRecord,
	queueExternalWrite,
	flushExternalQueue,
	adjustCapTable,
	getExternalClassTransactions,
	getCachedCertColumn,
} from '../services/TransactionService.js';
import { wfExecutionsProgressURL } from './api-urls.js';
import { balanceTolerance, nearlyEqualBalance, nearlyEqualTrancheSum, isTrancheEligibleSecurityType, isLLProducts } from './client-flags.js';
import { AppState } from './app-state.js';
import { getEffectiveDenomination } from './denomination-resolver.js';
import { resolveDescriptionTemplate } from './string-utils.js';
import {
	listTransactionCustomColumns,
	usesTransactionCustomColumnKeyStorage,
	isCustomColumnApplicableToClass,
	getCustomColumnAutoPopulateValue,
} from './transaction-custom-column-value.js';
import { TransactionColumnRegistry, isSenderBasisEnabled } from './TransactionColumnRegistry.js';

// Dollars and shares can't be added: format per-denomination totals as "$1,234 + 56 shares" (zero units are omitted).
const DENOMINATION_ORDER = ['dollars', 'shares', 'percent'];
export function mixedUnitsText(byDenomination, options, sep = ' + ') {
	const present = DENOMINATION_ORDER.filter((d) => d in byDenomination);
	const nonZero = present.filter((d) => Math.abs(Number(byDenomination[d]) || 0) > 0.005);
	return (nonZero.length ? nonZero : present.slice(0, 1))
		.map((d) => {
			const n = Number(byDenomination[d]) || 0;
			const text = Math.abs(n).toLocaleString('en-US', options);
			const sign = n < 0 && Number(text.replace(/[^\d]/g, '')) !== 0 ? '-' : '';
			return d === 'dollars' ? sign + '$' + text : d === 'percent' ? sign + text + '%' : sign + text + ' shares';
		})
		.join(sep);
}

// Dollar classes: Events (accruals, distributions...) move the balance but never create ledger rows.
const EVENT_BALANCE_SIGN = {
	Contribution: 1,
	'Gain (Loss) Accrual': 1,
	'Principal Funded': 1,
	'Interest Accrual': 1,
	Distribution: -1,
	Redemption: -1,
	'Return of Capital': -1,
	'Principal Repayment': -1,
};

// Dated, signed Events for one holder's dollar class: [{ day, sec, amount }].
function holderClassEvents(clientData, holderClass, classKey) {
	const clientEvents = new Map((clientData?.equity?.[classKey]?.events || []).map((e) => [e.nrid, e]));
	return (holderClass?.events || []).flatMap((a) => {
		const ce = clientEvents.get(a.nrid);
		const amount = (EVENT_BALANCE_SIGN[ce?.eventType] || 0) * (Number(a.grossDollars) || 0);
		if (!amount) return [];
		const sec = Number(ce.record_date || ce.date);
		return [{ day: Math.floor(sec / 86400), sec, amount }];
	});
}

// L&L: size of the event-funded lot (net Events), only when the ledger is short (sent > received) or the class has
// no ledger rows, and approved = net Events + received - sent to the penny. Otherwise 0, and the account flags as before.
export function eventFundedLotSize(approved, netEvents, received, sent, hasLedgerRows = true) {
	if ((hasLedgerRows && !(sent - received > 0.5)) || !(netEvents > 0)) return 0;
	const reconciles = Math.abs(approved - (netEvents + received - sent)) <= Math.max(0.05, Math.abs(approved) * 1e-6);
	return reconciles ? +netEvents.toFixed(4) : 0;
}

// Replays dated Events pro rata onto lots ({ day, qty }; events { day, amount }, signed). Same-day lots
// enter before events. Returns new quantities, or null unless they total the cap-table balance.
export function replayEventsOntoLots(lots, events, approved) {
	const bal = lots.map(() => 0);
	const steps = lots.map((l, i) => ({ day: l.day, order: 0, i, qty: l.qty }));
	events.forEach((e) => steps.push({ day: e.day, order: 1, amount: e.amount }));
	steps.sort((a, b) => a.day - b.day || a.order - b.order);
	for (const st of steps) {
		if (st.order === 0) {
			bal[st.i] = st.qty;
			continue;
		}
		const total = bal.reduce((sum, q) => sum + q, 0);
		if (total <= 0) return null; // Event before any capital exists: can't place it
		bal.forEach((q, i) => (bal[i] = q + (st.amount * q) / total));
	}
	const total = bal.reduce((sum, q) => sum + q, 0);
	return Math.abs(total - approved) <= Math.max(0.01, Math.abs(approved) * 1e-6) ? bal : null;
}

let CERT_COLUMN;
let CLASS_COLUMN;
let showGainsColumns;
let stringOptions;
let sharesOwned = 0;
let builtGiftTransferModalAlready;
let addedGiftTransferListenersAlready;
let giftTransferListenerAbort;
let viewRecipients;
let confirmRequestRemove;

function resetGiftTransferState() {
	builtGiftTransferModalAlready = false;
	sharesOwned = 0;
	window._stepwiseMasterLen = undefined;
	window._stepwiseTargetLen = null;
}

// #6405: Sender Basis is a standard column, but Gift Transfer fills and edits it like a custom one
function senderBasisGiftColumns(clientData) {
	if (!isSenderBasisEnabled(clientData)) return [];
	const label = new TransactionColumnRegistry(clientData).getLabel('senderBasis');
	return [{ key: 'senderBasis', label, format: 'currency', autoPopulate: 'own_field', autoPopulateField: 'taxBasis' }];
}

// Custom columns for this shareClass, stored flat under col.key on the row.
function applicableGiftCustomColumns(clientData, shareClass) {
	const custom = listTransactionCustomColumns(clientData)
		.filter(usesTransactionCustomColumnKeyStorage)
		.filter((col) => isCustomColumnApplicableToClass(col, shareClass));
	return senderBasisGiftColumns(clientData).concat(custom);
}

// Suggests a value for every applicable column not already set on `row`.
// sourceTxn: the source tranche this gift consumes, for own_field/source_field.
// fallbackResolvers: named fallbacks a col.autoPopulateFallback can invoke.
// classTransactions: this class's full list (EXTERNAL_DATA only, see
// commitTranche's call site) -- for next_cert_number.
// @returns {{key, value, autoPopulate}[]} columns actually set this call, so the
// caller can log next_cert_number assignments (see customCertLog).
function applyGiftCustomColumnDefaults(row, clientData, shareClass, dateMs, sourceTxn, fallbackResolvers, classTransactions) {
	const asOfSeconds = dateMs ? Math.floor(Number(dateMs) / 1000) : undefined;
	// Shared across columns so sequential cert fields don't collide (context.certOffset).
	const context = {};
	const assigned = [];
	applicableGiftCustomColumns(clientData, shareClass).forEach((col) => {
		if (row[col.key] !== undefined && row[col.key] !== '') return;
		// Isolate failures per column -- one bad resolver shouldn't blank the rest.
		try {
			const suggested = getCustomColumnAutoPopulateValue(col, clientData, shareClass, asOfSeconds, context, {
				row,
				sourceTxn,
				fallbackResolvers,
				classTransactions,
			});
			if (suggested) {
				row[col.key] = suggested;
				assigned.push({ key: col.key, value: suggested, autoPopulate: col.autoPopulate });
			}
		} catch (err) {
			console.warn('applyGiftCustomColumnDefaults: resolver failed for', col.key, err);
		}
	});
	return assigned;
}

// Parses Sheetz's real historical description grammar (pre-custom-columns) --
// see docs/sheetz-transaction-description-field.md. Always returns the
// receiver's cert, the last one minted in every shape.
function parseSheetzReceiverCertFromNotes(notesText) {
	const s = String(notesText || '');
	let m = s.match(
		/from\s+#?([A-Za-z]+-\d+[A-Za-z]?)\s+\(now\s+(?:closed|#?[A-Za-z]+-\d+[A-Za-z]?)\)\s+(?:at\s+\$[\d,]+(?:\.\d+)?\s+)?to\s+#?([A-Za-z]+-\d+[A-Za-z]?)/i
	);
	if (m) return m[2];
	m = s.match(/Transfer Received\s+\(#?([A-Za-z]+-\d+[A-Za-z]?)/i);
	if (m) return m[1];
	m = s.match(/Issuance to\s+#?([A-Za-z]+-\d+[A-Za-z]?)/i);
	if (m) return m[1];
	m = s.match(/Stock Certificate Issuance\s+\(#?([A-Za-z]+-\d+[A-Za-z]?)\)/i);
	if (m) return m[1];
	return '';
}

// Flat {key: value} snapshot for buildTransactionRecord()'s customColumns
// param -- mirrors ledgers.js's own customColumnSnapshot().
function giftCustomColumnSnapshot(row, clientData) {
	const out = {};
	senderBasisGiftColumns(clientData)
		.concat(listTransactionCustomColumns(clientData))
		.forEach((col) => {
			if (row[col.key] !== undefined) out[col.key] = row[col.key];
		});
	return out;
}

function escapeGiftFieldAttr(value) {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

function giftTransfer(shareholder, fromUser, options = {}) {
	/* NOTES
	This method creates the Gift / Transfer modal
	For each class, it gets relevant data for the active user
	The resultant display can be filtered by class

	Options:
	- dataOnly: boolean - If true, skip DOM operations and return processed data
	- clientData: object - Override client data (for dataOnly mode)
	- shareholderData: object - Override shareholder data (for dataOnly mode)
	- applyTypeFilter: boolean - Apply the Tranche Analysis Security Type filter (#6144b/#6144r).
	  Defaults to null/absent (no filter -- callers get every transaction, which is what Reports/
	  statements need). Tranche Analysis itself (the Scanner and the regular Gift/Transfer modal in
	  edit.js) must opt IN with applyTypeFilter: true.
	*/
	const dataOnly = options.dataOnly === true;
	const _clientData = options.clientData;
	const _shareholderData = options.shareholderData;
	const applyTypeFilter = options.applyTypeFilter === true;

	let listenerSignal;
	const listen = (el, type, fn) => el.addEventListener(type, fn, { signal: listenerSignal });
	// In dataOnly mode, skip DOM checks and use provided data
	if (!dataOnly) {
		// #6061: shareholderData can still be null here if this modal opens before the page populates it; rehydrate like adjustBalances does (~line 5137).
		if (!shareholderData) shareholderData = JSON.parse(sessionStorage.getItem('shareholder.Data'));
		// NTH-INVESTOR-PORTAL-HM, GH #6332: rehydration above can still come back null if shareholder.Data
		// hasn't landed in sessionStorage yet -- bail out rather than crashing on the null deref below.
		if (!shareholderData) return;
		if (userData?.userid == 'consolidated.view' || shareholderData?.userid == 'consolidated.view') {
			// Use AppState shareholderData (bridged via window) rather than re-parsing sessionStorage
			userData = shareholderData;
			shareholder = userData.userid;
		}
		let currentHeadline = document.getElementById('giftHeadline').innerText;
		currentHeadline = currentHeadline.replace('From:', '').trim();
		if (currentHeadline && currentHeadline != userData.user_name) {
			builtGiftTransferModalAlready = false;
		}
		// #6144e: sharesOwned only used to reset on an account switch (headline mismatch),
		// so reopening the SAME shareholder's modal kept `+=`-accumulating onto the previous
		// open's total instead of starting fresh -- silently inflated "Shares Owned Now".
		sharesOwned = 0;

		if (builtGiftTransferModalAlready) return;
		builtGiftTransferModalAlready = true;
		// Fresh build: drop the previous build's listeners and rebind.
		giftTransferListenerAbort?.abort();
		giftTransferListenerAbort = new AbortController();
		listenerSignal = giftTransferListenerAbort.signal;
		addedGiftTransferListenersAlready = false;
	}

	let members;
	let allGifts = [];
	let ttlBasis = 0;
	let ttlAfter = 0;
	let ttlBefore = 0;
	let pairedQty = 0;
	let selectedClass;
	let minDiff = 999; // arbitrarily large starting number used in tranche analysis
	let diffCount = 0; // counter used in tranche analysis
	let removeGiftId = '';
	let giftRecipients = [];
	let recipientPickerBound = false;
	// Cash Sale: keyed by recipient userid rather than stored on the recipient
	// object (those are shared refs into members/capTableData). All-or-nothing
	// per recipient -- see renderGiftRecipients()/'cash-sale-checkbox' listener.
	let cashSaleRecipients = {};
	let priorTransfers = [];
	let includedClasses = [];
	let giftTransactions = [];
	let historicHoldings = [];

	// #6144: which matching signal decided a Historic Holdings/Prior Transfer row --
	// feeds the "Keys" legend under the Prior Tranches table (Stepwise Analysis).
	const MATCH_KEY_NAMES = {
		A: 'Explicit Cert Match',
		B: 'Notes Text Match',
		C: 'Perfect Fit',
		D: 'Split Across Tranches',
		E: 'Best-Guess Match',
		F: 'Basis Carried Forward',
		G: 'Sender Basis Match',
		H: 'Date Match (from Notes)',
		I: 'Only Possibility',
	};
	const MATCH_KEY_DESCS = {
		A: 'Linked by an exact certificate/lot number referenced in the notes.',
		B: 'Linked by a free-text description in the notes.',
		C: "One prior holding exactly covers this transfer's quantity.",
		D: 'Multiple prior holdings were combined to exactly cover this transfer.',
		E: 'No explicit cert/notes link; the closest candidate by quantity, date, and price was inferred.',
		F: 'Share Price/Tax Basis was inherited from the matched holding rather than recorded on this transaction.',
		G: "The sender-declared basis exactly matches this candidate holding's recorded Tax Basis.",
		H: 'A date written in the notes is within 5 days of the actual transaction date on one side of the match.',
		I: 'Exactly one eligible holding existed at all.',
	};
	// Short mnemonic shown in the badge/legend -- avoids arbitrary single letters.
	const MATCH_KEY_LABELS = { A: 'EC', B: 'NM', C: 'PF', D: 'ST', E: 'BG', F: 'BC', G: 'SB', H: 'DM', I: 'OP' };
	// #6144b: confidence tier per Key, matches Nth Round's Tranche-Analysis-Explained.pdf exactly --
	// Only Possibility (violet, top tier): I, no other eligible candidate existed at all -- a stronger,
	// categorically different certainty than any textual evidence, since evidence can be mis-recorded
	// but "there was nothing else it could be" can't. Checked first in every branch that assigns it and
	// overrides everything else, including tagMatch's own sender-basis ('G') promotion below.
	// Confirmed (green): A/B/G direct evidence. Certain (blue): C/D/H, the numbers fit only one way.
	// Inferred (red): E, best-available estimate. F (Basis Carried Forward) is a modifier flag on a
	// row, not a rung on this ladder -- kept its own orange, distinct from E despite both being Inferred.
	const MATCH_KEY_TIER_CLASS = {
		I: 'text-violet-700',
		A: 'text-green-600',
		B: 'text-green-600',
		G: 'text-green-600',
		C: 'text-blue-700',
		D: 'text-blue-700',
		H: 'text-blue-700',
		E: 'text-red-700',
		F: 'text-orange-600',
	};
	function tagMatch(item, key, transferForSenderCheck) {
		if (!item || item._matchKey) return;
		// #6144b: 'I' (O.P., Only Possibility) is the top, overriding tier -- don't let a weaker
		// signal like sender-basis ('G') displace it once a caller has determined there was no
		// other eligible candidate at all.
		if (key !== 'I' && transferForSenderCheck && senderBasisMatchesCandidate(transferForSenderCheck, item)) key = 'G';
		item._matchKey = key;
	}
	let origTransactions = [];
	let selectedTranches = [];
	let toggle = 'Show Positves';
	let trancheErrorText = '';

	let giftUserData = dataOnly ? _shareholderData || {} : shareholderData;
	let giftUserId = giftUserData?.userid || shareholder;

	let viewGiftTransferModal, userGiftTransferTable, BUY_BACK;
	if (!dataOnly) {
		BUY_BACK = pn == '/' && clientData.flags?.buy_back_active;
		viewGiftTransferModal = document.getElementById('viewGiftTransferModal');
		userGiftTransferTable = document.getElementById('userGiftTransferTable');
	}

	// Need to wait for external data - use provided data in dataOnly mode
	if (dataOnly && _clientData) {
		clientData = _clientData;
	} else {
		clientData = JSON.parse(sessionStorage.getItem(client + '.Data'));
	}

	// Helper functions used by both dataOnly mode and renderUserGiftTransModal
	function numberToLetters(num) {
		let letters = '';
		while (num >= 0) {
			letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[num % 26] + letters;
			num = Math.floor(num / 26) - 1;
		}
		return letters;
	}

	function lettersToNumbers(ltrs) {
		if (!ltrs) return '?';
		let n = 0;
		for (let p = 0; p < ltrs.length; p++) {
			n = ltrs[p].charCodeAt() - 64 + n * 26;
		}
		return n.toString().padStart(6, '0');
	}

	const SORT_CLASS_BASIS_DATE_QTY = true; // or client-specific flag

	// By date then descending effective tax basis (unknowns last)
	function sortByClassThenBasisThenDateThenQty(transArray) {
		transArray.sort((a, b) => {
			if (SORT_CLASS_BASIS_DATE_QTY) {
				// 1) Class ascending
				if (a.shareClass < b.shareClass) return -1;
				if (a.shareClass > b.shareClass) return 1;

				// 2) Effective tax basis descending, unknowns last
				function normBasis(val) {
					return isKnownNumber(val) ? Number(val) : null;
				}

				let basisA = normBasis(a._effectiveTaxBasis);
				let basisB = normBasis(b._effectiveTaxBasis);

				if (basisA === null && basisB !== null) return 1;
				if (basisA !== null && basisB === null) return -1;

				if (basisA !== null && basisB !== null) {
					if (basisA < basisB) return 1;
					if (basisA > basisB) return -1;
				}

				// 3) Date ascending
				let dateA = Number(a.date);
				let dateB = Number(b.date);
				if (dateA < dateB) return -1;
				if (dateA > dateB) return 1;

				// 4) Quantity ascending
				let quantityA = Number(a.quantity);
				let quantityB = Number(b.quantity);
				if (quantityA < quantityB) return -1;
				if (quantityA > quantityB) return 1;

				return 0;
			}
			if (isVERM) {
				// First, sort by 'class'
				if (a.shareClass < b.shareClass) return -1;
				if (a.shareClass > b.shareClass) return 1;

				// Next, sort by 'date' ascending
				let dateA = Number(a.date);
				let dateB = Number(b.date);
				if (dateA < dateB) return -1;
				if (dateA > dateB) return 1;
			} else if (!isBB) {
				// First, sort by 'date' descending
				let dateA = Number(a.date);
				let dateB = Number(b.date);
				if (dateA < dateB) return 1;
				if (dateA > dateB) return -1;

				// Next, sort by 'class'
				if (a.shareClass < b.shareClass) return -1;
				if (a.shareClass > b.shareClass) return 1;
			}

			// Then, sort by effective tax basis (descending, unknowns last)
			function normBasis(val) {
				return isKnownNumber(val) ? Number(val) : null;
			}

			let basisA = normBasis(a._effectiveTaxBasis);
			let basisB = normBasis(b._effectiveTaxBasis);

			// Unknowns go LAST
			if (basisA === null && basisB !== null) return 1;
			if (basisA !== null && basisB === null) return -1;

			// Both known → descending
			if (basisA !== null && basisB !== null) {
				if (basisA < basisB) return 1;
				if (basisA > basisB) return -1;
			}

			if (isBB) {
				// First, sort by 'class'
				if (a.shareClass < b.shareClass) return -1;
				if (a.shareClass > b.shareClass) return 1;

				// Next, sort by 'date' ascending
				let dateA = Number(a.date);
				let dateB = Number(b.date);
				if (dateA < dateB) return -1;
				if (dateA > dateB) return 1;
			}

			// Finally, sort by 'quantity'
			let quantityA = Number(a.quantity);
			let quantityB = Number(b.quantity);
			if (quantityA < quantityB) return -1;
			if (quantityA > quantityB) return 1;

			return 0;
		});

		return transArray;
	}

	// Build a new transactions list
	// #6144e: CERT_COLUMN/CLASS_COLUMN are `||=`-latched module globals that used to
	// never reset outside dataOnly mode -- once any account/class anywhere in the tab
	// set them true, every later shareholder's render (including their matching logic,
	// not just the rendered column) silently inherited that state. Reset fresh per call.
	CERT_COLUMN = false;
	CLASS_COLUMN = false;
	// GH: giftTransactions below holds the SAME object references as classTransactions
	// (a plain push, not a clone) -- the matching engine mutates .quantity in place on
	// those objects to represent a partially-consumed lot's remaining balance for
	// display. Snapshot each transaction's real quantity here, before any matcher can
	// touch it, so a later full-record-replace write (e.g. the Tax Basis inline-edit
	// below) can send the true persisted value instead of an analysis-adjusted one.
	const pristineQuantityByNrid = new Map();
	// #6144-Y: prefer the cached whole-CLIENT answer over this call's own (possibly
	// single-shareholder-scoped) pool below -- see TransactionService.js's CERT_COLUMN cache.
	const cachedCertColumn = getCachedCertColumn(client);
	let classes = Object.keys(clientData.equity);
	if (classes.length > 1) CLASS_COLUMN = true;
	classes.sort();

	// CERT_COLUMN is decided solely by '#' in transaction notes (below) --
	// having a certificate template or show_certificates flag doesn't mean a
	// client assigns cert NUMBERS (false-positived for Edmund Optics and others).

	const headlineClasses = new Set();
	const headlineBefore = {}; // per-denomination "Shares Owned Now"; shown instead of one mixed sum when a holder has both
	classes.forEach((c, i) => {
		// console.log(clientData.equity[c].transactions)
		let classIncluded = false;
		// Try looking at Transactions first, then Approved balances
		// For EXTERNAL_DATA admin views, scoped transactions are on shareholderData.scopedTransactions
		// rather than clientData.equity[c].transactions (which is re-read from clean sessionStorage here).
		// dataOnly callers scanning many different shareholders in a loop can't rely on that page-global
		// (it's fixed to whichever one shareholder's edit page is actually open) -- options.shareholderData
		// lets them supply their own scopedTransactions (e.g. one bulk fetchScopedTransactions({userids: []})
		// call, reused across every holder in the loop) instead.
		// #6144b: filter by Security Type before anything downstream (matching, tranche building,
		// the sharesOwned headline) ever sees these -- see isTrancheEligibleSecurityType().
		let classTransactions = (
			dataOnly && _shareholderData?.scopedTransactions?.[c]
				? _shareholderData.scopedTransactions[c]
				: EXTERNAL_DATA && shareholderData?.scopedTransactions?.[c]
					? shareholderData.scopedTransactions[c]
					: clientData.equity[c].transactions || []
		).filter(applyTypeFilter ? isTrancheEligibleSecurityType : () => true);
		classTransactions.forEach(function (t) {
			if ((t.receiver && t.receiver.toLowerCase() == shareholder) || (t.sender && t.sender.toLowerCase() == shareholder)) {
				t.shareClass = classes[i];
				if (t.nrid && !pristineQuantityByNrid.has(t.nrid)) pristineQuantityByNrid.set(t.nrid, t.quantity);
				giftTransactions.push(t);
				classIncluded = true;
			}
		});

		// Gift Transfer uses certificate-aware matching as analysis logic,
		// not merely table rendering. Keep CERT_COLUMN as a runtime analysis flag.
		CERT_COLUMN ||= cachedCertColumn !== null ? cachedCertColumn : classTransactions.some((t) => t.notes.includes('#'));

		// Dont't display future (beyond this year) Events or Transactions
		let nextYear = new Date(new Date().getFullYear() + 1, 1, 1) / 1000;
		giftTransactions = giftTransactions.filter((t) => Number(t.date) < nextYear);
		console.log('initialTransactions[' + c + ']: ', giftTransactions);

		if (classIncluded) includedClasses.push(c);
		//clientEvents = clientEvents.concat(clientData.equity[c].events);

		// sharesOwned/balanceBefore only feed the interactive headline -- skip in dataOnly mode, where
		// a bare caller (e.g. Ledgers' Tax Basis placeholder) may never have populated shareholderData.
		if (!dataOnly) {
			// #6061: optional chaining as a backstop in case shareholderData is still unavailable after the rehydration above.
			let shareholderClientData = shareholderData?.clients?.[client];
			let userTransactions = classTransactions.filter((t) => t.receiver == shareholder || t.sender == shareholder);
			if (userTransactions.length && shareholderClientData?.shares[c]) {
				// Need to rationalize, or at least say, Owned vs Approved
				const classBalance = Number(shareholderClientData.shares[c].approved || '0');
				sharesOwned += classBalance;
				headlineClasses.add(c);
				const denomination = getEffectiveDenomination(clientData, c);
				headlineBefore[denomination] = (headlineBefore[denomination] || 0) + classBalance;
			}
		}
	});

	let ttlCurrentValue = 'n.a.';
	let balanceBefore = sharesOwned;
	let balanceAfter = balanceBefore;
	const headlineAfter = { ...headlineBefore };
	const headlineMixed = () => Object.keys(headlineBefore).length > 1;
	const headlineAmount = (scalar, byDenomination, options) =>
		headlineMixed() ? mixedUnitsText(byDenomination, options) : scalar.toLocaleString('en-US', options);
	const headlineOverdrawn = () => (headlineMixed() ? Object.values(headlineAfter).some((v) => v < -0.005) : balanceAfter < 0);
	function renderBalanceHeadline(options = customFormat()) {
		balanceHeadline.innerHTML =
			'Shares Owned Now:&emsp;<span class="text-primary">' +
			headlineAmount(balanceBefore, headlineBefore, options) +
			`</span>&nbsp;Before&emsp;<span id="balanceAfterElem" class="text-primary${headlineOverdrawn() ? ' red-text font-bold' : ''}">` +
			headlineAmount(balanceAfter, headlineAfter, options) +
			`</span>&nbsp;After`;
	}
	function denominationOfTranche(tranche) {
		const first = String(tranche || '')
			.split(/[+,\s]+/)
			.find(Boolean);
		const row = giftTransactions.find((t) => t.tranche == first);
		return row ? getEffectiveDenomination(clientData, row.shareClass) : null;
	}
	// let shortName = giftUserData.clients[client].notes;

	// dataOnly mode: this whole block is pure DOM/UI side effect, gated so a
	// dataOnly caller without the GT modal's HTML (e.g. Ledgers) doesn't throw
	// on getElementById(). Vars stay `let` since later interactive-only code
	// still references them, just unused/unreached on the dataOnly path.
	let giftHeadline, balanceHeadline, stepwiseAnalysis, giftTransferControls;
	if (!dataOnly) {
		giftHeadline = document.getElementById('giftHeadline');
		balanceHeadline = document.getElementById('balanceHeadline');
		stepwiseAnalysis = document.getElementById('stepwiseAnalysis');
		giftTransferControls = document.getElementById('giftTransferControls');
		if (!sharesOwned || stepwiseAnalysis?.checked) giftTransferControls.classList.add('hidden');
		else giftTransferControls.classList.remove('hidden');
		giftHeadline.innerHTML = `From:&ensp;<span class="text-light">` + giftUserData.user_name + `</span>`;
		// giftHeadline.innerHTML = `From:&ensp;<span class="text-light">` + (shortName ? shortName + " | " : "") + giftUserData.user_name + `</span>`;
		renderBalanceHeadline();
	}

	// SEE NOTES HERE: https://docs.google.com/document/d/1tASR37WcKOE-x_LJKlCy0JhD0g0cWPaTUCUPNAlRjNE/edit
	// First, sort all transactions chronologically, oldest to newest.
	// Next, scan for dates with multiple transactions.
	// Each of these dates must be analyzed separately for possible splits or related swaps.
	// In all cases, tax basis must be carried forward.

	// Determine if these transactions share the current account as both sender and receiver.
	// If all go only in one direction (single-leg) these transactions require no special treatment.

	// If the transactions go in opposite directions, start by sorting all of the transactions for a given date by descending dollar amounts.

	// Now look for matching quantities (swaps).
	// Do the total aggregate quantities in each direction match?
	// If not, do any individual quantities in each direction match?
	// If not, does any individual quantity match a group of opposite quantities?

	// If matching quantities are not found, look for matching dollar amounts (splits).
	// Do the total aggregate dollar amounts in each direction match?
	// If not, do any individual dollar amounts in each direction match?
	// If not, does any individual amount match a group of opposite amounts?
	// If any matches are found, determine and enter the new post-split tax basis.
	// Filter out first leg (cancellation) of stock swaps and stock splits.

	// No clue why, but we need new array objects...
	giftTransactions = JSON.parse(JSON.stringify(giftTransactions));

	// --- Stepwise: clamp giftTransactions to cutoff date  ---
	// Stepwise clamps only while its checkbox is checked (and never for dataOnly); a stale target must not trim a normal open.
	if (dataOnly || !stepwiseAnalysis?.checked) window._stepwiseTargetLen = null;
	(function () {
		// build sorted unique list of all dates
		let allDates = Array.from(new Set(giftTransactions.map((t) => String(t.date)))).sort((a, b) => Number(a) - Number(b));

		// master and target length
		window._stepwiseMasterLen = allDates.length;
		if (window._stepwiseTargetLen == null) window._stepwiseTargetLen = _stepwiseMasterLen;

		// clamp target length
		if (_stepwiseMasterLen === 0) {
			window._stepwiseTargetLen = 0;
		} else {
			if (_stepwiseTargetLen < 1) _stepwiseTargetLen = 1;
			if (_stepwiseTargetLen > _stepwiseMasterLen) _stepwiseTargetLen = _stepwiseMasterLen;
		}

		// cutoff date = last visible date
		const cutoffSec = _stepwiseTargetLen > 0 ? Number(allDates[_stepwiseTargetLen - 1]) : 0;

		// filter giftTransactions IN PLACE
		// Guard on _stepwiseTargetLen, not cutoffSec -- pre-1970 dates make cutoffSec negative.
		if (_stepwiseTargetLen > 0) {
			giftTransactions = giftTransactions.filter((t) => Number(t.date) <= cutoffSec);
		}

		// optional: update label/buttons
		const label = document.getElementById('stepLabel');
		if (label) {
			if (_stepwiseTargetLen === 0) {
				label.textContent = 'As of --/--/-- (0 of 0)';
			} else {
				const ms = cutoffSec * 1000;
				const dstr = window.moment ? moment(ms).format('MM/DD/YY') : new Date(ms).toLocaleDateString();
				label.textContent = `As of ${dstr} (${_stepwiseTargetLen} of ${_stepwiseMasterLen})`;
			}
		}
	})();

	function reduceToVestedOnly() {
		const userid = giftUserData.userid;
		const shareClasses = giftUserData.clients?.[client]?.shares || {};
		const grants = [];

		// Loop over all share classes
		for (const [classKey, classData] of Object.entries(shareClasses)) {
			const vestingData = classData.vesting || {};

			// Build array of structured grant data
			for (const [grantName, grant] of Object.entries(vestingData)) {
				const schedule = grant.schedule || {};
				const timestamps = Object.keys(schedule).map((t) => Number(t));
				const total = timestamps.reduce((sum, t) => sum + Number(schedule[t].amount || 0), 0);
				const vested = timestamps.filter((t) => schedule[t].posted).reduce((sum, t) => sum + Number(schedule[t].amount || 0), 0);

				// Extract a grant date from the title, e.g., "1/1/24"
				const dateMatch = grantName.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})/);
				let titleDateStr = null;
				if (dateMatch) titleDateStr = dateMatch[1];

				grants.push({ grantName, total, vested, titleDateStr, classKey });
			}
		}

		// Adjust the transactions
		for (const tx of giftTransactions) {
			if (tx.notes.toLowerCase().includes('restricted stock grant') && tx.receiver === userid) {
				const txQty = Number(tx.quantity);
				const txDate = Number(tx.date);
				const txClass = tx.shareClass;

				// Format transaction date as "m/d/yy" and "m/d/yyyy"
				const txDateStr = moment.unix(txDate).utc().format('M/D/YYYY');
				const [m, d, y] = txDateStr.split('/');
				const altYear = y.length === 4 ? y.slice(2) : '20' + y;
				const altDateStr = `${m}/${d}/${altYear}`;

				// Match against grants
				const match = grants.find(
					(grant) =>
						grant.total === txQty &&
						grant.classKey === txClass &&
						grant.titleDateStr &&
						(grant.titleDateStr === txDateStr || grant.titleDateStr === altDateStr)
				);

				if (match) {
					tx.quantity = match.vested.toString(); // even if 0
				}
			}
		}
	}

	// Returns:
	// - null    => no deferral
	// - "LOCK"  => TBD / future use (admin must edit to unlock)
	// - number  => unix seconds at midnight of the "use on/after" day
	function deferUseTs(notes) {
		const s = String(notes || '').trim();
		if (!s) return null;

		// Must contain the "for use" concept (or "for future use")
		if (!/\bfor\s+(?:future\s+)?use\b/i.test(s)) return null;

		// "for future use" => locked until admin edits
		if (/\bfor\s+future\s+use\b/i.test(s)) return 'LOCK';

		// Recognize TBD in any common form: TBD, T.B.D., T. B. D, etc.
		const TBD_RE = /\bt\s*\.?\s*b\s*\.?\s*d\s*\.?\b/i;

		// Allow: "... for use TBD" (no on/after keyword)
		if (/\bfor\s+use\b/i.test(s) && TBD_RE.test(s)) return 'LOCK';

		// Allow: "for use on X", "for use after X", "for use on/after X"
		const m = s.match(/\bfor\s+use\b.*?\b(on\/after|on|after)\b\s*[:\-]?\s*([A-Za-z0-9,./\- ]{2,60}|\bt\s*\.?\s*b\s*\.?\s*d\s*\.?\b)\b/i);
		if (!m) return null;

		const raw = String(m[2] || '').trim();
		if (TBD_RE.test(raw)) return 'LOCK';

		// Parse "any date format" using moment loose first, then Date.parse fallback
		let ms = null;
		if (window.moment) {
			const mm = moment(raw);
			if (mm.isValid()) ms = mm.valueOf();
		}
		if (ms == null) {
			const dp = Date.parse(raw);
			if (!isNaN(dp)) ms = dp;
		}
		if (ms == null) return null;

		return moment(ms).utc().startOf('day').unix();
	}

	// too early for historical context
	// if (isEDM) reduceToVestedOnly();

	// L&L Products: capital funded by Events has no ledger lot, so dollar transfers out of it matched nothing.
	// Add one event-funded lot (net Events) when the ledger is short or empty and the class's books reconcile.
	const eventFundedClasses = new Set();
	let headlineChanged = false;
	if (isLLProducts) {
		const sumQty = (rows) => rows.reduce((total, g) => total + Number(g.quantity), 0);
		classes.forEach((c) => {
			if (getEffectiveDenomination(clientData, c) !== 'dollars') return;
			const holderClass = giftUserData?.clients?.[client]?.shares?.[c];
			const approved = Number(holderClass?.approved);
			if (!holderClass?.events?.length || !Number.isFinite(approved)) return;
			const sent = giftTransactions.filter((g) => g.shareClass == c && g.sender == giftUserId);
			const received = giftTransactions.filter((g) => g.shareClass == c && g.receiver == giftUserId);
			const events = holderClassEvents(clientData, holderClass, c);
			const netEvents = events.reduce((total, e) => total + e.amount, 0);
			const size = eventFundedLotSize(approved, netEvents, sumQty(received), sumQty(sent), sent.length + received.length > 0);
			if (!size) return;
			eventFundedClasses.add(c);
			// The headline only counted classes with ledger rows; add this one so it matches the lot.
			if (!dataOnly && !headlineClasses.has(c)) {
				const owned = approved;
				const denomination = getEffectiveDenomination(clientData, c);
				sharesOwned += owned;
				balanceBefore += owned;
				balanceAfter += owned;
				headlineBefore[denomination] = (headlineBefore[denomination] || 0) + owned;
				headlineAfter[denomination] = (headlineAfter[denomination] || 0) + owned;
				headlineChanged = true;
			}
			giftTransactions.push({
				nrid: 'event-funded-' + c + '-' + giftUserId,
				shareClass: c,
				securityType: 'Partnership Interest',
				date: Math.min(...events.map((e) => e.sec), ...sent.map((g) => Number(g.date))),
				sender: client,
				senderName: 'Issuer',
				receiver: giftUserId,
				receiverName: giftUserData?.user_name || '',
				quantity: size.toString(),
				sharePrice: '1',
				taxBasis: '',
				notes: 'Event-funded capital (accruals and contributions; no ledger entry)',
				_eventFunded: true, // virtual lot: transferable, but never written to or updated in the ledger
			});
		});
		// The headline and controls were drawn before this lot existed.
		if (headlineChanged) {
			renderBalanceHeadline();
			if (!stepwiseAnalysis?.checked) giftTransferControls.classList.remove('hidden');
		}
	}

	giftTransactions.sort((a, b) => Number(a.date) - Number(b.date));
	origTransactions = JSON.parse(JSON.stringify(giftTransactions));

	// normalize all dates to midnight of that date
	giftTransactions.forEach((t) => t.date && (t.date = moment.unix(t.date).utc().startOf('day').unix()));
	giftTransactions.forEach((t) => (t._useAfter = deferUseTs(t.notes)));
	console.log(
		'LOCK lots:',
		giftTransactions.filter((t) => t._useAfter === 'LOCK')
	);

	// NEW EFECTIVE TAX BASIS CODE //
	function isNA(val) {
		const s = String(val ?? '')
			.trim()
			.toLowerCase();
		return s === 'n/a' || s === 'n.a.' || s === 'na' || s === 'n.a';
	}

	function isKnownNumber(val) {
		if (val === null || val === undefined) return false;
		if (typeof val === 'string') {
			const s = val.trim().toLowerCase();
			if (s === '' || isNA(s)) return false;
		}
		return !isNaN(Number(val));
	}

	// #6144: swaps the column header/tooltip to "Share Price" while Cash Sale is
	// checked (the cell still writes to taxBasis either way, only the label changes).
	function basisHeaderContent(isCashSale) {
		return isCashSale
			? {
					label: 'Share&nbsp;Price',
					title: 'The price paid per share in this cash sale. Cash Sale is checked for this recipient, so this value also becomes Tax Basis for each transaction.',
				}
			: {
					label: 'Tax&nbsp;Basis',
					title: 'Also called Cost Basis: the value used for tax reporting. Differs from Share Price/Price Paid -- e.g., 0 for an ordinary gift, unless Cash Sale is checked for this recipient, in which case Share Price mirrors this value.',
				};
	}

	// #6144: shared epsilon-tolerant quantity equality. Quantities are strings
	// (so '.777' vs '0.777' fails a bare ==) and can carry float noise from
	// upstream math -- Number() fixes the former, the epsilon fixes the latter.
	function quantitiesMatch(a, b) {
		const numA = Number(a);
		const numB = Number(b);
		if (isNaN(numA) || isNaN(numB)) return false;
		return nearlyEqualBalance(numA, numB);
	}

	function getLatestValuationField(txn, clientData, fieldName) {
		if (!txn?.shareClass || !txn?.date) return null;
		let vals = clientData?.equity?.[txn.shareClass]?.valuations;
		if (!Array.isArray(vals) || !vals.length) return null;
		let txnDate = Number(txn.date);
		let bestValue = null;
		let bestDate = null;
		vals.forEach((v) => {
			let vDate = Number(v?.date);
			if (!isFinite(vDate) || vDate > txnDate) return;
			if (!isKnownNumber(v?.[fieldName])) return;
			if (bestDate === null || vDate > bestDate) {
				bestDate = vDate;
				bestValue = Number(v[fieldName]);
			}
		});
		return bestValue;
	}

	// #6144: a sender can declare a lot's tax basis via `senderBasis` or embedded
	// in notes/Description (e.g. "Gift, basis: 4.50") -- highest-priority signal in
	// resolveEffectiveTaxBasis()/scoreMatches() below. Permissive on format since
	// this is admin-typed free text. #6405: an explicit "basis" only -- a bare "$" amount is often a price.
	function parseSenderBasisFromNotes(notesText) {
		const s = String(notesText || '');
		const m = s.match(/\bbasis\s*:?\s*\$?\s*(-?[\d,]*\.?\d+)/i);
		if (!m) return null;
		const value = Number(m[1].replace(/,/g, ''));
		return isNaN(value) ? null : value;
	}

	// #6144: tolerant numeric coercion for a sender-basis value that may come
	// back formatted ("$4.50", "1,234.5") depending on how it was entered.
	function coerceBasisNumber(raw) {
		if (raw === null || raw === undefined) return null;
		const cleaned = typeof raw === 'string' ? raw.replace(/[$,]/g, '').trim() : raw;
		if (cleaned === '') return null;
		const n = Number(cleaned);
		return isNaN(n) ? null : n;
	}

	function getSenderDeclaredBasis(txn) {
		// #6405: Sender Basis is a standard field now, not a custom column found by its label
		const val = coerceBasisNumber(txn?.senderBasis);
		return val != null ? val : parseSenderBasisFromNotes(txn?.notes);
	}

	// True when a SEND transaction's sender-declared basis (see
	// getSenderDeclaredBasis()) matches a candidate prior holding's own
	// recorded taxBasis -- the high-priority matching signal used by
	// scoreMatches()/scoreDifferences() below.
	function senderBasisMatchesCandidate(transfer, h) {
		const declared = getSenderDeclaredBasis(transfer);
		if (declared == null || !isKnownNumber(h?.taxBasis)) return false;
		return Math.abs(declared - Number(h.taxBasis)) < 0.005;
	}

	// #6144: same date-in-notes heuristic scoreDifferences() uses internally -- reused here
	// only to identify (for the 'H' Key) whether a note-embedded date, not the score itself, decided a pick.
	function extractNoteDate(text) {
		const match = String(text || '').match(/(\d{1,4}[\/\-]\d{1,2}[\/\-]\d{1,4}|\w{3} \d{1,2}, \d{4})/);
		if (match) {
			const m = moment(match[0], dateFormats, true);
			if (m.isValid()) return m.valueOf();
		}
		return null;
	}
	function isNoteDateCloseInTime(msFromNote, dateStampSeconds) {
		return Math.abs(msFromNote - Number(dateStampSeconds) * 1000) <= 5 * 24 * 60 * 60 * 1000;
	}
	function noteDateMatchesCandidate(transfer, h) {
		const transferNoteDate = extractNoteDate(transfer?.notes);
		const historicNoteDate = extractNoteDate(h?.notes);
		return Boolean(
			(transferNoteDate && isNoteDateCloseInTime(transferNoteDate, h?.date)) ||
			(historicNoteDate && isNoteDateCloseInTime(historicNoteDate, transfer?.date))
		);
	}

	function resolveEffectiveTaxBasis(txn, clientData) {
		// #6144a (2026-09-12, per Chris): reordered so a real recorded number always wins
		// over any guess, and dropped the current/latest-valuation fallbacks entirely --
		// too presumptuous for a Tax Basis default. Order is now: 1) this transaction's own
		// Tax Basis (explicit n/a included -- that's a known answer, not a gap), 2) Share
		// Price actually paid (admin-entered cash overrides a Sender Basis claim), 3) Sender
		// Basis, 4) n.a.

		// 1) explicit tax basis, including explicit n/a
		let tb = String(txn.taxBasis || '')
			.trim()
			.toLowerCase();
		if (isNA(tb)) return { value: txn.taxBasis, source: 'explicitNA' };
		if (isKnownNumber(txn.taxBasis)) return { value: Number(txn.taxBasis), source: 'taxBasis' };

		// 2) purchase price / transaction price -- excludes 0, which is now the
		// display default for a never-entered Share Price (not necessarily a
		// real $0), so it's no longer distinguishable from "unknown" here.
		if (isKnownNumber(txn.sharePrice) && Number(txn.sharePrice) > 0) return { value: Number(txn.sharePrice), source: 'sharePrice' };

		// 3) sender-declared basis (dedicated field or parsed from notes)
		const senderBasis = getSenderDeclaredBasis(txn);
		if (senderBasis != null) return { value: senderBasis, source: 'senderBasis' };

		// 4) nothing known -- 'n.a.', not a guess from today's/latest valuation data.
		return { value: 'n.a.', source: 'unresolved' };
	}

	// #6144: per-share value to compare against cost basis for the optional Gain/Loss
	// column -- prefers the latest Reference Price valuation as of today, falling back
	// to the latest Share Price valuation. Internal estimate only, not tax advice.
	function getCurrentPerShareValue(t, clientData) {
		const asOfToday = { shareClass: t.shareClass, date: Math.floor(Date.now() / 1000) };
		let ref = getLatestValuationField(asOfToday, clientData, 'referencePrice');
		if (isKnownNumber(ref)) return Number(ref);
		let sp = getLatestValuationField(asOfToday, clientData, 'sharePrice');
		if (isKnownNumber(sp)) return Number(sp);
		return null;
	}

	// #6144: estimated capital gain/loss for the optional Gain/Loss column --
	// (current per-share value - effective Tax Basis) * quantity. Long-term vs.
	// short-term is based on this tranche's own date, which is the acquisition
	// date a gift recipient inherits under IRS basis "tacking" rules. Returns
	// null when there isn't enough known data to estimate.
	function computeGainLoss(t, clientData) {
		if (!Number.isFinite(t._effectiveTaxBasis)) return null;
		const currentValue = getCurrentPerShareValue(t, clientData);
		if (!isKnownNumber(currentValue)) return null;
		const qty = Number(String(t.quantity).replace(/[^.\d-]/g, ''));
		if (!qty) return null;
		const totalGain = (currentValue - t._effectiveTaxBasis) * qty;
		const isLongTerm = moment.unix(t.date).add(1, 'year').isSameOrBefore(moment());
		return { totalGain, isLongTerm };
	}
	function normalizeCertToken(token) {
		let s = String(token || '')
			.trim()
			.toUpperCase();
		if (!s) return '';

		s = s.replace(/[.\-_:\/\\•|\s#]/g, '');

		let m = s.match(/^([A-Z]*)(\d+)$/);
		if (m) return m[1] + (m[2].replace(/^0+/, '') || '0');

		s = s.replace(/^0+(\d)/, '$1');
		return s;
	}

	// Matches cert numbers like "62-409", "SD.468" as one token (not truncated at the separator).
	// Only one separator allowed, so it won't accidentally match multi-part dates.
	const CERT_TOKEN = '[A-Za-z]*[-.]?0*\\d+(?:[-.]\\d+)?';

	function parseCertificateRefs(notesText) {
		const s = String(notesText || '');
		const result = {
			newCert: '',
			sourceCerts: [],
		};

		// First cert inside parens is often the new cert
		// e.g. Transfer (#15954 from ...)
		let m = s.match(new RegExp('\\((?:cert\\s*)?#?\\s*(' + CERT_TOKEN + ')', 'i'));
		if (m && m[1]) result.newCert = normalizeCertToken(m[1]);

		// Anything after "from" is a source list
		// e.g. from 14479, 14506, 14507
		m = s.match(/\bfrom\b(.*?)(?:\)|$)/i);
		if (m && m[1]) {
			const sourceCertPattern = new RegExp('\\b(' + CERT_TOKEN.replace('\\d+', '\\d{4,}') + ')\\b');
			result.sourceCerts = m[1]
				.split(',')
				.map((part) => {
					const mm = part.match(sourceCertPattern);
					return mm && mm[1] ? normalizeCertToken(mm[1]) : '';
				})
				.filter(Boolean);
		}

		// Fallback for simple cases like Cancellation (0006620)
		if (!result.newCert && !result.sourceCerts.length) {
			m = s.match(new RegExp('\\((?:cert\\s*)?#?\\s*(' + CERT_TOKEN + ')\\)', 'i'));
			if (m && m[1]) result.newCert = normalizeCertToken(m[1]);
		}

		// Bare notation, no parens at all, e.g. "Issuance 2094" -- old pre-electronic
		// entries. Anchored to the whole note (verb + cert, nothing else) so it can't
		// misfire on richer free text.
		if (!result.newCert && !result.sourceCerts.length) {
			m = s.match(new RegExp('^[A-Za-z]+(?:\\s+[A-Za-z]+)*\\s+(' + CERT_TOKEN + ')$'));
			if (m && m[1]) result.newCert = normalizeCertToken(m[1]);
		}

		// Cancellations that list multiple certs together with no "from", e.g.
		// "Stock Certificate Cancellation (62-422, 62-1051)" -- only the first was
		// being captured above. Scoped to "cancel" notes so nothing else changes.
		if (!result.sourceCerts.length && /cancel/i.test(s)) {
			const parenMatch = s.match(/\(([^)]+)\)/);
			if (parenMatch) {
				const tokens = (parenMatch[1].match(new RegExp(CERT_TOKEN, 'g')) || []).map(normalizeCertToken).filter(Boolean);
				if (tokens.length > 1) {
					result.sourceCerts = [...new Set(tokens)];
					result.newCert = '';
				}
			}
		}

		return result;
	}

	giftTransactions.forEach((t) => {
		const basis = resolveEffectiveTaxBasis(t, clientData);
		t._effectiveTaxBasis = basis.value;
		t._basisSource = basis.source;
		console.log('EFFECTIVE BASIS = ' + basis.value + ' ' + basis.source);
	});

	// For each Recieve from company with no sharePrice, insert latest valuation or assumed price
	// giftTransactions.forEach(r => {
	// 	if (r.receiver == giftUserId && (r.sender == "" || r.sender == client || r.senderName.toLowerCase() == "issuer"))
	// 		r.sharePrice = toNumber(r.sharePrice).toString();
	// 		let classValuations = clientData.equity[r.shareClass].valuations.sort((a, b) => Number(a.date) - Number(b.date));
	// 		let nearestValuation = null;
	// 		for (const valuation of classValuations) {
	// 			if (Number(valuation.date) > Number(r.date)) break;
	// 			nearestValuation = valuation;
	// 		}
	// 		if (nearestValuation) r.sharePrice = nearestValuation.sharePrice;
	// });

	// On first pass, go class-by-class.  May need to revisit with mixed classes for swaps.
	classes.forEach((c) => {
		// First, find dates with multiple transations on the same date
		let transactionsDateCount = giftTransactions
			.filter((g) => g.shareClass == c)
			.reduce((t, r) => {
				t[r.date] = (t[r.date] || 0) + 1;
				return t;
			}, {});
		let multipleSameDates = Object.keys(transactionsDateCount).filter((date) => transactionsDateCount[date] >= 1);
		multipleSameDates.push(Math.floor(Date.now() / 1000).toString()); // add today to catch any recent single sends.
		multipleSameDates.sort((a, b) => Number(a) - Number(b));
		console.log('multipleSameDates[' + c + ']: ', multipleSameDates);

		const usedSendNrids = new Set();
		function addPriorTransfers(transArray) {
			transArray.forEach(addPriorTransfer);
		}
		function addPriorTransfer(trans) {
			if (!trans?.nrid || usedSendNrids.has(trans.nrid)) return false;
			priorTransfers.push(trans);
			usedSendNrids.add(trans.nrid);
			return true;
		}

		let carryOverDeduct = 0;

		// Now, examine each of these dates, starting with the oldest one
		multipleSameDates.forEach(function (d) {
			console.log('### SAME DATE ###: ', moment.unix(d).utc().format('DD-MMM-YY'));

			// First handle any earlier single Send that occurs on a given date by itself
			let singleSendTransactions = giftTransactions.filter((g) => sameClassAndSender(g) && earlierDate(g));
			singleSendTransactions.forEach((singleSend) => {
				let targetQty = Number(singleSend.quantity);
				if (handleNotesMatch(singleSend, targetQty, Number(singleSend.date))) {
					console.log('NOTES MATCH Transaction: ', singleSend);
					singleSend.tranche = c + '-' + singleSend.date;
					addPriorTransfer(singleSend);
					giftTransactions = giftTransactions.filter((g) => g.nrid != singleSend.nrid);
				} else if (handlePerfectFit(singleSend, targetQty, Number(singleSend.date) - 43200, false)) {
					// 1/2 day
					console.log('SINGLE SEND Transaction: ', singleSend);
					singleSend.tranche = c + '-' + singleSend.date;
					addPriorTransfer(singleSend);
					giftTransactions = giftTransactions.filter((g) => g.nrid != singleSend.nrid);
				} else if (handlePerfectFit(singleSend, targetQty, Number(singleSend.date), false)) {
					console.log('SINGLE SEND Transaction: ', singleSend);
					singleSend.tranche = c + '-' + singleSend.date;
					addPriorTransfer(singleSend);
					giftTransactions = giftTransactions.filter((g) => g.nrid != singleSend.nrid);
				} else {
					console.log('*** NO SEND-ONLY MATCH ***');
					let safety = 0;
					let firstHistoric;
					// #6191: prefer a candidate whose own recorded tax basis matches what the sender
					// declared for this send (getSenderDeclaredBasis()/senderBasisMatchesCandidate()) --
					// otherwise this loop picks purely by array position (reversed), which can silently
					// draw shares from the wrong basis lot when an unrelated same-day receive is also in
					// play. Falls back to the original reversed-array search when no basis is declared or
					// no candidate matches it.
					const declaredBasis = getSenderDeclaredBasis(singleSend);
					while (targetQty > 0 && safety < 20) {
						safety++;
						// #6144h: exclude active ("#") certificates from both candidate searches below --
						// same protection oneForOneMatches/handlePerfectFit already give their pools.
						let firstPrior =
							(declaredBasis != null &&
								giftTransactions.find(
									(g) =>
										!activeCertificate(g) &&
										sameClassAndReceiver(g) &&
										Number(g.date) <= Number(singleSend.date) &&
										eligibleAsOf(g, singleSend.date) &&
										isKnownNumber(g.taxBasis) &&
										Math.abs(Number(g.taxBasis) - declaredBasis) < 0.005
								)) ||
							giftTransactions
								.slice()
								.reverse()
								.find(
									(g) =>
										!activeCertificate(g) &&
										sameClassAndReceiver(g) &&
										Number(g.date) <= Number(singleSend.date) &&
										eligibleAsOf(g, singleSend.date)
								);
						if (firstPrior) {
							if (firstPrior.quantity > targetQty) {
								firstPrior.quantity = (Number(firstPrior.quantity) - targetQty).toString();
								firstHistoric = JSON.parse(JSON.stringify(firstPrior));
								firstHistoric.quantity = targetQty.toString();
								firstHistoric.tranche = c + '-' + singleSend.date;
								historicHoldings.push(firstHistoric);
								tagMatch(firstHistoric, 'E', singleSend);
								targetQty = 0;
							} else {
								targetQty -= Number(firstPrior.quantity);
								firstPrior.tranche = c + '-' + singleSend.date;
								historicHoldings.push(firstPrior);
								tagMatch(firstPrior, 'E', singleSend);
								giftTransactions = giftTransactions.filter((g) => g.nrid != firstPrior.nrid);
							}
						}
					}
					console.log('SINGLE SEND Transaction: ', singleSend);
					singleSend.tranche = c + '-' + singleSend.date;
					addPriorTransfer(singleSend);
					giftTransactions = giftTransactions.filter((g) => g.nrid != singleSend.nrid);
				}
			});

			let sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
			let sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
			let sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);

			const certPairs = CERT_COLUMN ? matchSameDayByCertificate(sameDateTransactions) : [];
			if (certPairs.length) {
				certPairs.forEach(({ send, receive }) => {
					send.tranche = c + '-' + d;
					receive.tranche = c + '-' + d;
					addPriorTransfer(send);
					const certPairHolding = { ...receive, tranche: c + '-' + d, quantity: send.quantity };
					tagMatch(certPairHolding, 'A');
					historicHoldings.push(certPairHolding);

					giftTransactions = giftTransactions.filter((g) => g.nrid != send.nrid && g.nrid != receive.nrid);
				});

				sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
				sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
				sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);
			}

			// #6108c (Vermeer): a same-day Receive re-Transferred out same-day under a DIFFERENT
			// cert/reference number (e.g. Reissuance (2618)=214 -> Transfer (2619)=214) isn't caught
			// by matchSameDayByCertificate() above (same cert number required on both sides). Left
			// alone, parseTransfers() has to explain this Receive from priorGrossQty -- the sum of
			// ALL same-date sends, including unrelated Cancellations that have their own correct
			// historic matches elsewhere -- and its blind subset-sum can consume completely unrelated
			// historic lots to hit that inflated target. Net the pass-through by exact quantity here,
			// before any other same-day matcher runs.
			const quantityPairs = CERT_COLUMN ? matchSameDayByQuantityPassthrough(sameDateTransactions) : [];
			if (quantityPairs.length) {
				quantityPairs.forEach(({ send, receive }) => {
					send.tranche = c + '-' + d;
					receive.tranche = c + '-' + d;
					addPriorTransfer(send);
					const passthroughHolding = { ...receive, tranche: c + '-' + d, quantity: send.quantity };
					tagMatch(passthroughHolding, 'C');
					historicHoldings.push(passthroughHolding);

					giftTransactions = giftTransactions.filter((g) => g.nrid != send.nrid && g.nrid != receive.nrid);
				});

				sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
				sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
				sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);
			}

			// Next process any notes-match transfers (remove as we go)
			const descMatchedSendNrids = new Set();
			// #6144w: a multi-send same-day batch (2+ sends) can have EVERY send independently
			// name its OWN bare cert (no "from" clause, e.g. "Redemption (2094)"), each uniquely
			// matching a distinct earlier lot of the identical quantity -- resolve those FIRST,
			// before the batch-level blind quantity-sum matching below (exactMatchTrans /
			// findSingleCertExactMatch) gets a chance to grab an unrelated same-quantity cert
			// instead. Real incident: Reed Kirk Asplundh's "Redemption (2094)" + "Redemption
			// (1727)" (100+100) wrongly summed to and consumed "Issuance (1617)", a genuinely
			// unrelated 200-share cert. All-or-nothing: only commits when EVERY send in the batch
			// resolves this way, never partially -- a batch this can't fully explain is left
			// untouched for the existing matchers, so this never perturbs an account (e.g. Edward
			// K. Asplundh) whose same-day batches need the existing blind-sum/subset logic.
			if (sameDateSends.length > 1) {
				const candidatePairs = [];
				const claimedNrids = new Set();
				let allResolved = true;
				for (const send of sameDateSends) {
					const ownToken = getLotCertToken(send.notes);
					if (!ownToken) {
						allResolved = false;
						break;
					}
					const tokenMatches = giftTransactions.filter(
						(g) =>
							sameClassAndReceiver(g) &&
							!activeCertificate(g) &&
							!claimedNrids.has(g.nrid) &&
							earlierDate(g) &&
							eligibleAsOf(g, send.date) &&
							getLotCertToken(g.notes) === ownToken
					);
					if (tokenMatches.length !== 1 || Number(tokenMatches[0].quantity) !== Number(send.quantity)) {
						allResolved = false;
						break;
					}
					claimedNrids.add(tokenMatches[0].nrid);
					candidatePairs.push({ send, match: tokenMatches[0] });
				}
				if (allResolved && candidatePairs.length) {
					candidatePairs.forEach(({ send, match }) => {
						const trancheItem = { ...match, tranche: c + '-' + send.date };
						tagMatch(trancheItem, 'A');
						historicHoldings.push(trancheItem);
						send.tranche = c + '-' + send.date;
						addPriorTransfer(send);
						descMatchedSendNrids.add(send.nrid);
					});
					giftTransactions = giftTransactions.filter((g) => !claimedNrids.has(g.nrid) && !descMatchedSendNrids.has(g.nrid));
					sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
					sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
					sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);
				}
			}
			// #6144n: don't let a Send drain a cert some sibling same-day Receive explicitly
			// names for itself (e.g. from a copy/paste source list that just happens to include
			// it) -- parseTransfers()'s handlePerfectFit (#6144l, and its partial-lot draw,
			// #6144o) resolves that Receive's own claim instead. A cert no Receive claims at
			// all is unaffected -- this is the only place that will ever find it.
			const contestedCerts = CERT_COLUMN ? new Set(sameDateReceives.flatMap((r) => Array.from(getExplicitSourceCertSet(r.notes) || []))) : null;
			// IMPORTANT: iterate a snapshot because we mutate giftTransactions inside the loop
			for (const send of [...sameDateSends]) {
				if (handleNotesMatch(send, Number(send.quantity), Number(send.date), contestedCerts)) {
					console.log('NOTES MATCH Transaction: ', send);
					send.tranche = c + '-' + send.date;
					addPriorTransfer(send);
					// Remove the SEND from the active pool so later branches don't touch it
					descMatchedSendNrids.add(send.nrid);
				}
			}
			// Apply removals once (avoids mid-loop topology weirdness)
			if (descMatchedSendNrids.size) giftTransactions = giftTransactions.filter((t) => !descMatchedSendNrids.has(t.nrid));
			// Recompute same-day lists after removing desc-matched sends
			sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
			sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
			sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);

			// Next look for same day cancel/reissue (e.g. lost certificate)
			if (isEDM && sameDateSends.length && sameDateReceives.length >= 1) releaseRestrictedEquity(sameDateTransactions);

			const PREFER_CERT_LINEAGE = CERT_COLUMN && !isASP;
			const hasSelfContainedCertFlow = isSelfContainedCertificateFlow(sameDateTransactions);
			const shouldApplyCertLineage = PREFER_CERT_LINEAGE && !hasSelfContainedCertFlow && sameDateSends.length;
			if (shouldApplyCertLineage) cancelReissueCertificate(sameDateTransactions);

			// Next, more complex scenarios
			sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
			sameDateTransactions.sort((a, b) => Number(b.quantity) - Number(a.quantity));
			sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
			sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);
			let sameDateSenders = [...new Set(sameDateTransactions.map((t) => t.sender))];
			let sameDateReceivers = [...new Set(sameDateTransactions.map((t) => t.receiver))];
			let sameDateSendQtySum = sameDateSends.reduce((a, b) => a + Number(b.quantity), 0);
			let sameDateReceiveQtySum = sameDateReceives.reduce((a, b) => a + Number(b.quantity), 0);
			let sameDateSendAmtSum = sameDateSends.reduce((a, b) => a + Number(b.quantity) * toNumber(b.sharePrice), 0);
			let sameDateReceiveAmtSum = sameDateReceives.reduce((a, b) => a + Number(b.quantity) * toNumber(b.sharePrice), 0);
			let sameDateNetQty = Math.abs(sameDateSendQtySum - sameDateReceiveQtySum);
			let sameDateNetAmt = Math.abs(sameDateSendAmtSum - sameDateReceiveAmtSum);
			// #6144t: every txn in a $0-priced account nets sameDateNetAmt to a trivial 0,
			// so the amt-based SPLIT branch below is meaningless noise there -- gate it on
			// actual non-zero price data existing for this date's batch.
			let sameDateHasPriceAmt = sameDateSendAmtSum > 0 || sameDateReceiveAmtSum > 0;
			// console.log("sameDate: ", d);
			// console.log("sameDateTrans: ", sameDateTransactions);
			// console.log("Senders: ", sameDateSenders);
			// console.log("Receivers: ", sameDateReceivers);
			// console.log("Sends: ", sameDateSends);
			// console.log("Receives: ", sameDateReceives);
			// console.log("SendQtySum: ", sameDateSendQtySum);
			// console.log("ReceiveQtySum: ", sameDateReceiveQtySum);
			// console.log("SendAmtSum: ", sameDateSendAmtSum);
			// console.log("ReceiveAmtSum: ", sameDateReceiveAmtSum);

			let isSwapOrSplit = false;
			if (sameDateSends.length == 0) {
				// ONE WAY recieve transactions - do nothing
				console.log('ONE-WAY Receiving: ', sameDateReceives);
			} else if (sameDateReceives.length == 0) {
				// ONE WAY send transactions - look for matches
				console.log('ONE-WAY Sending: ', sameDateSends);

				if (moment.unix(Number(d)).utc().format('YYYY-MM-DD') == '2015-04-08' && c == 'C') {
					console.log(
						'AL sameDateSends:',
						sameDateSends.map((s) => ({
							nrid: s.nrid,
							notes: s.notes,
							quantity: s.quantity,
						}))
					);
				}

				// First try finding old certificates, where each exactly matches one of the Sends
				let exactMatches = oneForOneMatches(sameDateSends);
				if (exactMatches) return;

				// Attempt 1 - first look for one prior Receive that exactly matches the collection of Sends
				// #6144u: exclude active ("#") certs -- handlePerfectFit/oneForOneMatches already do, this search didn't.
				let exactMatchTrans = giftTransactions.find(
					(g) =>
						sameClassAndReceiver(g) &&
						!activeCertificate(g) &&
						earlierDate(g) &&
						eligibleAsOf(g, d) &&
						sameQty(g, sameDateSendQtySum) &&
						!(sameDateSends.length === 1 && cancellationCertMismatch(sameDateSends[0].notes, g.notes))
				);
				if (exactMatchTrans) {
					let sendTransactions = giftTransactions.filter((g) => sameClassAndSender(g) && sameDate(g));
					sendTransactions.forEach((s) => (s.tranche = c + '-' + d));
					addPriorTransfers(sendTransactions);
					console.log('SEND Transactions: ', sendTransactions);
					// FIX: only remove the sends we *actually captured*, not everything <= date
					const sendNridSet = new Set(sendTransactions.map((t) => t.nrid).filter(Boolean));
					giftTransactions = giftTransactions.filter((g) => !sendNridSet.has(g.nrid));
					// giftTransactions = giftTransactions.filter(g => !sameClassAndSender(g) || !sameDate(g));
					giftTransactions = giftTransactions.filter((g) => g.nrid != exactMatchTrans.nrid);
					exactMatchTrans.tranche = c + '-' + d;
					tagMatch(exactMatchTrans, 'C', sameDateSends[0]);
					historicHoldings.push(exactMatchTrans);
				} else {
					// Need to tear things apart, Send by Send
					let closeMatchTrans;
					sameDateSends.forEach((s) => {
						// Attempt 2 & 3 -Try to find one or more prior Receives that match this Send
						exactMatchTrans = giftTransactions.find(
							(g) =>
								sameClassAndReceiver(g) &&
								earlierDate(g) &&
								eligibleAsOf(g, s.date) &&
								sameQty(g, Number(s.quantity)) &&
								!cancellationCertMismatch(s.notes, g.notes) &&
								(notRestricted(g) || !isEDM)
						);
						if (exactMatchTrans || !parseTransfers()) {
							let sendTransaction = giftTransactions.filter((g) => g.nrid == s.nrid)[0];
							if (!sendTransaction) return;
							sendTransaction.tranche = c + '-' + d;
							addPriorTransfer(sendTransaction);
							giftTransactions = giftTransactions.filter((g) => g.nrid != s.nrid);
							if (exactMatchTrans) {
								// <- Good, a simple solution, all done (Attempt 2)
								giftTransactions = giftTransactions.filter((g) => g.nrid != exactMatchTrans.nrid);
								exactMatchTrans.tranche = c + '-' + d;
								tagMatch(exactMatchTrans, 'C', sendTransaction);
								historicHoldings.push(exactMatchTrans);
							} else if (!closeMatchTrans) {
								// <- Need to score "open" prior Receives to find the best one (Attempt 3 & 4)
								// #6144h: exclude active ("#") certificates -- oneForOneMatches/handlePerfectFit
								// already do; this pool was the odd one out.
								let openTransactions = giftTransactions.filter(
									(g) =>
										!activeCertificate(g) &&
										sameClassAndReceiver(g) &&
										earlierDate(g) &&
										!g.tranche &&
										eligibleAsOf(g, d) && // or sendTransaction.date ??
										(notRestricted(g) || !isEDM)
								);
								if (openTransactions.length) {
									// After finding the closest match, clone this Receive as a "Twin" and move on to the next Send for this date
									closeMatchTrans = scoreMatches(sendTransaction, openTransactions);
									console.log('CLOSE Match: ', closeMatchTrans);
									if (closeMatchTrans) {
										// need new object
										closeMatchTrans = JSON.parse(JSON.stringify(closeMatchTrans));
										// giftTransactions = giftTransactions.filter(g => g.nrid != bestMatchTrans.nrid);
										closeMatchTrans.tranche = c + '-' + d;
										if (!closeMatchTrans.before) closeMatchTrans.before = closeMatchTrans.quantity;
										closeMatchTrans.quantity = s.quantity;
										// #6144b: only one open candidate -> 'I' (O.P.), not 'E' -- no guessing was involved.
										tagMatch(closeMatchTrans, openTransactions.length == 1 ? 'I' : 'E', sendTransaction);
										historicHoldings.push(closeMatchTrans);
									}
								}
							}
						}
						// For a close match (not exact), deduct the from the "Twin" the quantity of every Send
						if (closeMatchTrans) {
							let matchTwin = giftTransactions.find((h) => h.nrid == closeMatchTrans.nrid);
							if (!matchTwin.before) matchTwin.before = Number(matchTwin.quantity);
							matchTwin.quantity = Number(matchTwin.quantity) - Number(s.quantity);
							if (carryOverDeduct) {
								matchTwin.quantity += carryOverDeduct;
								carryOverDeduct = 0;
							}
							if (matchTwin.quantity < 0) {
								carryOverDeduct = matchTwin.quantity;
								matchTwin.quantity = 0;
							}
							matchTwin.quantity = matchTwin.quantity.toString();
						}
						closeMatchTrans = null; // reset
					});
				}
			} else if (sameDateReceivers.length < 3 && (sameDateNetQty < 0.1 || (sameDateHasPriceAmt && sameDateNetAmt < 0.1))) {
				if (sameDateNetQty < 0.1) {
					if (sameDateSenders.sort().toString() != sameDateReceivers.sort().toString()) {
						// Attempt 1 - it's a TRANSFER
						let uniqueSenders = sameDateSenders.filter((u) => !sameDateReceivers.includes(u));
						let uniqueReceivers = sameDateReceivers.filter((u) => !sameDateSenders.includes(u));
						console.log('TRANSFER Transactions: ', uniqueSenders.concat(uniqueReceivers));
					}
					// Attempt 1 - it's a SWAP
					isSwapOrSplit = true;
					parseTransfers();
				} else if (sameDateNetAmt < 0.1) {
					// Attempt 2 - it's a SPLIT
					isSwapOrSplit = true;
					parseTransfers();
				}
			} else {
				// Attempt 2 - it might be a Vermeer-style reissue transfer
				// Look for a pair transaction with the issuer (equal quantity, cancellation/re-issue)
				pairedQty = 0;
				let exactMatch = false;
				// Remove active certificates - NO!!
				// sameDateSends = sameDateSends.filter(s => !activeCertificate(s));
				sameDateSends.forEach((send) => {
					sameDateReceives.forEach((receive) => {
						if (quantitiesMatch(send.quantity, receive.quantity)) pairedQty += Number(receive.quantity);
					});
				});

				// First try finding old certificates, where each quantity exactly matches one of the Sends
				if (pairedQty && !exactMatch) exactMatch = oneForOneMatches(sameDateSends);

				// Next try to find one old certificate that is an exact match for the collection of Sends
				// #6144u: exclude active ("#") certs here too -- same gap as the ONE-WAY Sending branch above.
				let exactMatchTrans = giftTransactions.find(
					(g) =>
						sameClassAndReceiver(g) &&
						!activeCertificate(g) &&
						earlierDate(g) &&
						sameQty(g, sameDateSendQtySum) &&
						!(sameDateSends.length === 1 && cancellationCertMismatch(sameDateSends[0].notes, g.notes))
				);
				if (exactMatchTrans) {
					giftTransactions = giftTransactions.filter((g) => g.nrid != exactMatchTrans.nrid);
					exactMatchTrans.tranche = c + '-' + d;
					tagMatch(exactMatchTrans, 'C', sameDateSends[0]);
					historicHoldings.push(exactMatchTrans);
					// Remove all of the associated send transactions
					let sendTransactions = giftTransactions.filter((g) => sameClassAndSender(g) && thisDateOrEarlier(g));
					sendTransactions.forEach((s) => (s.tranche = c + '-' + d));
					sendTransactions.forEach((adjTrans) => {
						carryForwardBasisFields(adjTrans, exactMatchTrans);
					});
					addPriorTransfers(sendTransactions);
					console.log('SEND Transactions: ', sendTransactions);
					// FIX: only remove the sends we *actually captured*, not everything <= date
					const sendNridSet = new Set(sendTransactions.map((t) => t.nrid).filter(Boolean));
					giftTransactions = giftTransactions.filter((g) => !sendNridSet.has(g.nrid));
					// giftTransactions = giftTransactions.filter(g => !sameClassAndSender(g) || !thisDateOrEarlier(g));
					sameDateReceives.forEach((adjTrans) => {
						carryForwardBasisFields(adjTrans, exactMatchTrans);
					});
					exactMatch = true;
				}

				let handledByParseTransfers = false;

				// If that fails, start rolling up old certificates using stub logic below
				if (pairedQty && !exactMatch) handledByParseTransfers = parseTransfers();

				// It's not a Vermeer-style reissue...
				if (!pairedQty && sameDateReceives.length == 1 && sameDateSends.length > 1) {
					// If net sending (no STUB), bail out.  Not sure how to handle if mult-legged....
					if (sameDateReceiveAmtSum > sameDateSendAmtSum + 1) {
						console.log('NET Receive Amount: ', sameDateReceiveAmtSum - sameDateSendAmtSum);
					}
					// It's a STUB
					console.log('NET Send Amount: ', sameDateSendAmtSum - sameDateReceiveAmtSum);
					handledByParseTransfers = parseTransfers();
				}

				// Finally look for cancel/reissue certificate number
				// #6144w: don't re-run this when exactMatchTrans above already fully resolved
				// the batch -- real incident: Edward K. Asplundh's single send "Redemption (1939)"
				// got consumed once by exactMatchTrans (a blind quantity match) and then AGAIN here
				// by canceledCertificate (a cert-token match), pulling in two historic holdings
				// (Issuance (404) and Issuance (1939)) for the one send, doubling the tranche.
				if (CERT_COLUMN && !handledByParseTransfers && !exactMatch) canceledCertificate(sameDateSends);
			}

			function matchSameDayByCertificate(sameDateTransactions) {
				const sends = sameDateTransactions.filter((t) => t.sender == giftUserId);
				const receives = sameDateTransactions.filter((t) => t.receiver == giftUserId);

				const usedReceiveNrids = new Set();
				const exactPairs = [];

				sends.forEach((send) => {
					const sendRefs = parseCertificateRefs(send.notes);
					const sendCert = sendRefs.newCert || (sendRefs.sourceCerts?.length == 1 ? sendRefs.sourceCerts[0] : '');
					if (!sendCert) return;

					const match = receives.find((r) => {
						if (usedReceiveNrids.has(r.nrid)) return false;

						const receiveRefs = parseCertificateRefs(r.notes);
						const receiveCert = receiveRefs.newCert || (receiveRefs.sourceCerts?.length == 1 ? receiveRefs.sourceCerts[0] : '');
						if (!receiveCert) return false;

						return receiveCert == sendCert && quantitiesMatch(r.quantity, send.quantity);
					});

					if (match) {
						usedReceiveNrids.add(match.nrid);
						exactPairs.push({ send, receive: match });
					}
				});

				return exactPairs;
			}

			// #6108c (Vermeer): pairs a same-day Send (an actual Transfer, never a Cancellation TO
			// the issuer -- that must still be matched against its own historic lot below) against a
			// same-day issuer-sourced Receive (a freshly manufactured cert, not an independent
			// third-party transfer-in with its own history) of the identical quantity. Excludes any
			// Receive tagged active/"#" (activeCertificate()) -- that convention marks a cert that
			// must persist as a live, current tranche, never get archived as a consumed historic
			// source just because its quantity coincides with an unrelated same-day Send. Exactly
			// one candidate must exist on each side -- an ambiguous multi-way quantity tie is left
			// untouched rather than guessed at.
			function matchSameDayByQuantityPassthrough(sameDateTransactions) {
				const sends = sameDateTransactions.filter((t) => t.sender == giftUserId && !toIssuer(t));
				const receives = sameDateTransactions.filter((t) => t.receiver == giftUserId && fromIssuer(t) && !activeCertificate(t));
				const pairs = [];
				const usedReceiveNrids = new Set();
				sends.forEach((send) => {
					const sendTwins = sends.filter((s) => quantitiesMatch(s.quantity, send.quantity));
					if (sendTwins.length !== 1) return; // ambiguous on the send side -- leave to existing matchers
					const receiveMatches = receives.filter((r) => !usedReceiveNrids.has(r.nrid) && quantitiesMatch(r.quantity, send.quantity));
					if (receiveMatches.length !== 1) return; // no match, or ambiguous
					usedReceiveNrids.add(receiveMatches[0].nrid);
					pairs.push({ send, receive: receiveMatches[0] });
				});
				return pairs;
			}

			function isSelfContainedCertificateFlow(transactions) {
				const getSourceCert = (notes) => {
					const match = notes?.match(/from (\d+)/);
					if (match) return match[1];
					const fallback = notes?.match(/\(#?(\d+)/);
					return fallback ? fallback[1] : null;
				};
				let sourceCerts = new Set();
				transactions.forEach((t) => {
					if (t.sender !== 'asplundh') {
						const cert = getSourceCert(t.notes);
						if (cert) sourceCerts.add(cert);
					}
				});
				return sourceCerts.size === 1;
			}

			function releaseRestrictedEquity(sameDaySends) {
				console.log('Begining sameDateSends: ', sameDaySends);
				// First try finding certificates, where each exactly matches one of the Sends
				let nonMatch;
				let matchCount = 0;
				sameDaySends.forEach((send) => {
					if (!toIssuer(send)) return;
					console.log('This Send: ', send);
					let exactMatches = giftTransactions.filter(
						(g) =>
							send.nrid != g.nrid &&
							quantitiesMatch(send.quantity, g.quantity) &&
							sameClassAndReceiver(g) &&
							sameDate(g) &&
							sameCertificate(g.notes, send.notes)
					);
					let exactMatch = !exactMatches ? null : exactMatches.length == 1 ? exactMatches[0] : scoreMatches(send, exactMatches);
					console.log('Very close match: ', exactMatch);
					if (exactMatch) {
						let sendQty = Number(send.quantity);
						let otherSends = giftTransactions.filter((g) => send.nrid != g.nrid && sameClassAndSender(g) && sameDate(g) && !isReleaseLike(g));
						console.log('Other sends: ', otherSends);
						let otherSend = otherSends.length == 1 ? otherSends[0] : scoreDifferences(send, otherSends);
						let otherSendQty = otherSend ? Number(otherSend.quantity) : 0;
						// if (otherSends.length) otherSendQty = otherSends.reduce((sum, g) => sum + Number(g.quantity), 0);

						// Get the other receive tranche, the non-match
						let nonMatches = giftTransactions.filter(
							(g) =>
								g.nrid !== exactMatch.nrid &&
								sameClassAndReceiver(g) &&
								thisDateOrEarlier(g) &&
								eligibleAsOf(g, send.date) &&
								Number(g.quantity) >= sendQty
						);
						if (nonMatches?.length) {
							// handle the nonMatch as new object
							nonMatch = JSON.parse(JSON.stringify(nonMatches.length == 1 ? nonMatches[0] : scoreDifferences(send, nonMatches)));
							let nonMatchQty = Number(nonMatch.quantity);
							// If excess quantity, split the NON MATCH. otherwise remove the NON MATCH from current gift transactions
							if (nonMatchQty < sendQty || otherSendQty > sendQty) return false;
							else {
								let origNonMatch = giftTransactions.find((g) => g.nrid == nonMatch.nrid);
								origNonMatch.quantity = (nonMatchQty - sendQty).toString();
								send.quantity = (sendQty - otherSendQty).toString();
								exactMatch.quantity = send.quantity;
							}

							nonMatch.quantity = sendQty.toString();
							nonMatch.tranche = c + '-' + d;
							// #6144b: only one eligible candidate -> 'I' (O.P.), not 'C' -- no guessing was involved.
							tagMatch(nonMatch, nonMatches.length == 1 ? 'I' : 'E', send);
							// 'G' (sender-declared basis) is the stronger signal -- only promote to 'H' if G didn't already win.
							if (nonMatch._matchKey !== 'G' && noteDateMatchesCandidate(send, nonMatch)) nonMatch._matchKey = 'H';
							historicHoldings.push(nonMatch);

							matchCount++;
							processSendTransaction(send);
							// if (otherSends.length) otherSends.forEach(processSendTransaction);
							if (otherSend) processSendTransaction(otherSend);
						}
					}
				});
				console.log('Ending sameDateSends: ', sameDaySends);
				return matchCount > 0;

				function processSendTransaction(g) {
					g.tranche = c + '-' + d;
					giftTransactions = giftTransactions.filter((x) => x.nrid !== g.nrid);
					carryForwardBasisFields(g, nonMatch);
					addPriorTransfer(g);
					console.log('SEND Transaction: ', g);
					sameDateSends = sameDateSends.filter((s) => s.nrid !== g.nrid);
				}
			}

			function cancelReissueCertificate(sameDateSends) {
				console.log('sameDateSends: ', sameDateSends);
				// #6108b (Vermeer): identity match, not sameCertificate()'s lineage match --
				// a same-day Reissuance(Y from X) is X's successor, not what X's Cancellation consumed.
				function sameCertificateIdentity(transNotes, notes) {
					if (!CERT_COLUMN) return areSimilar(transNotes, notes);
					const a = parseCertificateRefs(transNotes);
					const b = parseCertificateRefs(notes);
					return !!(a.newCert && b.newCert && a.newCert == b.newCert);
				}
				// First try finding certificates, where each exactly matches one of the Sends
				let matchCount = 0;
				sameDateSends.forEach((send) => {
					if (!toIssuer(send)) return;
					console.log('This Send: ', send);
					let exactMatches = giftTransactions.filter(
						(g) =>
							send.nrid != g.nrid &&
							quantitiesMatch(send.quantity, g.quantity) &&
							sameClassAndReceiver(g) &&
							sameDate(g) &&
							sameCertificateIdentity(g.notes, send.notes)
					);
					let exactMatch = !exactMatches ? null : exactMatches.length == 1 ? exactMatches[0] : scoreMatches(send, exactMatches);
					console.log('SAME Certificate: ', exactMatch);
					if (exactMatch) {
						// #6144f: exactMatch is the same-day REISSUANCE side of a cancel+reissue pair.
						// If the cancelled cert ALSO still has its own, separately-dated holding open
						// (e.g. a partial Buy-Back already drew it down without closing it out), that
						// older holding is what this Cancellation is really supposed to close -- not
						// the brand-new reissuance, which is a live holding in its own right and has
						// nothing further disposing of it. Defer to the general matchers below
						// (oneForOneMatches/parseTransfers) instead of archiving the reissuance.
						const olderSameCertHolding = giftTransactions.some(
							(g) => g.nrid != exactMatch.nrid && sameClassAndReceiver(g) && !sameDate(g) && sameCertificate(g.notes, send.notes)
						);
						if (olderSameCertHolding) return;

						matchCount++;
						send.tranche = c + '-' + d;
						// Remove the SEND from current gift transactions
						giftTransactions = giftTransactions.filter((g) => g.nrid != send.nrid);
						let matches = giftTransactions.filter(
							(g) =>
								send.nrid != g.nrid &&
								quantitiesMatch(send.quantity, g.quantity) &&
								sameClassAndReceiver(g) &&
								sameDate(g) &&
								sameCertificateIdentity(g.notes, send.notes)
						);
						if (matches.length) {
							// there should only be one match, alert otherwise??
							let match = matches.length == 1 ? matches[0] : scoreMatches(send, matches);
							match.tranche = c + '-' + d;
							// Add the MATCH to historic holdings
							tagMatch(match, 'A');
							historicHoldings.push(match);
							// Remove the MATCH from current gift transactions
							giftTransactions = giftTransactions.filter((g) => g.nrid != match.nrid);
							carryForwardBasisFields(send, match);
							// Add the SEND to prior transfers
							addPriorTransfer(send);
							console.log('SEND Transaction: ', send);
						}
					}
				});
				return matchCount > 0;
			}

			function canceledCertificate(sameDateSends) {
				// First, see whether this path applies at all
				let foundAny = sameDateSends.some((send) => {
					let exactMatches = giftTransactions.filter(
						(g) =>
							quantitiesMatch(send.quantity, g.quantity) &&
							sameClassAndReceiver(g) &&
							earlierDate(g) &&
							eligibleAsOf(g, send.date) &&
							sameCertificate(g.notes, send.notes)
					);
					let exactMatch = !exactMatches ? null : exactMatches.length == 1 ? exactMatches[0] : scoreMatches(send, exactMatches);
					return !!exactMatch;
				});

				if (!foundAny) return false;

				// Process the whole send set ONCE
				sameDateSends.forEach((s) => {
					s.tranche = c + '-' + d;
					giftTransactions = giftTransactions.filter((g) => g.nrid != s.nrid);

					let matches = giftTransactions.filter(
						(g) => sameClassAndReceiver(g) && earlierDate(g) && eligibleAsOf(g, s.date) && sameCertificate(g.notes, s.notes)
					);

					if (!matches.length) {
						matches = giftTransactions.filter(
							(g) => quantitiesMatch(s.quantity, g.quantity) && sameClassAndReceiver(g) && sameDate(g) && sameCertificate(g.notes, s.notes)
						);
					}

					if (matches.length) {
						let match = matches.length == 1 ? matches[0] : scoreMatches(s, matches);
						let sendQty = Number(s.quantity);
						let matchQty = Number(match.quantity);
						// #6144j: only archive the portion this send needs -- a same-cert match bigger
						// than the send (the norm for a partial cancellation) was being consumed whole,
						// silently dropping the untouched remainder instead of leaving it live.
						if (matchQty > sendQty) {
							let consumed = JSON.parse(JSON.stringify(match));
							consumed.quantity = sendQty.toString();
							consumed.tranche = c + '-' + d;
							tagMatch(consumed, 'A');
							historicHoldings.push(consumed);
							let orig = giftTransactions.find((g) => g.nrid == match.nrid);
							orig.quantity = (matchQty - sendQty).toString();
							carryForwardBasisFields(s, consumed);
						} else {
							match.tranche = c + '-' + d;
							tagMatch(match, 'A');
							historicHoldings.push(match);
							giftTransactions = giftTransactions.filter((g) => g.nrid != match.nrid);
							carryForwardBasisFields(s, match);
						}
					}
				});

				addPriorTransfers(sameDateSends);
				console.log('SEND Transactions: ', sameDateSends);
				return true;
			}

			// A cancellation's quantity can coincidentally match an unrelated transaction's.
			// If the cancellation names real cert(s), require the candidate to share one.
			function cancellationCertMismatch(sendNotes, candidateNotes) {
				if (!/\bcancel/i.test(sendNotes || '')) return false;
				const sendRefs = parseCertificateRefs(sendNotes);
				const candRefs = parseCertificateRefs(candidateNotes);
				const sendHasCert = sendRefs.newCert || sendRefs.sourceCerts.length;
				const candHasCert = candRefs.newCert || candRefs.sourceCerts.length;
				if (!sendHasCert || !candHasCert) return false;
				return !sameCertificate(candidateNotes, sendNotes);
			}

			function oneForOneMatches(sameDateSends) {
				// First try finding old certificates, where each exactly matches one of the Sends
				let matchCount = 0;
				// need clone of giftTransactions to handle potential duplicate quantities
				let giftClone = JSON.parse(JSON.stringify(giftTransactions));
				if (isEDM) giftClone = giftClone.filter((g) => notRestricted(g));
				sameDateSends.forEach((send) => {
					let exactMatches = giftClone.filter(
						(g) =>
							!activeCertificate(g) &&
							sameClassAndReceiver(g) &&
							earlierDate(g) &&
							eligibleAsOf(g, send.date) &&
							(notRestricted(g) || !isEDM) &&
							sameQty(g, Number(send.quantity))
					);
					let exactMatch = !exactMatches ? null : exactMatches.length == 1 ? exactMatches[0] : scoreMatches(send, exactMatches);
					console.log('EXACT Match: ', exactMatch);
					if (exactMatch) {
						giftClone = giftClone.filter((g) => g.nrid != exactMatch.nrid);
						matchCount++;
					}
				});

				if (moment.unix(Number(d)).utc().format('YYYY-MM-DD') == '2015-04-08' && c == 'C') {
					console.log('AL oneForOne matchCount:', matchCount, 'needed:', sameDateSends.length);
				}

				if (matchCount == sameDateSends.length) {
					sameDateSends.forEach((s) => {
						s.tranche = c + '-' + d;
						giftTransactions = giftTransactions.filter((g) => g.nrid != s.nrid);
						let matches = giftTransactions.filter(
							(g) =>
								!activeCertificate(g) &&
								sameClassAndReceiver(g) &&
								earlierDate(g) &&
								eligibleAsOf(g, s.date) &&
								(notRestricted(g) || !isEDM) &&
								sameQty(g, Number(s.quantity))
						);
						if (matches.length) {
							let match = matches.length == 1 ? matches[0] : scoreMatches(s, matches);
							match.tranche = c + '-' + d;
							tagMatch(match, matches.length == 1 ? 'C' : 'E', s);
							historicHoldings.push(match);
							giftTransactions = giftTransactions.filter((g) => g.nrid != match.nrid);
							carryForwardBasisFields(s, match);
						}
					});
					addPriorTransfers(sameDateSends);
					console.log('SEND Transactions: ', sameDateSends);
					return true;
				}
				return false;
			}

			// This method identifies the best combination of Prior Receives to match a collection of Sends
			// It also handles swaps and splits, if identified
			function parseTransfers() {
				let splitRatio = 1;
				if (isSwapOrSplit) {
					splitRatio = sameDateReceiveQtySum / sameDateSendQtySum;
					console.log('SPLIT Ratio: ', splitRatio);
				}
				// If stub, get stub info
				let stubs = sameDateTransactions.filter((b) => b.receiver == giftUserId);
				// let stubMatches = sameDateTransactions.filter(m => sameClassAndSender(m));
				let stubMatches = giftTransactions.filter((m) => sameClassAndSender(m) && sameDate(m));
				// Remove active certificates - Maybe...
				// stubMatches = stubMatches.filter(s => !activeCertificate(s));
				console.log('STUB Transactions: ', stubs);
				console.log('STUB Matches: ', stubMatches);

				// For each re-issue, remove the returned cert(s)
				// Find matching old cert(s), and calculate gross cost
				// Move old cert(s) to Prior list

				let foundGroupFit;
				let priorGrossQty = stubMatches.reduce((a, b) => a + Number(b.quantity), 0);
				let priorGrossCost = stubMatches.reduce((a, b) => a + Number(b.quantity) * toNumber(b.sharePrice), 0);
				let newPrice = priorGrossQty ? priorGrossCost / priorGrossQty : 0;
				console.log('PRIOR grossQty = ' + priorGrossQty);
				console.log('PRIOR grossCost = ' + priorGrossCost);
				console.log('NEW price = ' + newPrice);
				if (!sameDateReceives.length) {
					// Look for perfect match
					// find and remove the prior receive transaction(s) that sum to targetQty
					let priorTrans = giftTransactions.filter((g) => thisDateOrEarlier(g) && sameClassAndReceiver(g));
					priorTrans = priorTrans.filter((p) => eligibleAsOf(p, d));
					// Remove active certificates
					priorTrans = priorTrans.filter((p) => !activeCertificate(p));
					priorTrans = priorTrans.filter((p) => !isSameDayReissuance(p, d));
					priorTrans.sort((a, b) => (Number(a.date) > Number(b.date) ? -1 : 1)); // newest to oldest
					priorTrans.sort((a, b) => Number(b.quantity) - Number(a.quantity)); // largest to smallest
					console.log('PRIOR Transactions: ', priorTrans);
					minDiff = 999; // reset arbitrarily large starting number used in tranche analysis
					findSubset.callCount = 0;
					let perfectFit = findSubset(priorGrossQty, priorTrans);
					if (perfectFit) {
						foundGroupFit = true;
						console.log('PERFECT Fit: ', perfectFit);
						perfectFit.forEach((p) => (p.tranche = c + '-' + d));
						perfectFit.forEach((p) => tagMatch(p, perfectFit.length > 1 ? 'D' : priorTrans.length == 1 ? 'I' : 'C', stubMatches[0]));
						historicHoldings = historicHoldings.concat(perfectFit);
						let perfectNrids = new Set(perfectFit.map((item) => item.nrid));
						giftTransactions = giftTransactions.filter((g) => !perfectNrids.has(g.nrid));
						priorGrossQty = perfectFit.reduce((acc, val) => acc + Number(val.quantity), 0);
						priorGrossCost = perfectFit.reduce((acc, val) => acc + Number(val.quantity) * toNumber(val.sharePrice), 0);
					} else console.log('no further fit');

					// let thisTransaction = giftTransactions.find(g => g.nrid == trans.nrid);
					// if (thisTransaction) thisTransaction.sharePrice = (priorGrossCost / (isSwapOrSplit ? Number(thisTransaction.quantity) : priorGrossQty)).toString();
					// console.log("ADJUSTED Transaction: ", thisTransaction);
					// return perfectFit ? true : false;
				} else {
					let totalReceived = 0;
					let sourcedQty = 0; // #6144r: qty actually sourced from historicals (vs. receive-side qty)
					let targetQuantity = priorGrossQty * splitRatio;
					// #6144l: claim every receive's EXPLICIT cert match first, across the whole
					// batch, before any receive falls through to blind subset-sum below --
					// otherwise an unresolved line's blind search can eat a candidate a later,
					// explicitly-sourced line in this same batch still needs.
					sameDateReceives.forEach((receiveTran) => {
						let explicitTargetQty = isSwapOrSplit ? Number(receiveTran.quantity) / splitRatio : priorGrossQty;
						let sourced = handlePerfectFit(receiveTran, explicitTargetQty, Number(d), isSwapOrSplit, true);
						if (sourced) {
							totalReceived += Number(receiveTran.quantity);
							sourcedQty += Number(sourced);
							receiveTran.tranche = c + '-' + d;
							foundGroupFit = true;
						}
					});
					// #6144q: smallest-target-first -- a same-date batch's blind subset-sum searches
					// share one candidate pool, and findSubset() just returns the first exact-sum match
					// it finds, so a larger target processed first can consume candidates a smaller
					// sibling target actually needed (confirmed against RAT 2011 GST Exempt Irrevocable
					// Trust's real data: descending order let the 2704 target grab 8 lots including ones
					// the 769 target could only be built from, leaving 769 unmatched). Ascending order
					// resolves the more-constrained (smaller) targets while the pool is still whole.
					[...sameDateReceives]
						.sort((a, b) => Number(a.quantity) - Number(b.quantity))
						.forEach((receiveTran) => {
							if (receiveTran.tranche) return; // already claimed above
							// #6144r: buy-backs (receives < sends) can never satisfy totalReceived>=targetQuantity,
							// so every remaining receive ran its own full-target blind search. Compare sourced qty
							// instead for that case; swap/split path (Walt/Mazzetti below) is untouched.
							let groupSatisfied = isSwapOrSplit ? totalReceived >= targetQuantity : sourcedQty >= targetQuantity;
							if (foundGroupFit && groupSatisfied) return;
							// if (foundGroupFit) return; <-- FAILS with Walt at Mazzetti
							if (isSwapOrSplit) {
								priorGrossQty = Number(receiveTran.quantity) / splitRatio;
								console.log('SPLIT grossQty = ' + priorGrossQty);
							}
							let sourced = handlePerfectFit(receiveTran, priorGrossQty, Number(d), isSwapOrSplit);
							if (sourced) {
								totalReceived += Number(receiveTran.quantity);
								sourcedQty += Number(sourced);
								console.log('TOTAL received = ' + totalReceived, ' SOURCED qty = ' + sourcedQty);
								receiveTran.tranche = c + '-' + d;
								foundGroupFit = true;
							}
						});
				}
				if (foundGroupFit) {
					// remove all of the associated send transactions
					let sendTransactions = giftTransactions.filter((g) => sameClassAndSender(g) && sameDate(g));
					let sendSum = sendTransactions.reduce((a, b) => a + Number(b.quantity), 0);
					console.log('PRIOR GrossQty = ' + priorGrossQty, ' SEND Sum = ' + sendSum);
					sendTransactions.forEach((s) => (s.tranche = c + '-' + d));
					addPriorTransfers(sendTransactions);
					console.log('SEND Transactions: ', sendTransactions);
					giftTransactions = giftTransactions.filter((g) => !sameClassAndSender(g) || !sameDate(g));
				}
				return foundGroupFit ? true : false;
			}

			// Utility functions
			function toIssuer(trans) {
				return (
					trans.receiver == client ||
					String(trans.receiver || '').toLowerCase() == 'issuer' ||
					String(trans.receiverName || '').toLowerCase() == 'issuer' ||
					itsTreasury(trans.receiver, trans.receiverName)
				);
			}
			function fromIssuer(trans) {
				return (
					trans.sender == client ||
					String(trans.sender || '').toLowerCase() == 'issuer' ||
					String(trans.senderName || '').toLowerCase() == 'issuer' ||
					itsTreasury(trans.sender, trans.senderName)
				);
			}
			function thisDateOrEarlier(trans) {
				return Number(trans.date) <= Number(d);
			}
			function earlierDate(trans) {
				return Number(trans.date) < Number(d);
			}
			function sameDate(trans) {
				return Number(trans.date) == Number(d);
			}
			function sameQty(trans, qty) {
				return Number(trans.quantity) == qty;
			}

			function isReleaseLike(trans) {
				const s = trans.notes;
				if (!s) return false;
				const t = s.toLowerCase();
				// specific phrase first
				if (/release\s+of\s+unvested\s+shares\s+granted/.test(t)) return true;
				// broader release signal
				if (t.includes('release') && (t.includes('unvested') || t.includes('restricted'))) return true;
				return false;
			}

			// Check historicHoldings for negative quantities
			historicHoldings.forEach((h, i) => {
				if (Number(h.quantity) < 0) alert(`Negative quantity in historicHoldings[${i}]:`, h);
			});

			// Check giftTransactions for negative quantities
			giftTransactions.forEach((g, i) => {
				if (Number(g.quantity) < 0) alert(`Negative quantity in giftTransactions[${i}]:`, g);
			});
		});

		function allocateCarryOver() {
			let remaining = -carryOverDeduct; // Make positive for internal logic
			while (remaining > 0.0001) {
				// Step 1: get valid candidates with quantity > 0
				const candidates = giftTransactions.filter((gt) => {
					const qty = Number(gt.quantity);
					return !isNaN(qty) && qty > 0 && eligibleAsOf(gt, gt.date || 0);
				});

				// Step 2: pick tranche already being reduced
				let target = candidates.find((gt) => Number(gt.before) > Number(gt.quantity));

				// Step 3: otherwise pick by lowest basis + oldest date
				if (!target) {
					candidates.sort((a, b) => {
						const basisA = isKnownNumber(a._effectiveTaxBasis) ? Number(a._effectiveTaxBasis) : Infinity;
						const basisB = isKnownNumber(b._effectiveTaxBasis) ? Number(b._effectiveTaxBasis) : Infinity;
						if (basisA !== basisB) return basisA - basisB;
						return Number(a.date || 0) - Number(b.date || 0);
					});
					target = candidates[0];
				}

				if (!target) break; // Nothing valid left

				// Initialize .before if not already set
				if (target.before == null) target.before = Number(target.quantity);

				const qty = Number(target.quantity);
				const deductNow = Math.min(remaining, qty);

				target.quantity = (qty - deductNow).toFixed(6).replace(/\.?0+$/, '');
				remaining -= deductNow;
			}
		}

		// Must account for any remaining carryOverDeduct
		if (carryOverDeduct) allocateCarryOver();

		// For Edmund Optics, never include SARS, RSGs, or Awards
		if (isEDM) giftTransactions = giftTransactions.filter((g) => notRestricted(g));

		function carryForwardBasisFields(targetTrans, sourceTrans) {
			if (!sourceTrans) return;
			// #6144: tag when a value here didn't come from the transaction's own record -- feeds the 'F' Key.
			let carried = false;
			if (!isKnownNumber(targetTrans.sharePrice) && isKnownNumber(sourceTrans.sharePrice)) {
				targetTrans.sharePrice = sourceTrans.sharePrice;
				carried = true;
			}
			if (!isKnownNumber(targetTrans.taxBasis)) {
				if (isKnownNumber(sourceTrans.taxBasis)) {
					targetTrans.taxBasis = sourceTrans.taxBasis;
					carried = true;
				} else if (isKnownNumber(sourceTrans._effectiveTaxBasis)) {
					targetTrans.taxBasis = Number(sourceTrans._effectiveTaxBasis).toString();
					carried = true;
				}
			}
			if (carried) targetTrans._basisCarried = true;
			let basis = resolveEffectiveTaxBasis(targetTrans, clientData);
			targetTrans._effectiveTaxBasis = basis.value;
			targetTrans._basisSource = basis.source;
			console.log('ADJUSTED Transaction: ', targetTrans);
		}

		// #6144g: a same-day Reissuance is disqualified as a candidate when it's
		// matched by a same-day Cancellation of the SAME quantity -- that's the
		// signature of a cancel/reissue re-cert pair (quantitiesMatch, not a bare
		// date check), and the true source is always whatever cert existed BEFORE
		// that pair, never the reissuance itself -- so excluding it never risks
		// losing the real source, only a same-day proxy for it.
		function isSameDayReissuance(candidate, date) {
			if (Number(candidate.date) !== Number(date)) return false;
			if (!/re-?issu/i.test(candidate.description || '')) return false;
			return giftTransactions.some(
				(g) =>
					g.nrid !== candidate.nrid &&
					g.sender === giftUserId &&
					g.shareClass === candidate.shareClass &&
					Number(g.date) === Number(date) &&
					/cancel/i.test(g.description || '') &&
					quantitiesMatch(g.quantity, candidate.quantity)
			);
		}

		// This method looks for one or more prior transactions specified precisely in the send notes
		function handleNotesMatch(trans, targetQty, date, contestedCerts) {
			let priorTrans = giftTransactions.filter((g) => Number(g.date) <= date && sameClassAndReceiver(g));
			priorTrans = priorTrans.filter((p) => eligibleAsOf(p, date));
			priorTrans = priorTrans.filter((p) => !isSameDayReissuance(p, date));

			// Consume explicitly named lots (cert or dated book entry); commits only if they cover the send.
			function consumeExplicitLots(lotMatches) {
				let remainingTarget = Number(targetQty) || 0;
				const stagedItems = [];
				const stagedUpdates = [];

				lotMatches.forEach((match) => {
					if (remainingTarget <= 0) return;

					const priorGrossQty = Number(match.quantity) || 0;
					const consumeQty = Math.min(remainingTarget, priorGrossQty);

					stagedItems.push({
						...match,
						tranche: c + '-' + trans.date,
						quantity: consumeQty.toString(),
					});

					stagedUpdates.push({
						nrid: match.nrid,
						remaining: priorGrossQty - consumeQty,
						grossQty: priorGrossQty,
					});

					remainingTarget -= consumeQty;
				});

				// IMPORTANT: commit only if the explicit cert list fully satisfies the send
				if (remainingTarget <= 0) {
					stagedItems.forEach((p) => tagMatch(p, 'A'));
					historicHoldings = historicHoldings.concat(stagedItems);

					stagedUpdates.forEach((u) => {
						// Epsilon-tolerant, not <= 0 -- float noise can leave remaining ~1e-16.
						if (u.remaining <= balanceTolerance(u.grossQty)) {
							giftTransactions = giftTransactions.filter((g) => g.nrid != u.nrid);
						} else {
							const idx = giftTransactions.findIndex((g) => g.nrid == u.nrid);
							if (idx >= 0) giftTransactions[idx].quantity = u.remaining.toString();
						}
					});

					let thisTransaction = giftTransactions.find((g) => g.nrid == trans.nrid);
					if (thisTransaction) carryForwardBasisFields(thisTransaction, lotMatches[0]);
					return true;
				}

				return false;
			}

			// NEW: explicit source-cert handling
			const explicitSourceCerts = CERT_COLUMN ? getExplicitSourceCertSet(trans.notes) : null;
			if (explicitSourceCerts) {
				let certMatches = priorTrans.filter((p) => {
					const lotCert = getLotCertToken(p.notes);
					if (!lotCert || !explicitSourceCerts.has(lotCert)) return false;
					// #6144n: a sibling same-day Receive already claims this cert by its own notes.
					if (contestedCerts && contestedCerts.has(lotCert)) return false;
					return true;
				});
				if (certMatches.length) {
					certMatches.sort((a, b) => Number(a.date) - Number(b.date));
					// If cert-linked lots are insufficient, do NOTHING here and let later logic handle it
					return consumeExplicitLots(certMatches);
				}
			}

			// Book-entry lots have no cert; "Book Entry dated M/D/YY" names the lot by its date.
			const bookEntryDate = CERT_COLUMN ? getBookEntrySourceDate(trans.notes, trans.date) : null;
			if (bookEntryDate) {
				const beMatches = priorTrans.filter(
					(p) =>
						isBookEntryLot(p) &&
						(moment.unix(Number(p.date)).utc().format('YYYY-MM-DD') === bookEntryDate || getBookEntrySourceDate(p.notes, p.date) === bookEntryDate)
				);
				if (beMatches.length) {
					const exact = (p) => (quantitiesMatch(p.quantity, targetQty) ? 0 : 1);
					beMatches.sort((a, b) => exact(a) - exact(b) || Number(a.date) - Number(b.date));
					if (consumeExplicitLots(beMatches)) return true;
				}
			}

			// EXISTING behavior for simpler notes
			let notesMatch = findNotesMatch(trans.notes, priorTrans);
			if (notesMatch) {
				console.log('NOTES MATCH: ', notesMatch);

				const priorGrossQty = Number(notesMatch.quantity) || 0;
				const consumeQty = Math.min(Number(targetQty) || 0, priorGrossQty);

				// Clone BEFORE pushing so we don't mutate the pushed reference
				const trancheItem = {
					...notesMatch,
					tranche: c + '-' + trans.date,
					quantity: consumeQty.toString(),
				};
				// Immutable List -> reassign
				// #6144f: only one eligible candidate -> 'I' (O.P.), not 'B' -- no note needed to be sure.
				tagMatch(trancheItem, priorTrans.length == 1 ? 'I' : 'B');
				historicHoldings.push(trancheItem);

				// Update/remove the ORIGINAL in giftTransactions
				const remaining = priorGrossQty - consumeQty;
				// #6144-follow-up: same epsilon-tolerant check as above -- see comment there.
				if (remaining <= balanceTolerance(priorGrossQty)) {
					giftTransactions = giftTransactions.filter((g) => g.nrid != notesMatch.nrid);
				} else {
					const idx = giftTransactions.findIndex((g) => g.nrid == notesMatch.nrid);
					if (idx >= 0) giftTransactions[idx].quantity = remaining.toString();
				}

				// Carry price to the current transaction
				let thisTransaction = giftTransactions.find((g) => g.nrid == trans.nrid);
				if (thisTransaction) carryForwardBasisFields(thisTransaction, notesMatch);
				return true;
			}

			console.log('no notes match');
			return false;
		}

		function getExplicitSourceCertSet(notesText) {
			const refs = parseCertificateRefs(notesText);
			return refs.sourceCerts?.length ? new Set(refs.sourceCerts) : null;
		}

		function getLotCertToken(notesText) {
			const refs = parseCertificateRefs(notesText);

			// For old lots like "Issuance (Cert 0002475)", use the cert on the lot itself
			if (refs.newCert) return refs.newCert;

			// If somehow the lot only parses as a single source cert, allow that too
			if (refs.sourceCerts?.length == 1) return refs.sourceCerts[0];

			return '';
		}

		// "Book Entry dated 6/27/08" -> '2008-06-27'. Two-digit years can't land after the send itself.
		function getBookEntrySourceDate(notesText, asOf) {
			const m = String(notesText || '').match(/book\s*entry\s+dated\s+(\d{1,2}\/\d{1,2}\/\d{2,4})\b/i);
			if (!m) return null;
			let dt = moment.utc(m[1], ['M/D/YYYY', 'M/D/YY'], true);
			if (!dt.isValid()) return null;
			if (asOf && dt.unix() > Number(asOf)) dt = dt.subtract(100, 'years');
			return dt.format('YYYY-MM-DD');
		}

		function isBookEntryLot(p) {
			return /book\s*entry/i.test(p?.notes || '') && !getLotCertToken(p.notes);
		}

		function findNotesMatch(transNotes, priorTrans) {
			console.log('transNotes', transNotes);

			const tn = String(transNotes || '').trim();
			const tnLower = tn.toLowerCase();

			// Hard gate: only match notes that explicitly say "... from ...", to avoid
			// accidental substring matches on busy accounts.
			const hasFromPhrase = /\bfrom\b\s*[:\-]?\s+\S+/i.test(tn);
			if (!hasFromPhrase) {
				console.log("findNotesMatch: skipping (no '... from ...' phrase)");
				return false;
			}

			function extractPossibleDate(str) {
				if (!str) return null;
				const dateRegex = /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s*\d{1,2},?\s*\d{2,4}|\b\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/i;
				const match = str.match(dateRegex);
				return match ? match[0] : null;
			}

			const possibleDateString = extractPossibleDate(tn);
			const transMoment = possibleDateString ? moment(possibleDateString, dateFormats, true) : null;

			for (const t of priorTrans) {
				if (!t || !t.notes || !t.date) continue;

				const priorNotes = String(t.notes || '').trim();
				if (!priorNotes) continue;

				// Keep your original "notes contain prior notes" behavior
				const notesHit = tnLower.includes(priorNotes.toLowerCase());
				if (!notesHit) continue;

				let dateMatch = true; // allow notes-only when no parseable date
				if (transMoment && moment.unix(Number(t.date)).utc().isValid()) {
					const tMoment = moment.unix(Number(t.date)).utc();
					dateMatch = tMoment.isSame(transMoment, 'day');
				}

				if (dateMatch) {
					console.log('Matched prior transaction:', t);
					return t; // <- returns a single OBJECT
				}
			}

			return false;
		}

		// Character overlap similarity (basic fuzzy match)
		function areSimilar(a, b) {
			const setA = new Set(a.toLowerCase());
			const setB = new Set(b.toLowerCase());
			const overlap = [...setA].filter((c) => setB.has(c)).length;
			const avgLen = (setA.size + setB.size) / 2;
			return overlap / avgLen;
		}

		// true if this prior lot is eligible to be consumed as-of "asOf"
		function eligibleAsOf(priorLot, asOf) {
			const u = priorLot?._useAfter;
			if (!u) return true;
			if (u === 'LOCK') return false;

			const asOfSec = Number(asOf);
			const useAfterSec = Number(u);

			if (!Number.isFinite(asOfSec) || !Number.isFinite(useAfterSec)) return false;

			return asOfSec >= useAfterSec;
		}

		function sameCertificate(transNotes, notes) {
			if (!CERT_COLUMN) return areSimilar(transNotes, notes);

			// PRIOR EFFORTS
			// console.log("transNotes", (transNotes.match(/\(([^)]*?(\d+)[^)]*?)\)/) || [])[2])
			// console.log("notes", (notes.match(/\(([^)]*?(\d+)[^)]*?)\)/) || [])[2])
			// return (transNotes.match(/\((\d+)\)/) || [])[1] == (notes.match(/\((\d+)\)/) || [])[1];
			// if ((transNotes.match(/\(([^)]*?(\d+)[^)]*?)\)/) || [])[2] == (notes.match(/\(([^)]*?(\d+)[^)]*?)\)/) || [])[2]) {
			// 	console.log("***MATCH***");
			// 	return true;
			// } else return false;

			// Extract a cert token from the first (...) group; must contain a digit
			// (so "(Cancelled)" etc. don't match).
			const a = parseCertificateRefs(transNotes);
			const b = parseCertificateRefs(notes);

			// Best case: overlap of source cert lists
			if (a.sourceCerts.length || b.sourceCerts.length) {
				const aSet = new Set(a.sourceCerts.length ? a.sourceCerts : a.newCert ? [a.newCert] : []);
				const bSet = new Set(b.sourceCerts.length ? b.sourceCerts : b.newCert ? [b.newCert] : []);

				for (const cert of aSet) {
					if (bSet.has(cert)) {
						console.log('***MATCH via source overlap***');
						return true;
					}
				}
				return false;
			}

			// Simple fallback: compare single certs
			if (a.newCert && b.newCert && a.newCert == b.newCert) {
				console.log('***MATCH via single cert***');
				return true;
			}

			return false;
		}

		// This method takes a given Send and tries to find a collection of Prior Receives that fits perfectly
		// #6144g: prefer an exact single-cert match before falling back to findSubset's
		// notes-blind cross-cert search below, which can settle for a close-but-wrong
		// multi-cert split when a single cert's own holdings already sum exactly.
		function findSingleCertExactMatch(targetQty, candidates) {
			const groups = {};
			candidates.forEach((c) => {
				const token = getLotCertToken(c.notes);
				if (!token) return;
				(groups[token] = groups[token] || []).push(c);
			});
			for (const token in groups) {
				const group = groups[token];
				const sum = group.reduce((a, b) => a + Number(b.quantity), 0);
				if (Math.abs(sum - targetQty) < 0.000001) return group;
			}
			return null;
		}

		// #6144k: prefer candidates the SEND's own notes explicitly name as source certs
		// (e.g. "Cancellation (10484 from 10484)") over blind quantity-only matching --
		// clients with PREFER_CERT_LINEAGE off (e.g. Asplundh) only reach cert info here.
		//
		// #6144m: a legacy cert can legitimately fund more than one new cert (e.g. a
		// tax-basis-driven reissuance that splits one surrendered cert into several stub
		// lots, same real cert # entered as separate rows with their own quantities). The
		// full-group sum below only covers one-cert-to-one-reissuance; findSubsetSum below
		// lets a subset of same-cert rows satisfy the target instead of requiring the whole
		// named group to sum exactly.
		function findExplicitCertMatch(transNotes, targetQty, candidates) {
			const explicit = candidates.filter((c) => sameCertificate(transNotes, c.notes));
			if (!explicit.length) {
				return null;
			}
			const sum = explicit.reduce((a, b) => a + Number(b.quantity), 0);
			if (Math.abs(sum - targetQty) < 0.000001) {
				return explicit;
			}
			if (explicit.length <= 1) {
				return null;
			}
			const subsetResult = findSubsetSum(explicit, targetQty);
			return subsetResult;
		}

		// #6144o: a single, UNAMBIGUOUSLY-named source cert holding more than the target --
		// a partial draw from a larger historical lot. findExplicitCertMatch above only
		// returns an exact-sum group, so it can't represent "take part of this one row,
		// leave the rest" -- handlePerfectFit handles that case using this instead.
		// Deliberately stricter than findExplicitCertMatch's sameCertificate() (source-set
		// overlap, which can match on any one of several named certs): this only fires when
		// transNotes names EXACTLY ONE source cert and EXACTLY ONE candidate carries that
		// exact token, so it can't misfire on a multi-cert citation where a sibling cert was
		// already claimed elsewhere and just happens to leave a single unrelated candidate.
		function findExplicitCertPartial(transNotes, targetQty, candidates) {
			const soleCerts = getExplicitSourceCertSet(transNotes);
			if (!soleCerts || soleCerts.size !== 1) return null;
			const [cert] = soleCerts;
			const matches = candidates.filter((c) => getLotCertToken(c.notes) === cert);
			if (matches.length !== 1) return null;
			const avail = Number(matches[0].quantity) || 0;
			if (avail <= Number(targetQty) + 0.000001) return null;
			return matches[0];
		}

		// Bounded brute-force subset-sum -- a same-cert group is always a handful of
		// historicals rows, never large, so this stays cheap and auditable. Returns the
		// first exact-sum subset found; if more than one subset fits, the choice is
		// arbitrary but deterministic (candidate order), same as any other tie.
		function findSubsetSum(items, targetQty) {
			if (items.length > 24) {
				return null;
			}
			const n = items.length;
			for (let mask = 1; mask < 1 << n; mask++) {
				let sum = 0;
				for (let i = 0; i < n; i++) if (mask & (1 << i)) sum += Number(items[i].quantity);
				if (Math.abs(sum - targetQty) < 0.000001) return items.filter((_, i) => mask & (1 << i));
			}
			return null;
		}

		function handlePerfectFit(trans, targetQty, date, isSwapOrSplit, explicitOnly) {
			// Look for perfect match
			let priorGrossQty = 0;
			let priorGrossCost = 0;
			// find and remove the prior receive transaction(s) that sum to targetQty
			let priorTrans = giftTransactions.filter((g) => Number(g.date) <= date && sameClassAndReceiver(g));
			priorTrans = priorTrans.filter((p) => eligibleAsOf(p, date));
			// Remove active certificates
			priorTrans = priorTrans.filter((p) => !activeCertificate(p));
			priorTrans = priorTrans.filter((p) => !isSameDayReissuance(p, date));
			// Remove restricted stock
			if (isEDM) priorTrans = priorTrans.filter((p) => !p.notes.startsWith('Restricted'));
			// Sort according to priority
			if (isVERM)
				priorTrans.sort((a, b) => (Number(a.date) > Number(b.date) ? 1 : -1)); // oldest to newest
			else {
				priorTrans.sort((a, b) => (Number(a.date) > Number(b.date) ? -1 : 1)); // newest to oldest
				priorTrans.sort((a, b) => Number(b.quantity) - Number(a.quantity)); // largest to smallest
			}
			console.log('PRIOR Transactions: ', priorTrans);
			let olderPriorTrans = priorTrans.filter((g) => Number(g.date) < date);
			// #6144o: a single named cert holding more than this target needs -- handle the
			// partial draw directly, before the exact-sum-only chain below gets a chance to
			// (wrongly) report no fit just because the lot is bigger than what's needed.
			let partialCert = findExplicitCertPartial(trans.notes, targetQty, olderPriorTrans) || findExplicitCertPartial(trans.notes, targetQty, priorTrans);
			let perfectFit;
			if (partialCert) {
				const consumeQty = Number(targetQty) || 0;
				const avail = Number(partialCert.quantity) || 0;
				const remaining = avail - consumeQty;
				const consumed = { ...partialCert, tranche: c + '-' + trans.date, quantity: consumeQty.toString() };
				tagMatch(consumed, priorTrans.length == 1 ? 'I' : 'C', trans);
				console.log('PARTIAL Fit: ', consumed);
				historicHoldings = historicHoldings.concat([consumed]);
				if (remaining <= balanceTolerance(avail)) {
					giftTransactions = giftTransactions.filter((g) => g.nrid != partialCert.nrid);
				} else {
					const idx = giftTransactions.findIndex((g) => g.nrid == partialCert.nrid);
					if (idx >= 0) giftTransactions[idx].quantity = remaining.toString();
				}
				priorGrossQty = consumeQty;
				priorGrossCost = consumeQty * toNumber(partialCert.sharePrice);
				perfectFit = [consumed];
			} else {
				// first try with only older transactions
				minDiff = 999; // reset arbitrarily large starting number used in tranche analysis
				findSubset.callCount = 0;
				perfectFit =
					findExplicitCertMatch(trans.notes, targetQty, olderPriorTrans) ||
					(explicitOnly ? null : findSingleCertExactMatch(targetQty, olderPriorTrans) || findSubset(targetQty, olderPriorTrans));
				// now include same-date transactions
				minDiff = 999; // reset arbitrarily large starting number used in tranche analysis
				findSubset.callCount = 0;
				if (!perfectFit)
					perfectFit =
						findExplicitCertMatch(trans.notes, targetQty, priorTrans) ||
						(explicitOnly ? null : findSingleCertExactMatch(targetQty, priorTrans) || findSubset(targetQty, priorTrans));
				if (perfectFit) {
					console.log('PERFECT Fit: ', perfectFit);
					perfectFit.forEach((p) => (p.tranche = c + '-' + trans.date));
					perfectFit.forEach((p) => tagMatch(p, perfectFit.length > 1 ? 'D' : priorTrans.length == 1 ? 'I' : 'C', trans));
					historicHoldings = historicHoldings.concat(perfectFit);
					let perfectNrids = new Set(perfectFit.map((item) => item.nrid));
					giftTransactions = giftTransactions.filter((g) => !perfectNrids.has(g.nrid));
					priorGrossQty = perfectFit.reduce((acc, val) => acc + Number(val.quantity), 0);
					priorGrossCost = perfectFit.reduce((acc, val) => acc + Number(val.quantity) * toNumber(val.sharePrice), 0);
				} else console.log('no further fit');
			}

			let thisTransaction = giftTransactions.find((g) => g.nrid == trans.nrid);
			if (thisTransaction) thisTransaction.sharePrice = (priorGrossCost / (isSwapOrSplit ? Number(thisTransaction.quantity) : priorGrossQty)).toString();
			console.log('ADJUSTED Transaction: ', thisTransaction);
			// #6144r: report qty sourced, not just bool, for group-target tracking below.
			return perfectFit ? priorGrossQty : false;
		}

		// This subroutine finds a subset of Receives that sum exactly to a target quantity
		function findSubset(targetQty, priorTrans, subset = [], index = 0, currentSum = 0) {
			if (!findSubset.callCount) findSubset.callCount = 0;
			findSubset.callCount++;
			if (findSubset.callCount > 500000) return null; // hard stop

			// tolerate tiny decimal noise
			if (Math.abs(currentSum - targetQty) < 0.000001) return subset;

			let diff = Math.abs(targetQty - currentSum);
			if (diff < minDiff) {
				minDiff = diff;
				diffCount = 0;
				console.log('minDiff = ' + minDiff);
			}
			diffCount++;

			if (diff < 1 && diffCount > 99) return subset;

			if (currentSum > targetQty || index >= priorTrans.length) return null;

			let qty = Number(priorTrans[index].quantity) || 0;

			let includeCurrent = findSubset(targetQty, priorTrans, [...subset, priorTrans[index]], index + 1, currentSum + qty);
			if (includeCurrent) return includeCurrent;

			return findSubset(targetQty, priorTrans, subset, index + 1, currentSum);
		}

		// This method scores potential matches, and returns the one with the highest score
		function scoreMatches(transfer, historics) {
			let bestMatch;
			let bestScore = 0;
			if (historics.length) bestMatch = historics[0];
			historics.forEach((h) => {
				let score = areSimilar(transfer.notes, h.notes);
				// High-priority sender-declared-basis signal (2026-08-28, per Chris,
				// GT improvements #6144) -- outweighs the exact-quantity match (7)
				// below since an explicit sender assertion is more trustworthy than
				// any heuristic here.
				if (senderBasisMatchesCandidate(transfer, h)) score += 10;
				if (transfer.sharePrice == h.sharePrice) score += 3;
				if (quantitiesMatch(transfer.quantity, h.quantity)) score += 7;
				if (transfer.notes.toLowerCase().includes(h.notes.toLowerCase())) score += 5;
				// $0 transfers (gifts/distributions) have no real sale price to compare against --
				// this guard only makes sense for an actual paid transfer.
				if (Number(transfer.sharePrice) > 0 && Number(transfer.sharePrice) < Number(h.sharePrice)) score = 0;
				if (Number(h.quantity) > Number(transfer.quantity)) score += 1;
				if (Number(h.quantity) < Number(transfer.quantity)) score = 0;
				if (Number(transfer.date) < Number(h.date)) score = 0;
				// if (activeCertificate(h)) score = 0;
				if (score > bestScore) {
					bestScore = score;
					bestMatch = h;
				}
			});
			return bestMatch;
		}

		function scoreDifferences(transfer, historics) {
			let bestMatch;
			let bestScore = -Infinity; // allow 0 scores
			if (historics.length) bestMatch = historics[0];

			historics.forEach((h) => {
				let score = areSimilar(transfer.notes, h.notes);
				if (score > 0.9) score = 0; // handle EO Releases

				// Extract dates from notes
				const transferNoteDate = extractDate(transfer.notes);
				const historicNoteDate = extractDate(h.notes);

				// Strongly prefer a note-date ≈ record .date
				const dateBoost =
					(transferNoteDate && isCloseInTime(transferNoteDate, h.date) ? 1.0 : 0) +
					(historicNoteDate && isCloseInTime(historicNoteDate, transfer.date) ? 0.5 : 0);

				// Small bonus if share prices match (helps pick the Grant @ 318.03)
				const priceBoost = +transfer.sharePrice && +h.sharePrice && Math.abs(+transfer.sharePrice - +h.sharePrice) < 0.005 ? 0.5 : 0;

				// High-priority sender-declared-basis signal (2026-08-28, per Chris,
				// GT improvements #6144) -- outweighs dateBoost/priceBoost above.
				const basisBoost = senderBasisMatchesCandidate(transfer, h) ? 2.0 : 0;

				// Down-weight loose text similarity; up-weight structured signals
				const totalScore = score * 0.3 + dateBoost + priceBoost + basisBoost;

				if (totalScore > bestScore) {
					bestScore = totalScore;
					bestMatch = h;
				}
			});

			return bestMatch;

			// Extract timestamp (ms) from note string if any recognizable date found
			function extractDate(text) {
				const match = text.match(/(\d{1,4}[\/\-]\d{1,2}[\/\-]\d{1,4}|\w{3} \d{1,2}, \d{4})/);
				if (match) {
					const m = moment(match[0], dateFormats, true);
					if (m.isValid()) return m.valueOf(); // ms since epoch
				}
				return null;
			}

			// True if two dates are within ±5 days (input: ms, seconds)
			function isCloseInTime(msFromNote, dateStampSeconds) {
				const dateFromItem = Number(dateStampSeconds) * 1000;
				const delta = Math.abs(msFromNote - dateFromItem);
				return delta <= 5 * 24 * 60 * 60 * 1000; // ±5 days in ms
			}
		}

		function notRestricted(trans) {
			const note = trans.notes?.toLowerCase() || '';
			return !['restricted stock grant', 'stock award', 'appreciation rights grant'].some((term) => note.includes(term));
		}

		function activeCertificate(trans) {
			return /\(.*?#.*?\)/.test(trans.notes);
		}

		function sameClassAndSender(trans) {
			return trans.shareClass == c && trans.sender == giftUserId;
		}

		function sameClassAndReceiver(trans) {
			return trans.shareClass == c && trans.receiver == giftUserId;
		}
	});

	// dataOnly mode: filterViewGT/showDecimals DOM reads are skipped (same as the
	// giftHeadline block above); stringOptions just keeps its m0 default.
	let filterViewGT;
	stringOptions = m0;
	if (!dataOnly) {
		filterViewGT = document.getElementById('filterViewGiftTrans');
		if (document.getElementById('showDecimals').checked) stringOptions = m2;

		if (true) {
			//includedClasses.length <= 1) {
			document.getElementById('filterWrapViewGiftTrans').style.display = 'none';
		} else {
			document.getElementById('filterWrapViewGiftTrans').style.display = 'block';
			let labelViewGT = document.getElementById('labelViewGiftTrans');
			if (labelViewGT) labelViewGT.innerHTML = clientData.use_real_estate_language ? 'Property' : UFL ? 'Fund' : 'Share&nbsp;Class';

			if (isJMS || isACC) selectedClass = classes[0];
			else includedClasses.unshift('ALL');

			filterViewGT.innerHTML = `
				<option value="" disabled selected>Select</option>
				${includedClasses
					.map(
						(className, index) =>
							`
						<option ${index == 0 ? 'selected' : ''} value="${className}">${className}</option>
					`
					)
					.join('')}
				<label>Fund</label>

			`;
		}
	}

	let treasuryId;
	let giftSources;
	let lastCertNum;
	let nextCertNum;
	// certIssueLog[shareClass]: {receiver, tranche} entries in commit order, one per
	// incrementNextCertNumber() call -- lets recipient removal check whether the
	// certs it consumed were the most recent ones issued (safe to roll back) or not.
	let certIssueLog;
	// customCertLog[shareClass]: {receiver, tranche, colKey, value} entries, in
	// commit order, for next_cert_number custom-column suggestions (Sheetz's
	// new_sender_cert/new_receiver_cert) -- a separate log from certIssueLog
	// since this system doesn't touch nextCertNum/CERT_COLUMN at all. Fed back
	// into getNextCertNumber (via classTransactions) so sequential recipients in
	// one sitting get sequential suggestions instead of the same one repeated.
	let customCertLog = {};
	let recipientId;
	let recipientName;
	let currentTranche;
	let originalLastCertNum;

	let holders = [];
	let gifts = [' '];
	let theseGifts = {};
	const NEW_CODE = true;
	let trancheAutoTimers = {};
	theseGifts[' '] = ['', '', ,];

	let recipientSearchData = [];
	let recipientSearchIndex = -1;
	let recipientSearchMatches = [];

	// Remove giftTransactions with quantity == 0;  <-- NO !!
	// giftTransactions = giftTransactions.filter(g => g.quantity != "0");

	// Dollar-denominated classes with 2+ leftover lots: restate the lots for Events so they total the cap-table
	// balance (the 1-lot case is the #6108 fallback below). Applied only when the replay reconciles exactly.
	classes.forEach((c) => {
		if (getEffectiveDenomination(clientData, c) !== 'dollars' || eventFundedClasses.has(c)) return;
		const holderClass = giftUserData?.clients?.[client]?.shares?.[c];
		const approved = Number(holderClass?.approved);
		if (!holderClass?.events?.length || !Number.isFinite(approved)) return;
		const lots = giftTransactions.filter((g) => g.shareClass == c && g.receiver == giftUserId && Number(g.quantity) > 0);
		if (lots.length < 2) return;
		const events = holderClassEvents(clientData, holderClass, c);
		const restated = replayEventsOntoLots(
			lots.map((l) => ({ day: Math.floor(Number(l.date) / 86400), qty: Number(l.quantity) })),
			events,
			approved
		);
		if (!restated) return;
		lots.forEach((l, i) => {
			l.quantity = restated[i].toString();
			l._eventRestated = true; // marks the lot for anyone inspecting the data later
		});
	});

	// #6108 (Ullico) fallback: if a class has exactly one leftover unconsumed entry
	// and its quantity disagrees with the precomputed "Shares Owned Now" balance,
	// correct that entry to match. No-op if matching fully reconciled or 2+ are left over.
	classes.forEach((c) => {
		const correctBalanceRaw = giftUserData?.clients?.[client]?.shares?.[c]?.approved;
		if (correctBalanceRaw == null) return;
		const correctBalance = Number(correctBalanceRaw);
		if (!Number.isFinite(correctBalance)) return;

		const leftover = giftTransactions.filter((g) => g.shareClass == c && g.receiver == giftUserId);
		if (leftover.length !== 1) return; // ambiguous or already empty -- leave alone

		const leftoverQty = Number(leftover[0].quantity);
		if (leftoverQty === correctBalance) return; // already reconciled, nothing to do

		console.warn(
			`giftTransfer (#6108 fallback): unreconciled tranche history for ${giftUserId}/${c} -- leftover entry shows ${leftoverQty}, precomputed balance is ${correctBalance}. Displaying the precomputed balance instead of the stale tranche quantity.`
		);
		leftover[0].quantity = correctBalance.toString();
		leftover[0]._reconciliationFallback = true; // marks this entry for anyone inspecting the data later
	});

	// In dataOnly mode, apply sorting and tranche letters, then return data
	if (dataOnly) {
		console.log('giftTransfer: dataOnly mode - sorting and assigning tranches for', shareholder);

		// Sort: non-zero quantities first, then zero quantities (same as renderUserGiftTransModal)
		let nonZero = giftTransactions.filter((t) => Number(t.quantity) != 0);
		let zeroQty = giftTransactions.filter((t) => Number(t.quantity) == 0).sort((a, b) => Number(b.date) - Number(a.date));
		giftTransactions = sortByClassThenBasisThenDateThenQty(nonZero).concat(zeroQty);
		historicHoldings = sortByClassThenBasisThenDateThenQty(historicHoldings);
		priorTransfers = sortByClassThenBasisThenDateThenQty(priorTransfers);

		// Sort historicHoldings by tranche (class-date format)
		historicHoldings.sort((a, b) => {
			if (!a.tranche || !b.tranche) return 0;
			let [classA, dateA] = a.tranche.toString().split('-');
			let [classB, dateB] = b.tranche.toString().split('-');
			dateA = Number(dateA);
			dateB = Number(dateB);
			if (dateA < dateB) return 1;
			if (dateA > dateB) return -1;
			if (classA < classB) return -1;
			if (classA > classB) return 1;
			return 0;
		});

		// Assign tranche letters (A, B, C, etc.) - same logic as renderUserGiftTransModal
		let letterCount = 0;
		giftTransactions.forEach(function (h) {
			let trancheLetter = numberToLetters(letterCount);
			if (!h.tranche || h.tranche.length > 3) {
				h.tranche = trancheLetter;
				letterCount++;
			}
			if (!h.before) h.before = h.quantity;
		});
		historicHoldings.forEach(function (h) {
			let trancheLetter = numberToLetters(letterCount);
			if (!h.tranche) {
				h.tranche = trancheLetter;
				letterCount++;
			} else if (h.tranche.length > 3) {
				let timeStamp = h.tranche;
				historicHoldings.forEach((k) => {
					if (k.tranche == timeStamp) {
						k.tranche = trancheLetter;
					}
				});
				priorTransfers.forEach((p) => {
					if (p.tranche == timeStamp) p.tranche = trancheLetter;
				});
				letterCount++;
			}
			if (!h.before) h.before = h.quantity;
		});

		console.log('giftTransfer: dataOnly mode - returning', giftTransactions.length, 'transactions');
		return {
			giftTransactions: JSON.parse(JSON.stringify(giftTransactions)),
			historicHoldings: JSON.parse(JSON.stringify(historicHoldings)),
			priorTransfers: JSON.parse(JSON.stringify(priorTransfers)),
			origTransactions: JSON.parse(JSON.stringify(origTransactions)),
		};
	}

	if (giftTransactions.length > 0 || NEW_CODE) showFilteredGiftTrans();

	function showFilteredGiftTrans() {
		console.log('origTransactions: ', origTransactions);
		console.log('giftTransactions: ', giftTransactions);
		console.log('historicHoldings: ', historicHoldings);
		console.log('priorTransfers: ', priorTransfers);
		// let filteredTransactions = isMAZ ? giftTransactions : origTransactions;
		let filteredTransactions = NEW_CODE ? giftTransactions.concat(historicHoldings).concat(priorTransfers) : origTransactions;
		filteredTransactions = filteredTransactions.filter(
			(t) => !(clientData.equity[t.shareClass] && clientData.equity[t.shareClass].hasOwnProperty('interestRate'))
		);
		if (selectedClass && selectedClass != 'ALL') {
			filteredTransactions = filteredTransactions.filter(function (item) {
				if (item.shareClass) return item.shareClass == selectedClass;
				else return true;
			});
		}
		renderUserGiftTransModal(filteredTransactions);
	}

	function renderUserGiftTransModal(filteredGiftTransactions) {
		// Put giftTransactions with quantityu == "0" at the end
		let nonZero = filteredGiftTransactions.filter((t) => Number(t.quantity) != 0);
		let zeroQty = filteredGiftTransactions.filter((t) => Number(t.quantity) == 0).sort((a, b) => Number(b.date) - Number(a.date));

		filteredGiftTransactions = sortByClassThenBasisThenDateThenQty(nonZero).concat(zeroQty);

		// Compile list of reference prices from Valuations
		let referenceVals = [];
		classes.forEach(function (c) {
			let clientEquity = clientData.equity[c];
			if (clientEquity) {
				if (clientEquity.valuations) {
					clientEquity.valuations.forEach(function (v) {
						let ref = {
							class: c,
							date: v.date,
							price: v.sharePrice,
							ref: v.referencePrice,
						};
						referenceVals.push(ref);
					});
				}
				if (clientEquity.transactions) CERT_COLUMN ||= clientEquity.transactions.some((t) => t.notes.includes('#'));
			}
		});

		// insert reference price if available
		let ttlTrans = 0;
		Object.keys(filteredGiftTransactions).forEach(function (trans, i) {
			let t = filteredGiftTransactions[trans];
			let refPrice = referenceVals.find(
				(v) => v.class == t.shareClass && Number(v.price) == Number(t.sharePrice) && Math.abs(Number(v.date) - Number(t.date)) <= 2 * 86400
			);
			if (refPrice) filteredGiftTransactions[i].referencePrice = refPrice.ref;
			ttlTrans += filteredGiftTransactions[i].receiver == shareholder ? Number(filteredGiftTransactions[i].quantity) : 0;
		});

		console.log(filteredGiftTransactions.filter((t) => !t.receiver));

		// Put giftTransactions with quantityu == "0" at the end
		nonZero = giftTransactions.filter((t) => Number(t.quantity) != 0);
		zeroQty = giftTransactions.filter((t) => Number(t.quantity) == 0).sort((a, b) => Number(b.date) - Number(a.date));

		giftTransactions = sortByClassThenBasisThenDateThenQty(nonZero).concat(zeroQty);
		historicHoldings = sortByClassThenBasisThenDateThenQty(historicHoldings);
		priorTransfers = sortByClassThenBasisThenDateThenQty(priorTransfers);
		historicHoldings.sort((a, b) => {
			let [classA, dateA] = a.tranche.split('-');
			let [classB, dateB] = b.tranche.split('-');
			dateA = Number(dateA);
			dateB = Number(dateB);
			if (dateA < dateB) return 1;
			if (dateA > dateB) return -1;
			if (classA < classB) return -1;
			if (classA > classB) return 1;
			return 0;
		});
		historicHoldings.sort((a, b) => {
			a.tranche > b.tranche ? -1 : 1;
		});
		console.log('POST giftTransactions: ', giftTransactions);
		console.log('POST historicHoldings: ', historicHoldings);
		console.log('POST priorTransfers: ', priorTransfers);

		let letterCount = 0;
		giftTransactions.forEach(function (h, i) {
			let trancheLetter = numberToLetters(letterCount);
			if (!h.tranche || h.tranche.length > 3) {
				h.tranche = trancheLetter;
				letterCount++;
			}
			if (!h.before) h.before = h.quantity;
		});
		historicHoldings.forEach(function (h, i) {
			let trancheLetter = numberToLetters(letterCount);
			if (!h.tranche) {
				h.tranche = trancheLetter;
				letterCount++;
			} else if (h.tranche.length > 3) {
				let timeStamp = h.tranche;
				historicHoldings.forEach((k) => {
					if (k.tranche == timeStamp) {
						k.tranche = trancheLetter;
					}
				});
				priorTransfers.forEach((p) => {
					if (p.tranche == timeStamp) p.tranche = trancheLetter;
				});
				letterCount++;
			}
			if (!h.before) h.before = h.quantity;
		});

		// If there remain unmatched prior transfers, start assigning them to tranches with the highest tax basis
		priorTransfers.forEach(function (p, i) {
			if (!p.tranche && nthAdmin) alert('Unmatched Prior Transfer: ' + JSON.stringify(p));
		});

		let ttlPrior = 0;
		let ttlHist = 0;
		priorTransfers.forEach((p) => (ttlPrior += Number(p.quantity)));
		historicHoldings.forEach((h) => (ttlHist += Number(h.quantity)));

		priorTransfers = priorTransfers.sort((a, b) => (a.tranche < b.tranche ? -1 : 1));
		stringOptions = isTBC ? m6 : m5;
		if (CERT_COLUMN) {
			// insert certificate number if available
			filteredGiftTransactions.forEach(function (f, index) {
				let certNumber = '';
				if (f.notes) {
					certNumber = getCertNumber(f.notes);
				}
				if (certNumber) {
					filteredGiftTransactions[index].certNumber = certNumber;
					let certSort = certNumber.split('-')[1];
					filteredGiftTransactions[index].certSort = Number((certSort ? certSort : certNumber).replace(/[^\d.]/g, ''));
				} else {
					filteredGiftTransactions[index].certNumber = '';
					filteredGiftTransactions[index].certSort = 0;
				}
			});
			historicHoldings.sort((a, b) => (a.certSort < b.certSort ? -1 : 1));
		}

		// Restore original sharePrice after temporary adjustments used during matching
		// giftTransactions.forEach((t) => {
		// 	let origTrans = origTransactions.find((o) => o.nrid == t.nrid);
		// 	if (origTrans) t.sharePrice = origTrans.sharePrice;
		// });

		// NEW VERSION
		// Restore original pricing / basis fields after temporary matching adjustments
		// giftTransactions.forEach((t) => {
		// 	let origTrans = origTransactions.find((o) => o.nrid == t.nrid);
		// 	if (!origTrans) return;
		// 	t.sharePrice = origTrans.sharePrice != null ? origTrans.sharePrice : '';
		// 	t.taxBasis = origTrans.taxBasis != null ? origTrans.taxBasis : '';
		// 	let basis = resolveEffectiveTaxBasis(t, clientData);
		// 	t._effectiveTaxBasis = basis.value;
		// 	t._basisSource = basis.source;
		// });

		const labelTaxBasis = language?.label_tax_basis || 'Tax Basis';

		// #6144a: kept in the same order resolveEffectiveTaxBasis() resolves in, now that
		// referencePrice/valuationSharePrice can no longer be returned (see that function) --
		// this tooltip only ever needs to explain a source that can actually occur.
		function friendlyBasisTooltip(source) {
			switch (source) {
				case 'taxBasis':
					return 'Recorded Tax Basis';
				case 'sharePrice':
					return 'From Share Price';
				case 'senderBasis':
					return 'Sender-Declared Basis';
				case 'explicitNA':
					return 'Marked N/A';
				default:
					return '';
			}
		}

		function basisColorClass(source) {
			switch (source) {
				case 'taxBasis':
					return ''; // default -- a real recorded number, nothing derived
				case 'sharePrice':
					return 'text-secondary'; // price actually paid
				case 'senderBasis':
					return 'text-primary'; // sender's own claim -- only used when no recorded Tax Basis or Share Price exists
				default:
					return 'text-secondary'; // explicitNA / unresolved
			}
		}

		// #6144a: superscript Key badge for a Historic Holdings/Prior Transfers line, colored by
		// confidence tier (see MATCH_KEY_TIER_CLASS) -- legend below always lists all Keys.
		function matchKeyBadge(key) {
			if (!key || !MATCH_KEY_NAMES[key]) return '';
			const tierClass = MATCH_KEY_TIER_CLASS[key] || 'text-blue-400';
			// Tooltip is just the abbreviation spelled out -- the full explanation lives in the legend below.
			return `<sup class="${tierClass}" style="cursor: help;" title="${MATCH_KEY_LABELS[key]} = ${MATCH_KEY_NAMES[key]}">${MATCH_KEY_LABELS[key]}</sup>`;
		}
		// Always show every Key, per Chris (2026-09-03): this is meant to educate admins on how
		// the matching engine reasons, not just report what happened to show up on this holder.
		// #6144a: unified with the Basis & Price Source key (previously only a per-cell hover
		// tooltip via friendlyBasisTooltip/basisColorClass) into one legend, per Chris's request.
		const keysLegendHtml =
			`<div class="text-xs font-bold mb-0">Match Evidence</div>` +
			Object.keys(MATCH_KEY_LABELS)
				.sort((a, b) => MATCH_KEY_LABELS[a].localeCompare(MATCH_KEY_LABELS[b]))
				.map(
					(k) =>
						`<div class="text-xs text-secondary mb-0"><b class="${MATCH_KEY_TIER_CLASS[k] || 'text-blue-400'}">${MATCH_KEY_LABELS[k]}</b> -- ${MATCH_KEY_NAMES[k]}: ${MATCH_KEY_DESCS[k]}</div>`
				)
				.join('') +
			`<div class="text-xs font-bold mb-0 mt-2">Basis &amp; Price Source</div>` +
			// #6144a: matches resolveEffectiveTaxBasis()'s order -- referencePrice/valuationSharePrice
			// dropped, since that function can no longer return either (see its own comment).
			['taxBasis', 'sharePrice', 'senderBasis']
				.map(
					(source) =>
						`<div class="text-xs text-secondary mb-0"><b class="${basisColorClass(source)}">&#9679;</b> ${friendlyBasisTooltip(source)}</div>`
				)
				.join('');

		// #6144a: this is the one Tax Basis tooltip that also has to explain HOW the
		// shown value was picked (resolveEffectiveTaxBasis) -- the per-cell tooltip below
		// names the actual source for that row; this one names the order it's tried in.
		const taxBasisHeaderTooltip =
			"Also called Cost Basis: the value used for tax-reporting purposes.\nMay differ from Share Price/Price Paid. For gifts and transfers,\nTax Basis often carries over from the prior owner.\n\nShown here in this order: this transaction's own Tax Basis (n/a included) --\nthen Share Price actually paid -- then the sender's declared basis -- else n/a.\nNever derived from a current or reference valuation.";
		const gainLossTooltip =
			"Estimated gain/loss if sold today: (Current Value - Tax Basis) x Quantity.\nCurrent Value = latest available valuation. A red ST flag means < a year old.\nNo flag shown if > a year. Gift recipients generally inherit the sender's holding period.\nInternal estimate for admin review only, not tax advice.";

		let userTransactionsMarkup = `
			<div class="table-wrap card p-1 mt-4 mb-0 pb-1">
				<div class="sm-hz-scroll-no-margin">
					${
						!giftTransactions.length
							? `
						<div class="bg-grey-lightest rounded border-grey grey-text center-align p-1 mt-4">
							No available holdings to send. To create a new holding, click <a id="newHoldingButton" class="link-accent">here</a>.<br><span class="text-sm">Gift Transfers require at least one "open" (unmatched) Transaction with ${safeHtml(giftUserData.user_name)} as the receiver.</span>
						</div>
					`
							: `
					<div id="showHideGainsDiv" class="mb-0 mr-6 right-align">
						<span id="showHideGains" class="text-xs link-accent">
							${showGainsColumns ? 'Hide Gain/Loss' : 'Show Gain/Loss'}
						</span>
					</div>
					<table id="historicalsSortTable" class="highlight table-content">
						<thead>
							<tr style="height: 30px;">
								<th class="text-sm center-align p-0" style="width:8%" data-sort-default>Tranche</th>
								${CLASS_COLUMN ? `<th class="text-sm center-align p-0" style="width:8%">Class</th>` : ``}
								${CERT_COLUMN ? `<th class="text-sm center-align p-0" style="width:8%">Cert #</th>` : ``}
								<th class="text-sm center-align p-0" style="width:15%">Date</th>
								<th class="text-sm p-0">Description</th>
								<!-- <th>Sender</th> -->
								<!-- <th>Receiver</th> -->
								<th class="text-sm center-align p-0" style="width:12%">Initial</th>
								<th class="text-sm center-align p-0" style="width:12%">Remaining</th>
								<th class="hidden">Exact Basis</th>
								<th class="hidden">Reference Basis</th>
								<th class="hidden">Qty * Exact Basis</th>
								<th class="hidden">Qty * Ref. Basis</th>
								<th class="center-align" title="${taxBasisHeaderTooltip}">
									${(() => {
										const words = labelTaxBasis.split(' ');
										return words.length > 1
											? `${words[0]}<i class="material-icons" style="font-size: 10px; vertical-align: middle; color: #999; margin-left: 2px;">info_outline</i>&ensp;&ensp;<br>${words.slice(1).join(' ')}`
											: `${labelTaxBasis}<i class="material-icons" style="font-size: 10px; vertical-align: middle; color: #999; margin-left: 2px;">info_outline</i>`;
									})()}
								</th>
								<th class="${showGainsColumns ? '' : 'hidden'} gains-column text-sm center-align p-0" style="width:10%" title="${gainLossTooltip}">Gain/<i class="material-icons" style="font-size: 10px; vertical-align: middle; color: #999; margin-left: 2px;">info_outline</i>&ensp;&ensp;<br>Loss</th>
							</tr>
						</thead>
						<tbody>
							${giftTransactions
								.map(
									(t, i) => `
								<tr id="historicsTable${i}" ${Math.abs(Number(t.quantity)) < 0.005 ? 'class="hidden"' : ''} style="height: 10px;">
									<td class="p-0" data-sort='${lettersToNumbers(t.tranche)}'>
										<div class="shareholder-row" style="position: relative;">
											<p id="cbElem-${t.tranche}" class="${fromUser ? '' : 'selectShareholder'} ml-6 mt-0 mb-0" style="overflow: hidden; text-align: center;">
												${
													fromUser || t._eventFunded
														? ``
														: `
													<label>
														<input class="selectTrancheInput" type="checkbox" data-tranche=${t.tranche} data-class=${t.shareClass} />
														<span></span>
													</label>
												`
												}
											</p>
											<div id="imgElem-${t.tranche}" class="text-sm font-bold ${fromUser ? '' : 'shareholder-img'} center-align ml-6">${t.tranche}</div>
										</div>
									</td>
									${
										CLASS_COLUMN
											? `
										<td class="text-xs center-align p-0" style="width: 80px; max-width: 80px;">
											${t.shareClass}
										</td>
									`
											: ``
									}
									${
										CERT_COLUMN
											? `
										<td class="text-xs center-align p-0" style="width: 80px; max-width: 80px;" data-sort='${t.certSort}'>
											${t.certNumber}
										</td>
									`
											: ``
									}
									<td class="text-xs center-align p-0" data-sort='${t.date}'>
										${moment.unix(t.date).utc().format('MMM D, YYYY')}
									</td>
									<td class="text-xs p-0">
										${safeHtml(t.notes)}
									</td>
									<td class="text-xs center-align p-0">
										${Number(t.before).toLocaleString('en-US', m5)}
									</td>
									<td id="quantity${t.tranche}" class="text-xs center-align p-0">
										${Number(t.quantity.replace(/[^.\d]/g, '')).toLocaleString('en-US', m5)}
									</td>
									<td class="hidden">
										${t.sharePrice && !isNaN(t.sharePrice) ? Number(t.sharePrice.replace(/[^.\d]/g, '')).toLocaleString('en-US', usp) : 'n.a.'}
									</td>
									<td class="hidden">
										${t.referencePrice && !isNaN(t.referencePrice) ? Number(t.referencePrice.replace(/[^.\d]/g, '')).toLocaleString('en-US', usp) : ''}
									</td>
									<td class="hidden">
										${t.quantity && t.sharePrice && !isNaN(t.sharePrice) ? (Number(t.quantity.replace(/[^.\d]/g, '')) * Number(t.sharePrice.replace(/[^.\d]/g, ''))).toLocaleString('en-US', usp) : ''}
									</td>
									<td class="hidden">
										${t.quantity && t.referencePrice && !isNaN(t.referencePrice) ? (Number(t.quantity.replace(/[^.\d]/g, '')) * Number(t.referencePrice.replace(/[^.\d]/g, ''))).toLocaleString('en-US', usp) : ''}
									</td>
									<td id="historicalBasis-${i}" class="text-xs center-align p-0 editable-tax-basis ${basisColorClass(t._basisSource)}"
										data-nrid="${t.nrid || ''}" data-share-class="${t.shareClass || ''}" data-row-index="${i}"
										title="${
											isAdmin && !VOA && !isExec && !isSupv
												? 'Click to edit -- entering a value here sets the actual, reported Tax Basis on this transaction.'
												: friendlyBasisTooltip(t._basisSource)
										}"
										${isAdmin && !VOA && !isExec && !isSupv ? 'contenteditable' : ''}>
										${
											/^(n\/?a|n\.a\.?)$/i.test(String(t._effectiveTaxBasis).trim())
												? t._effectiveTaxBasis
												: Number.isFinite(t._effectiveTaxBasis)
													? t._effectiveTaxBasis.toLocaleString('en-US', usd)
													: ''
										}
									</td>
									<td class="${showGainsColumns ? '' : 'hidden'} gains-column text-xs center-align p-0" title="${gainLossTooltip}">
										${(() => {
											const gl = computeGainLoss(t, clientData);
											if (!gl) return 'n.a.';
											const amountClass = gl.totalGain >= 0 ? 'text-primary' : 'text-error';
											const stBadge = gl.isLongTerm ? '' : '&nbsp;<span class="text-error text-xxs font-bold">ST</span>';
											return `<span class="${amountClass}">${gl.totalGain.toLocaleString('en-US', usd)}</span>${stBadge}`;
										})()}
									</td>
								</tr>
							`
								)
								.join('')}
							<tfoot id="historicalsFooter">
								<tr style="height: 36px;">
									<td id="consolidate" class="text-xs py-0 px-4" style="max-width: 50px; white-space: nowrap; overflow: visible; position: relative; z-index: 2;"></td>
									${CLASS_COLUMN ? `<td class="p-0"></td>` : ``}
									${CERT_COLUMN ? `<td class="p-0"></td>` : ``}
									<td class="p-0"></td>
									<td class="p-0"></td>
									<td id="totalBefore" class="text-xs font-bold center-align p-0"></td>
									<td id="totalAfter" class="text-xs font-bold center-align p-0"></td>
									<td class="hidden"></td>
									<td class="hidden"></td>
									<td class="hidden"></td>
									<td class="hidden"></td>
									<td id="avgCostBasis" class="text-xs font-bold center-align p-0"></td>
									<td id="totalGainLoss" class="${showGainsColumns ? '' : 'hidden'} gains-column text-xs font-bold center-align p-0"></td>
								</tr>
							</tfoot>
						</tbody>
					</table>
					`
					}
				</div>
				${
					priorTransfers.length
						? `
				<div id="showHidePriorsDiv" class="${stepwiseAnalysis?.checked ? 'mb-2' : 'mb-0'} mt-3 flex justify-between items-center">
					<span id="showHidePriors" class="text-xs ml-6 p-0 link-accent">
						${stepwiseAnalysis?.checked ? 'Hide Prior Tranches' : 'Show Prior Tranches'}
					</span>
				</div>
				`
						: `
				`
				}
				<table id="trancheTable" class="highlight table-content${stepwiseAnalysis?.checked ? '' : ' hidden'}">
					<thead>
						<tr>
							<th class="text-xs center-align p-05" style="width:4%" data-sort-default>Tranche</th>
							<th class="text-xs center-align p-05" style="width:6%">Class</th>
							<th class="text-xs p-05">Historic Holdings (source)</th>
							<th class="text-xs p-05">Prior Transfers (destination)</th>
						</tr>
					</thead>
					<tbody id="tableBody">
						<!-- Table rows will be added here -->
					</tbody>
				</table>
				<div id="trancheKeysLegend" class="mt-1 ml-1${stepwiseAnalysis?.checked ? '' : ' hidden'}">${keysLegendHtml}</div>
			</div>
		`;

		let table, sort;
		userGiftTransferTable.innerHTML = userTransactionsMarkup;
		table = document.getElementById('historicalsSortTable');
		if (table) sort = new Tablesort(table, { ascending: true });

		if (isAdmin && !VOA && !isExec && !isSupv) renderHistoricalBasisListeners();

		// Historical Tax Basis column (#5621): every value is editable, whether an
		// already-reported basis or a derived fallback being promoted to a real one.
		// Writes go through TransactionService.js, same as Ledgers.
		function renderHistoricalBasisListeners() {
			document.querySelectorAll('.editable-tax-basis').forEach((cell) => {
				let originalText;

				// Click in: clear to just "$", cursor after it (same collapse() idiom
				// as .gift-qty's listeners above).
				cell.addEventListener('focus', function () {
					originalText = this.textContent.trim();
					this.textContent = '$';
					document.getSelection().collapse(this, this.childNodes.length);
				});

				cell.addEventListener('keydown', function (e) {
					if (e.key === 'Enter') {
						e.preventDefault();
						e.stopPropagation();
						this.blur();
					} else if (e.key === 'Escape') {
						// stopPropagation is essential here -- Materialize's modal
						// listens for Escape at the document level to close itself,
						// and would otherwise close the whole Gift Transfer modal out
						// from under the user instead of just reverting this cell.
						e.preventDefault();
						e.stopPropagation();
						this.textContent = originalText;
						this.blur();
					}
				});

				// Live-sanitize like .gift-qty above, keeping one leading "$". Skipped once
				// a letter's been typed, since "n/a" is a valid value here.
				cell.addEventListener('input', function () {
					const current = this.textContent;
					if (/[a-zA-Z]/.test(current)) return;

					const digits = current.replace(/[^-.\d]/g, '');
					const cleaned = '$' + digits;
					if (cleaned === current) return;

					const sel = document.getSelection();
					const caretInDigits = sel && sel.rangeCount ? Math.max(0, sel.getRangeAt(0).startOffset - 1) : digits.length;
					this.textContent = cleaned;
					document.getSelection().collapse(this.firstChild || this, Math.min(caretInDigits + 1, cleaned.length));
				});

				cell.addEventListener('blur', function () {
					const raw = this.textContent.trim();
					if (raw === originalText) return;

					// Nothing typed after focus cleared it to "$" -- not a real edit.
					const withoutDollar = raw.replace(/^\$/, '').trim();
					if (withoutDollar === '') {
						this.textContent = originalText;
						return;
					}

					const nrid = this.getAttribute('data-nrid');
					const shareClass = this.getAttribute('data-share-class');
					if (!nrid || !shareClass) {
						this.textContent = originalText;
						return;
					}

					// "#" is never relevant here -- unlike cert numbers, Tax Basis is a
					// plain number or an explicit "n/a" (meaning: don't derive one).
					const isExplicitNA = isNA(withoutDollar);
					const cleaned = withoutDollar.replace(/[^-.\d]/g, '');
					const isValidNumber = cleaned !== '' && isKnownNumber(cleaned);

					if (!isExplicitNA && !isValidNumber) {
						// Not a recognizable value -- revert rather than save garbage.
						this.textContent = originalText;
						return;
					}

					const newTaxBasis = isExplicitNA ? 'n/a' : cleaned;
					const cellElem = this;

					const t = giftTransactions.find((g) => g.nrid === nrid);
					if (!t) {
						cellElem.textContent = originalText;
						return;
					}

					// Full-record replace (see TransactionService.js docstring) --
					// every field must be resent, not just taxBasis, or the backend
					// blanks whatever's omitted.
					// GH: quantity comes from pristineQuantityByNrid, NOT t.quantity --
					// t is the same object reference the matching engine may have
					// mutated in place to hold a partially-consumed lot's remaining
					// balance (for display only). Sending t.quantity here would
					// silently overwrite the real persisted quantity with that
					// analysis-computed remainder. Falls back to t.quantity only if
					// this transaction was never captured pre-mutation (shouldn't happen).
					const transaction = {
						// #6405: custom values too, or the replace drops them
						...giftCustomColumnSnapshot(t, clientData),
						date: t.date,
						expDate: t.expDate,
						notes: t.notes,
						securityType: t.securityType,
						sharePrice: t.sharePrice,
						quantity: pristineQuantityByNrid.has(t.nrid) ? pristineQuantityByNrid.get(t.nrid) : t.quantity,
						sender: t.sender,
						receiver: t.receiver,
						senderName: t.senderName,
						receiverName: t.receiverName,
						strikePrice: t.strikePrice,
						nrid: t.nrid,
						taxBasis: newTaxBasis,
					};

					// Snapshot the pre-edit state so a failed save can be rolled back --
					// applyLocalUpdate() below is optimistic (runs immediately, before
					// the network call resolves), so undoing it means restoring both
					// the transaction object and the cell's rendered state.
					const priorTaxBasis = t.taxBasis;
					const priorEffective = t._effectiveTaxBasis;
					const priorSource = t._basisSource;
					const priorClassName = cellElem.className;
					const priorTitle = cellElem.title;
					const priorOriginalText = originalText;

					function applyLocalUpdate() {
						t.taxBasis = newTaxBasis;
						const basis = resolveEffectiveTaxBasis(t, clientData);
						t._effectiveTaxBasis = basis.value;
						t._basisSource = basis.source;

						// Redraw with the same formatting the initial render uses (row
						// template above) -- without this the cell was just left showing
						// whatever raw text was typed (e.g. "42.5" instead of "42.50").
						const displayText = isNA(String(t._effectiveTaxBasis))
							? t._effectiveTaxBasis
							: Number.isFinite(t._effectiveTaxBasis)
								? t._effectiveTaxBasis.toLocaleString('en-US', usd)
								: '';
						cellElem.textContent = displayText;
						cellElem.className = `text-xs center-align p-0 editable-tax-basis ${basisColorClass(t._basisSource)}`;
						cellElem.title = 'Click to edit -- entering a value here sets the actual, reported Tax Basis on this transaction.';
						originalText = displayText;

						// The footer's average-cost-basis figure is derived from every
						// row's _effectiveTaxBasis and doesn't update itself -- recompute
						// it now that this row's value changed.
						showTotals();
					}

					function revertLocalUpdate() {
						t.taxBasis = priorTaxBasis;
						t._effectiveTaxBasis = priorEffective;
						t._basisSource = priorSource;
						cellElem.textContent = priorOriginalText;
						cellElem.className = priorClassName;
						cellElem.title = priorTitle;
						originalText = priorOriginalText;
						showTotals();
					}

					// Format immediately on Enter/blur rather than waiting on the
					// network round-trip -- the save happens in the background and
					// rolls the cell back only if it actually fails.
					applyLocalUpdate();

					// Both branches fire 'RefreshSessionStorage' on success, same as GT's other
					// write flows, so Events & Transactions (which snapshots its own
					// transactions array) picks up the new Tax Basis without a manual refresh.
					if (EXTERNAL_DATA) {
						queueExternalWrite({ record: transaction, action: 'update' });
						flushExternalQueue({
							userid: authedUser,
							cookie,
							client,
							clientData,
							isACR: typeof isACR !== 'undefined' && isACR,
						}).then((ok) => {
							if (!ok) revertLocalUpdate();
							else document.dispatchEvent(new Event('RefreshSessionStorage'));
						});
					} else {
						patchTransactionRemote({ userid: authedUser, cookie, client, clientData, shareClass, transaction })
							.then(() => document.dispatchEvent(new Event('RefreshSessionStorage')))
							.catch((err) => {
								console.error('Failed to save Tax Basis edit:', err);
								revertLocalUpdate();
							});
					}
				});
			});
		}

		trancheErrorText = '';

		let tranches = new Set([...priorTransfers, ...historicHoldings].map((item) => item.tranche));
		let sortedTranches = Array.from(tranches).sort((a, b) => lettersToNumbers(a) - lettersToNumbers(b));

		// Flag only when the transaction's own notes have a #-tagged, cert-shaped reference
		// but no certNumber -- not gated on CERT_COLUMN, which false-positived for Edmund Optics.
		{
			const seenMissingCerts = new Set();
			const certRefPattern = new RegExp('#\\s*' + CERT_TOKEN, 'i');

			giftTransactions.forEach((transaction) => {
				const certNumber = String(transaction.certNumber ?? '').trim();
				if (certNumber) return;
				if (!certRefPattern.test(transaction.notes || '')) return;

				const key =
					transaction.nrid ||
					[
						transaction.date || '',
						transaction.shareClass || '',
						transaction.sender || '',
						transaction.receiver || '',
						transaction.quantity || '',
						transaction.notes || '',
					].join('|');

				if (seenMissingCerts.has(key)) return;
				seenMissingCerts.add(key);

				const missingCertErrorText = `Gift transaction missing certNumber${
					transaction.date ? ` on ${moment.unix(Number(transaction.date)).utc().format('M/D/YYYY')}` : ''
				}${transaction.notes ? `: ${transaction.notes}` : ''}`;

				trancheErrorText += (trancheErrorText ? ' | ' : '') + missingCertErrorText;

				// Suppressed in Stepwise Analysis -- these dev-only diagnostics compare against
				// full transaction history, so a partial (stepped-to) view spuriously trips them.
				if (!getCookie('userIndex') && authedUser == 'cmcconnell@nthround.com' && !stepwiseAnalysis?.checked) {
					showToast('warning-toast', missingCertErrorText, 10000);
				}
			});
		}

		// Flag each unique historic holding note/certificate containing a # character
		const seenHashNotes = new Set();
		// Suppressed in Stepwise Analysis -- see comment on the missing-certNumber check above.
		let showErrorToast = !getCookie('userIndex') && authedUser == 'cmcconnell@nthround.com' && !stepwiseAnalysis?.checked;

		historicHoldings.forEach((holding) => {
			const notes = String(holding.notes || '');
			if (!notes.includes('#')) return;
			const key = [holding.tranche || '', holding.certNumber || '', notes].join('|');
			if (seenHashNotes.has(key)) return;
			seenHashNotes.add(key);
			const notesErrorText =
				'Historic holding notes contain #' +
				(holding.tranche ? ' in tranche ' + holding.tranche : '') +
				(holding.certNumber ? ', cert ' + holding.certNumber : '') +
				': ' +
				notes;
			trancheErrorText += (trancheErrorText ? ' | ' : '') + notesErrorText;
			if (showErrorToast) showToast('warning-toast', notesErrorText, 10000);
		});

		let reportSourceHoldings = [];
		sortedTranches.forEach((tranche) => {
			let transfers = priorTransfers.filter((item) => item.tranche == tranche);
			let holdings = collapseHistoricHoldingsForDisplay(historicHoldings.filter((item) => item.tranche == tranche));
			if (transfers.length) {
				let sumTransfers = transfers.reduce((a, b) => a + Number(b.quantity), 0);
				let sumHoldings = holdings.reduce((a, b) => a + Number(b.quantity), 0);
				// #6144a: report-only tolerance (1% of tranche size, see nearlyEqualTrancheSum) --
				// looser than the matching engine's own quantitiesMatch, which stays tight.
				let quantityMismatch = !nearlyEqualTrancheSum(sumTransfers, sumHoldings);
				let hasBuyBack = false;
				if (quantityMismatch) {
					const mismatchErrorText = 'Tranche ' + tranche + ' sum mismatch (' + sumHoldings + ' != ' + sumTransfers + ').';
					// Scanner-only marker (does not affect the toast/UI): lets the report
					// distinguish a real mismatch from a tranche with a Buy-Back/Redemption,
					// where a quantity decline can be legitimate.
					hasBuyBack = transfers.some((t) => /buy.?back|redemption/i.test(t.notes || ''));
					trancheErrorText += (trancheErrorText ? ' | ' : '') + mismatchErrorText + (hasBuyBack ? ' [Buy-Back/Redemption present]' : '');
					if (showErrorToast) showToast('warning-toast', mismatchErrorText, 10000);
				}
				holdings.sort((a, b) => Number(a.quantity) - Number(b.quantity));
				transfers.sort((a, b) => Number(a.quantity) - Number(b.quantity));
				// #6144a: flag the row inline (not just the toast) when the mismatch isn't already
				// explained by a Buy-Back/Redemption -- surfaces the existing detector at a glance.
				addTableRow(tranche, holdings, transfers, sumTransfers, quantityMismatch && !hasBuyBack, sumHoldings);
				reportSourceHoldings.push(...holdings);
			}
		});

		// Scanner-only rollup: how many current sources (tranches) back this holder's
		// balance, and "tranche:Key" for each, oldest source first (a repeated tranche
		// letter here means that tranche was drawn on more than once).
		let sourceCount = reportSourceHoldings.length;
		let keysList = reportSourceHoldings
			.slice()
			.sort((a, b) => Number(a.date) - Number(b.date))
			.map((h) => (MATCH_KEY_LABELS[h._matchKey] ? h.tranche + ':' + MATCH_KEY_LABELS[h._matchKey] : ''))
			.filter(Boolean)
			.join('|');

		// #6144a: fraction of current shares sourced via a Best-Guess (weak-evidence) match --
		// feeds the Scanner's "majority Best-Guess" category. A balanced total can still rest
		// entirely on inferred sources, which sourceCount/keysList alone don't call out.
		let sourceQtyTotal = reportSourceHoldings.reduce((a, h) => a + (Number(h.quantity) || 0), 0);
		let bestGuessQty = reportSourceHoldings.filter((h) => h._matchKey === 'E').reduce((a, h) => a + (Number(h.quantity) || 0), 0);
		let bestGuessShareFraction = sourceQtyTotal > 0 ? bestGuessQty / sourceQtyTotal : 0;

		ttlBefore = 0;
		ttlAfter = 0;

		giftTransactions.forEach((row) => {
			const beforeQty = Number(row.before);
			const afterQty = row.after != null ? Number(row.after) : Number(row.quantity);
			ttlBefore += Number.isFinite(beforeQty) ? beforeQty : 0;
			ttlAfter += Number.isFinite(afterQty) ? afterQty : 0;
		});

		document.dispatchEvent(
			new CustomEvent('giftTransferTrancheAnalysisComplete', {
				detail: {
					userid: giftUserId,
					sharesOwned: Number(balanceBefore) || 0,
					sourceCount,
					keysList,
					bestGuessShareFraction,
					ttlBefore,
					ttlAfter,
					trancheErrorText,
				},
			})
		);

		function collapseHistoricHoldingsForDisplay(holdings) {
			const merged = [];
			holdings.forEach((h) => {
				const qty = Number(h.quantity) || 0;

				// Only merge rows that are really the same source lot for display purposes
				let existing = merged.find(
					(m) =>
						(m.notes || '') == (h.notes || '') &&
						(m.shareClass || '') == (h.shareClass || '') &&
						(m.tranche || '') == (h.tranche || '') &&
						String(m.sender || '') == String(h.sender || '') &&
						String(m.receiver || '') == String(h.receiver || '') &&
						String(m.sharePrice || '') == String(h.sharePrice || '') &&
						String(m.taxBasis || '') == String(h.taxBasis || '')
				);

				if (existing) existing.quantity = (Number(existing.quantity) + qty).toString();
				else merged.push({ ...h });
			});
			return merged;
		}

		function trancheValueLabel(row) {
			const hasPrice = isKnownNumber(row?.sharePrice);
			const hasBasis = isKnownNumber(row?.taxBasis);
			const priceNum = hasPrice ? Number(String(row.sharePrice).replace(/[^.\d]/g, '')) : null;
			const basisNum = hasBasis ? Number(String(row.taxBasis).replace(/[^-.\d]/g, '')) : null;
			const priceText = priceNum == null || isNaN(priceNum) ? '' : priceNum.toLocaleString('en-US', usp);
			const basisText = basisNum == null || isNaN(basisNum) ? '' : basisNum.toLocaleString('en-US', usp);
			if (basisNum != null && priceNum != null) {
				if (Math.abs(basisNum - priceNum) < 0.005) return basisText;
				return priceText + ' | TB ' + basisText;
			}
			if (basisNum != null) return 'TB ' + basisText;
			if (priceNum != null) return priceText;
			return '';
		}

		// Dollar classes read "$1,234", share classes "1,234 shares".
		function quantityText(classKey, amount) {
			const text = Math.abs(Number(amount)).toLocaleString();
			if (getEffectiveDenomination(clientData, classKey) !== 'dollars') return (Number(amount) < 0 ? '-' : '') + text + ' shares';
			return (Number(amount) < 0 ? '-$' : '$') + text;
		}

		function addTableRow(tranche, holdings, transfers, sumTransfers, flagged, sumHoldings) {
			let tableBody = document.getElementById('tableBody');
			let row = document.createElement('tr');
			// #6144a: highlight rows whose Historic Holdings/Prior Transfers totals don't
			// reconcile (and aren't explained by a Buy-Back/Redemption) -- same detector that
			// already drives trancheErrorText/the warning toast, just surfaced per-row too.
			if (flagged) row.classList.add('bg-red-50');

			let trancheCell = document.createElement('td');
			let trancheDate = transfers[0] ? moment.unix(Number(transfers[0].date)).utc() : moment.utc();
			trancheCell.innerHTML =
				tranche +
				'<p class="text-xxs font-md mt-1 mb-0">' +
				trancheDate.format('MMM D') +
				'<br>' +
				trancheDate.format('YYYY') +
				'</p>' +
				(flagged
					? `<p class="text-xxs text-red-700 font-bold mt-1 mb-0" style="cursor: help;" title="Historic Holdings total (${quantityText(transfers[0]?.shareClass, sumHoldings)}) doesn't match Prior Transfers total (${quantityText(transfers[0]?.shareClass, sumTransfers)}) -- under review.">` +
						`<i class="material-icons" style="font-size: 12px; vertical-align: text-bottom;">warning_amber</i>&nbsp;Review</p>`
					: '');
			trancheCell.classList.add('text-xs', 'font-bold', 'center-align', 'p-0');
			trancheCell.setAttribute('data-sort', lettersToNumbers(tranche));
			row.appendChild(trancheCell);

			let classCell = document.createElement('td');
			let trancheClass = transfers[0] ? transfers[0].shareClass : '?';
			const classQuantity = quantityText(trancheClass, sumTransfers).replace(/ shares$/, '<br>shares');
			classCell.innerHTML = trancheClass + '<p class="text-xxs mt-1 mb-0">' + classQuantity + '</p>';
			classCell.classList.add('text-xs', 'center-align', 'p-0');
			row.appendChild(classCell);

			let holdingsCell = document.createElement('td');
			holdingsCell.innerHTML = holdings
				.map((h) => {
					let valueLabel = trancheValueLabel(h);
					return (
						h.notes +
						' - ' +
						quantityText(h.shareClass, h.quantity) +
						' from ' +
						h.senderName.replace('Issuer', capitalize(client)) +
						matchKeyBadge(h._matchKey) +
						(valueLabel ? '<span class="text-secondary">&ensp;' + valueLabel + '</span>' : '')
					);
				})
				.join('<br>');
			if (isVERM) holdingsCell.innerHTML = holdingsCell.innerHTML.replace(/from Vermeer/g, 'reissued');
			holdingsCell.classList.add('text-xs');
			row.appendChild(holdingsCell);

			let transfersCell = document.createElement('td');
			transfersCell.innerHTML = transfers
				.map((t) => {
					let valueLabel = trancheValueLabel(t);
					return (
						t.notes +
						' - ' +
						quantityText(t.shareClass, t.quantity) +
						' to ' +
						t.receiverName.replace('Issuer', capitalize(client)) +
						matchKeyBadge(t._matchKey || (t._basisCarried ? 'F' : '')) +
						(valueLabel ? '<span class="text-secondary">&ensp;' + valueLabel + '</span>' : '')
					);
				})
				.join('<br>');
			if (isVERM) transfersCell.innerHTML = transfersCell.innerHTML.replace(/to Vermeer/g, 'cancelled');
			transfersCell.classList.add('text-xs');
			row.appendChild(transfersCell);

			tableBody.appendChild(row);
		}

		let tableP, sortP;
		tableP = document.getElementById('trancheTable');
		if (tableP) sortP = new Tablesort(tableP, { ascending: true });

		if (addedGiftTransferListenersAlready) showTotals();
	}

	const debtEquityTabs = document.getElementById('debtEquityTabs');
	const trancheTable = document.getElementById('trancheTable');
	const trancheKeysLegend = document.getElementById('trancheKeysLegend');
	const showHidePriors = document.getElementById('showHidePriors');
	const selectRecipient = document.getElementById('selectRecipient');
	const addRecipientBtn = document.getElementById('addRecipientBtn');
	const newHoldingButton = document.getElementById('newHoldingButton');
	const showHidePriorsDiv = document.getElementById('showHidePriorsDiv');
	const selectRecipientDiv = document.getElementById('selectRecipientDiv');
	const previewGiftTransfer = document.getElementById('previewGiftTransfer');
	const statementWaitHeader = document.getElementById('statementWaitHeader');
	const totalCurrentValueDiv = document.getElementById('totalCurrentValueDiv');
	const historicalsSortTable = document.getElementById('historicalsSortTable');
	const consolidateTransfers = document.getElementById('consolidateTransfers');

	if (showHidePriors)
		listen(showHidePriors, 'click', function (e) {
			// Prior Tranches always stays shown in Stepwise Analysis.
			if (stepwiseAnalysis?.checked) return;
			document.getElementById('viewGiftTransferModal').style.overflowY = 'auto';
			if (trancheTable.classList.contains('hidden')) {
				trancheTable.classList.remove('hidden');
				if (trancheKeysLegend) trancheKeysLegend.classList.remove('hidden');
				if (showHidePriorsDiv) {
					showHidePriorsDiv.classList.remove('mb-0');
					showHidePriorsDiv.classList.add('mb-2');
				}
				this.innerText = 'Hide Prior Tranches';
			} else {
				if (showHidePriorsDiv) {
					showHidePriorsDiv.classList.remove('mb-2');
					showHidePriorsDiv.classList.add('mb-0');
				}
				trancheTable.classList.add('hidden');
				if (trancheKeysLegend) trancheKeysLegend.classList.add('hidden');
				this.innerText = 'Show Prior Tranches';
			}
		});

	const showHideGains = document.getElementById('showHideGains');
	if (showHideGains)
		listen(showHideGains, 'click', function () {
			showGainsColumns = !showGainsColumns;
			document.querySelectorAll('#historicalsSortTable .gains-column').forEach((el) => el.classList.toggle('hidden', !showGainsColumns));
			this.innerHTML = showGainsColumns ? 'Hide Gain/Loss' : 'Show Gain/Loss';
		});

	if (!addedGiftTransferListenersAlready) {
		addedGiftTransferListenersAlready = true;
		let showHideZeros = document.getElementById('showHideZeros');
		if (showHideZeros) {
			listen(showHideZeros, 'click', function (e) {
				historicalsSortTable?.classList.toggle('hidden', toggle === 'Hide All');
				if (toggle !== 'Hide All') {
					let len = giftTransactions.length; //historicHoldings.length;
					for (let i = 0; i < len; i++) {
						let thisRow = document.getElementById('historicsTable' + i);
						// #6144e: was `thisRow.cells[4].innerText.trim() === '0'` -- a hardcoded
						// column index that assumed no CLASS_COLUMN/CERT_COLUMN. Those columns
						// render whenever the shareholder holds >1 class or has certificate
						// numbers, shifting cells[4] off "Remaining" onto Date/Cert# and silently
						// breaking this check (zero row never hidden). Read the underlying data
						// directly instead, matching the same epsilon used at render time.
						let isZero = Math.abs(Number(giftTransactions[i].quantity)) < 0.005;
						if (toggle === 'Show All' || (toggle === 'Show Positves' && !isZero) || (toggle === 'Show Zeros' && isZero))
							thisRow.classList.remove('hidden');
						else thisRow.classList.add('hidden');
					}
				}
				if (toggle === 'Show All' || toggle === 'Show Positves') showTotals();
				switch (toggle) {
					// case "Open Modal": toggle = "Hide All"; break;
					case 'Show Positves':
						toggle = 'Hide All';
						break;
					case 'Show Zeros':
						toggle = 'Show Positves';
						break;
					case 'Show All':
						toggle = 'Show Zeros';
						break;
					case 'Hide All':
						toggle = 'Show All';
						break;
				}
				this.innerText = toggle;
			});
		}
		listen(addRecipientBtn, 'click', async function (e) {
			e.preventDefault();

			if (selectRecipientDiv.classList.contains('hidden')) {
				selectRecipient.selectedIndex = 0;
				selectRecipient.dispatchEvent(new Event('change'));
				selectRecipientDiv.classList.remove('hidden');

				const recipientPicker = document.getElementById('recipientPicker');
				if (recipientPicker) recipientPicker.classList.remove('hidden');

				const oldSelectWrapper = selectRecipientDiv.querySelector('.select-wrapper');
				if (oldSelectWrapper) oldSelectWrapper.classList.add('hidden');

				totalCurrentValueDiv.classList.add('hidden');
			} else {
				selectRecipientDiv.classList.add('hidden');
				totalCurrentValueDiv.classList.remove('hidden');
			}

			await getNextCertNumber();
		});
		listen(selectRecipient, 'change', function (e) {
			if (!this.value) return;
			// document.getElementById('viewGiftTransferModal').style.overflowY = "auto";
			let id, recip;
			let name = DOMPurify.sanitize(this.value.trim());
			// let name = this.value.includes('|') ? this.value.split('|')[1].trim() : this.value.trim();
			if (name == 'Issuer') {
				id = 'Issuer';
				recip = { userid: 'issuer', user_name: `${clientData.company_name} (Issuer)`, clients: { [client]: { shares: {} } } };
			} else {
				// see if the name has the email already, or if it's BB-style name
				let id = name.split('|')[1].trim();
				if (!validator.isEmail(id)) id = capTableData.find((u) => u.user_name == name).userid;
				if (!id || id == giftUserId) return;
				recip = members ? members.find((u) => u.userid == id) : capTableData ? capTableData.find((u) => u.userid == id) : null;
			}
			if (recip && !giftRecipients.find((r) => r.userid == id)) giftRecipients.push(recip);
			selectRecipientDiv.classList.add('hidden');
			totalCurrentValueDiv.classList.remove('hidden');
			renderGiftRecipients();
			// viewGiftTransferModal.scrollTo({ top: 0, behavior: 'smooth' });
			renderNewGiftListeners();
			buildSelectRecipientOptions();
			document.getElementById(recip.userid + 'AddGift').dispatchEvent(new Event('click'));
		});
		listen(document.getElementById('removeRecipientBtn'), 'click', function (e) {
			let giftsToRemove = allGifts.filter((g) => g.receiver == removeGiftId);
			giftsToRemove.forEach((g) => adjustBalances(-Number(g.quantity.replace(/[^-.\d]/g, '')), g.receiver, g.tranche));

			// Roll back the cert numbers this recipient consumed, per class -- but only
			// when they're exactly the most recent ones issued for that class (the tail
			// of certIssueLog). If someone else already got a later cert for this class,
			// rewinding would be wrong, so warn instead of guessing.
			[...new Set(giftsToRemove.map((g) => g.shareClass))].forEach((c) => {
				const n = giftsToRemove.filter((g) => g.shareClass == c).length;
				const log = certIssueLog?.[c] || [];
				const tail = log.slice(-n);
				if (tail.length == n && tail.every((entry) => entry.receiver == removeGiftId)) {
					certIssueLog[c] = log.slice(0, -n);
					rewindNextCertNumber(c, n);
				} else if (log.some((entry) => entry.receiver == removeGiftId)) {
					M.toast({
						html: `Removing this recipient leaves a skipped certificate number for Class ${c} -- another recipient already received a later one.`,
						classes: 'orange darken-1 text-sm',
						displayLength: 6000,
					});
				}

				// Same idea for next_cert_number custom-column suggestions (e.g. Sheetz) --
				// a separate log since it's not tied to certIssueLog/nextCertNum at all.
				const customLog = customCertLog[c] || [];
				const customCount = customLog.filter((entry) => entry.receiver == removeGiftId).length;
				if (customCount) {
					const customTail = customLog.slice(-customCount);
					if (customTail.every((entry) => entry.receiver == removeGiftId)) {
						customCertLog[c] = customLog.slice(0, -customCount);
					} else {
						M.toast({
							html: `Removing this recipient leaves a skipped certificate number in a custom field for Class ${c} -- another recipient already received a later one.`,
							classes: 'orange darken-1 text-sm',
							displayLength: 6000,
						});
					}
				}
			});

			giftRecipients = giftRecipients.filter((r) => r.userid != removeGiftId);
			allGifts = allGifts.filter((g) => g.receiver != removeGiftId);
			delete cashSaleRecipients[removeGiftId];
			M.Modal.getInstance(document.getElementById('confirmRecipientRemove')).close();
			let recipientDiv = document.getElementById(removeGiftId + '|Div');
			recipientDiv.outerHTML = '';
			if (giftRecipients.length < 1) {
				document.getElementById('viewRecipients').innerHTML = `
					<div class="bg-grey-lightest rounded border-grey grey-text center-align p-1 mt-4">
						Recipients will appear here.
						<!-- <p class="mt-0"><a href="#addRecipient" class="modal-trigger">Add Recipient</a></p> -->
					</div>
				`;
				listen(viewRecipients, 'click', (e) => addRecipientBtn.dispatchEvent(new Event('click')));
			}
			if (!allGifts.length) previewGiftTransfer.classList.add('disabled');
			renderNewGiftListeners();
			buildSelectRecipientOptions();
		});
		listen(filterViewGT, 'change', function (e) {
			selectedClass = filterViewGT.value;
			showFilteredGiftTrans();
			if (selectedClass == 'ALL') initializeDebtEquityTabs(); //viewEventsIconLarge.dispatchEvent(new Event('click'));
		});
		listen(document.getElementById('showDecimals'), 'change', function (e) {
			e.preventDefault();
			if (includedClasses.length > 1) {
				if (!selectedClass) selectedClass = 'ALL';
				showFilteredGiftTrans();
			} else {
				showFilteredGiftTrans();
			}
		});
		if (newHoldingButton) {
			listen(newHoldingButton, 'click', function (e) {
				window.location.replace('/admin/ledgers/?action=newtransaction&recipient=' + encodeURIComponent(shareholder));
			});
		}
		listen(document.getElementById('exportGiftTransfer'), 'click', async function (e) {
			e.preventDefault();

			// NTH-INVESTOR-PORTAL-HC: these tables aren't always in the DOM for every render state.
			if (!historicalsSortTable || !trancheTable) return;

			let origHistoricalsHtml = historicalsSortTable.innerHTML;
			let origTrancheHtml = trancheTable.innerHTML;

			// export-only cleanup
			let footer = historicalsSortTable.querySelector('tfoot');
			if (footer) footer.remove();

			trancheTable.querySelectorAll('tbody tr').forEach((row) => {
				let cells = row.querySelectorAll('td');
				if (!cells.length) return;

				// Column A: AH | Dec 11 2025
				if (cells[0]) {
					let main = cells[0].innerHTML.split('<p')[0].trim();
					let sub = cells[0].querySelector('p')?.innerHTML.replace(/<br>/g, ', ').trim() || '';
					cells[0].innerHTML = sub ? main + ' | ' + sub : main;
				}

				// Column B: C | 680 shares
				if (cells[1]) {
					let main = cells[1].innerHTML.split('<p')[0].trim();
					let sub = cells[1].querySelector('p')?.innerHTML.replace(/<br>/g, ' ').trim() || '';
					cells[1].innerHTML = sub ? main + ' | ' + sub : main;
				}
			});

			try {
				await export_table_to_excel(
					'historicalsSortTable|trancheTable',
					giftUserData.user_name + ' Gift / Transfer Report (' + new Date().toLocaleString() + ').csv'
				);
			} finally {
				historicalsSortTable.innerHTML = origHistoricalsHtml;
				trancheTable.innerHTML = origTrancheHtml;
			}
		});
		listen(previewGiftTransfer, 'click', async function (e) {
			if (fromUser) sendThisRequest();
			else {
				const giftsForPreview = consolidateTransfers?.checked ? await getConsolidatedTransferGifts() : allGifts;
				renderTransactionMapTable(giftsForPreview);
			}
		});
		if (historicalsSortTable)
			listen(historicalsSortTable, 'click', async function (e) {
				// 🔹 Add `async`
				if (e.target.classList.contains('selectTrancheInput')) {
					let selected = e.target.getAttribute('data-tranche');
					let consolidate = document.getElementById('consolidate');
					let cbElem = document.getElementById('cbElem-' + selected);
					let imgElem = document.getElementById('imgElem-' + selected);
					let cbElems = document.querySelectorAll('.selectTrancheInput');

					if (selected == 'all') {
						selectedTranches = [];
						if (e.target.checked) {
							currentData.forEach((u) => selectedTranches.push(u.userid));
							document.querySelectorAll('input[type=checkbox]').forEach((el) => {
								if (el.classList.contains('selectTrancheInput')) el.checked = true;
							});
						} else {
							document.querySelectorAll('input[type=checkbox]').forEach((el) => {
								if (el.classList.contains('selectTrancheInput')) el.checked = false;
							});
						}
					} else {
						if (e.target.checked) {
							let thisClass = e.target.dataset.class;
							cbElem.style.display = 'block';
							imgElem.style.display = 'none';
							selectedTranches.push(selected);
							for (let i = 0; i < cbElems.length; i++) {
								if (cbElems[i].dataset.class != thisClass) cbElems[i].disabled = true;
								else cbElems[i].disabled = false;
							}
							addRecipientBtn.classList.add('text-secondary');
							addRecipientBtn.style.pointerEvents = 'none';
						} else {
							cbElem.style.display = 'none';
							imgElem.style.display = 'block';
							selectedTranches = selectedTranches.filter(function (tr) {
								return tr !== selected;
							});
						}
					}

					let trLen = selectedTranches.length;
					if (trLen == 0) {
						for (let i = 0; i < cbElems.length; i++) cbElems[i].disabled = false;
						addRecipientBtn.classList.remove('text-secondary');
						addRecipientBtn.style.pointerEvents = null;
					} else if (trLen == 1) {
						await getNextCertNumber();
						consolidate.innerHTML = 'Reissue Tranche: ' + (selectedTranches[0] || '');
						previewGiftTransfer.classList.remove('disabled');
						previewGiftTransfer.innerHTML = 'Preview This Reissuance';
					} else if (trLen > 1) {
						await getNextCertNumber();
						selectedTranches.sort();
						consolidate.innerHTML =
							'Consolidate Tranches: ' +
							(trLen > 1 ? `${selectedTranches.slice(0, -1).join(', ')} and ${selectedTranches[trLen - 1]}` : selectedTranches[0] || '');
						previewGiftTransfer.classList.remove('disabled');
						previewGiftTransfer.innerHTML = 'Preview This Consolidation';
					} else {
						consolidate.innerHTML = '';
						previewGiftTransfer.classList.add('disabled');
						previewGiftTransfer.innerHTML = 'Preview These Transfers';
					}

					console.log(selectedTranches);
				}
			});

		if (isAdmin) {
			const stepwiseControls = document.getElementById('stepwiseControls');
			const stepwiseCheckBox = document.getElementById('stepwiseCheckBox');
			stepwiseCheckBox?.classList.remove('hidden');
			// Hoisted out of the 'change' handler so this only ever binds once (was resetting
			// on every toggle, stacking duplicate button/keydown listeners).
			let _stepwiseBound = false;

			function initStepwiseListenersOnce() {
				if (_stepwiseBound) return;
				const firstBtn = document.getElementById('stepFirst');
				const prevBtn = document.getElementById('stepPrev');
				const nextBtn = document.getElementById('stepNext');
				const lastBtn = document.getElementById('stepLast');
				if (!firstBtn || !prevBtn || !nextBtn || !lastBtn) return;

				const onClick = (name) => (e) => {
					e.preventDefault();
					if (name === 'First') window._stepwiseTargetLen = 1;
					else if (name === 'Prev') window._stepwiseTargetLen = Math.max(1, (window._stepwiseTargetLen || 1) - 1);
					else if (name === 'Next') window._stepwiseTargetLen = Math.min(window._stepwiseMasterLen || 1, (window._stepwiseTargetLen || 1) + 1);
					else if (name === 'Last') window._stepwiseTargetLen = window._stepwiseMasterLen || 1;
					giftTransfer(giftUserId, false);
					showTrancheTable();
				};

				listen(firstBtn, 'click', onClick('First'));
				listen(prevBtn, 'click', onClick('Prev'));
				listen(nextBtn, 'click', onClick('Next'));
				listen(lastBtn, 'click', onClick('Last'));

				document.addEventListener(
					'keydown',
					(e) => {
						if (e.key === 'Home') return onClick('First')(e);
						if (e.key === 'ArrowLeft') return onClick('Prev')(e);
						if (e.key === 'ArrowRight') return onClick('Next')(e);
						if (e.key === 'Last') return onClick('Last')(e);
					},
					{ passive: false, signal: listenerSignal }
				);

				_stepwiseBound = true;
			}

			if (stepwiseAnalysis)
				listen(stepwiseAnalysis, 'change', function (e) {
					if (stepwiseAnalysis.checked) {
						balanceHeadline.classList.add('hidden');
						stepwiseControls.classList.remove('hidden');
						giftTransferControls.classList.add('hidden');
						showTrancheTable();
						initStepwiseListenersOnce();
					} else {
						stepwiseControls.classList.add('hidden');
						balanceHeadline.classList.remove('hidden');
						giftTransferControls.classList.remove('hidden');
						window._stepwiseTargetLen = null; // set clamp set to end
						giftTransfer(giftUserId, false); // repaint with full range
					}
				});

			// Already in stepwise mode (repaint/reopen): the change event won't fire to rebind.
			if (stepwiseAnalysis?.checked) initStepwiseListenersOnce();

			function showTrancheTable() {
				if (showHidePriorsDiv) {
					showHidePriorsDiv.classList.add('mb-2');
					showHidePriorsDiv.classList.remove('mb-0');
				}
				trancheTable.classList.remove('hidden');
				if (trancheKeysLegend) trancheKeysLegend.classList.remove('hidden');
				const showHide = document.getElementById('showHidePriors');
				if (showHide) showHide.innerText = 'Hide Prior Tranches';
			}

			const proTips = document.getElementById('proTips');
			if (proTips)
				listen(proTips, 'click', function (e) {
					e.preventDefault();
					const helpModalEl = document.getElementById('trancheAnalysisHelpModal');
					const giftModal = M.Modal.getInstance(viewGiftTransferModal);
					const helpModal = M.Modal.getInstance(helpModalEl);
					if (giftModal && giftModal.isOpen) {
						giftModal.options.onCloseEnd = () => helpModal.open();
						giftModal.close();
					} else helpModal.open();
				});
		}

		function initializeDebtEquityTabs() {
			let instance = M.Tabs.init(debtEquityTabs, {});
			debtEquityTabs.classList.add('hidden');
			setTimeout(() => {
				if (showTabs) {
					debtEquityTabs.classList.remove('hidden');
					instance.updateTabIndicator();
					console.log('Intialized Debt-Equity tabs');
				}
			}, 100);
		}

		showHideZeros.dispatchEvent(new Event('click'));
		renderGiftRecipients();

		const removeThisRequest = document.getElementById('removeThisRequest');
		confirmRequestRemove = document.getElementById('confirmRequestRemove');
		const shareholderTransferRequests = shareholderData.clients[client].transfer_requests || {};

		// Extract all gifts that are "Open"
		const openGifts = Object.values(shareholderTransferRequests)
			.flat() // Flatten nested arrays
			.filter((gift) => gift.status === 'Open');

		if (openGifts.length > 0) {
			removeThisRequest.classList.remove('hidden');

			allGifts = openGifts;
			renderNewGiftListeners();
			getNextCertNumber();

			// Extract unique recipients
			const uniqueRecipients = new Set(allGifts.map((gift) => gift.receiver));

			// Fire events for each unique recipient
			uniqueRecipients.forEach((recipientId) => {
				let recip = capTableData.find((u) => u.userid == recipientId);
				if (!recip) {
					if (itsTreasury(recipientId, '')) {
						recip = {};
						recip.userid = recipientId;
						recip.user_name = clientData.treasury_account?.split('|')[0]?.trim() || 'Treasury';
					} else {
						alert('⛔ Missing recipient account: ' + recipientId + '.\n\n⚠️ Please add this account to the cap table before continuing.');
						return;
					}
				}
				if (!giftRecipients.find((r) => r.userid == recipientId)) giftRecipients.push(recip);

				renderGiftRecipients();
				renderNewGiftListeners();
				buildSelectRecipientOptions();
				document.getElementById(recip.userid + 'AddGift').dispatchEvent(new Event('click'));
			});

			// Adjust balances based on gifts
			allGifts.forEach((g) => adjustBalances(Number(g.quantity), g.receiver, g.tranche));

			// Add event listeners for request removal
			listen(document.getElementById('removeThisRequest'), 'click', function (e) {
				M.Modal.getInstance(confirmRequestRemove).open();
			});
			listen(document.getElementById('removeRequestBtn'), 'click', function (e) {
				M.Modal.getInstance(viewGiftTransferModal).close();
				M.Modal.getInstance(confirmRequestRemove).close();
				M.Modal.getInstance(statementWaitState).open();
				clearPendingRequests('Removed');
			});
		} else {
			removeThisRequest?.classList.add('hidden');
		}
	}

	let labelBB = clientData.language?.label_buy_back?.includes('Redemption') ? 'Redemption' : 'Buy-Back';

	if (BUY_BACK) {
		let treasuryAccount = clientData.treasury_account;
		if (!treasuryAccount) treasuryAccount = 'Treasury | temp_treasury@' + client + '.com';
		holders.push(treasuryAccount);
		treasuryId = treasuryAccount.split('|')[1].trim();
		let treasuryName = treasuryAccount.split('|')[0].trim();
		members = [{ userid: treasuryId, user_name: treasuryName, clients: { [client]: { shares: {} } } }];
		buildSelectRecipientOptions();
		selectRecipient.value = treasuryAccount;
		selectRecipient.dispatchEvent(new Event('change'));

		// now do these
		addRecipientBtn.classList.add('hidden');
		totalCurrentValueDiv.classList.remove('hidden');
		previewGiftTransfer.innerHTML = 'Request This ' + labelBB;
		document.getElementById('toTheseRecipientsDiv').classList.add('hidden');
		document.getElementById('requestHeadline').classList.remove('hidden');
		document.getElementById('requestInstructions').classList.remove('hidden');
		document.getElementById('taxLiabilityNotice').classList.remove('hidden');
		totalCurrentValueDiv.innerHTML = `Total Current Value${isEDM ? ` (${currentPrice('C')?.toLocaleString('en-US', usd)} per share)` : ``}:&ensp;<span id="totalCurrentValue" class="text-black"></span>`;
		const firstTranche = document.getElementById(' |' + treasuryId + '|TrancheId');
		if (firstTranche)
			setTimeout(() => {
				firstTranche.focus();
			}, 10);
	} else if (capTableData && !fromUser) {
		// <-- 2/10/2025 added !fromUser to more accurately preview user experience
		capTableData.forEach(function (i) {
			if (i.userid == giftUserId) return;
			if ((isBB && client != 'onwardfe') || i.user_name.includes('|')) holders.push(i.user_name);
			else holders.push(i.user_name + ' | ' + i.userid);
		});
		// capTableData.forEach(function (i) { if (i.userid != giftUserId) holders.push((i.clients[client].notes ? (i.clients[client].notes + "\xa0\xa0|\xa0\xa0") : "") + i.user_name); });
		// capTableData.forEach(function (i) { if (i.userid != giftUserId) holders.push(i.user_name + "\xa0\xa0|\xa0\xa0" + i.userid); });
		// capTableData.forEach(function (i) { holders.push(i.user_name); });
		holders.sort();
		buildSelectRecipientOptions();
	} else {
		// regular user needs members
		addRecipientBtn.classList.add('text-secondary');
		addRecipientBtn.style.pointerEvents = 'none';
		addRecipientBtn.innerHTML = '<i class="material-icons align-middle mb-1 mr-05">person_add</i>One moment...';
		axios
			.post(
				'https://api.nthround.io/portal/user/alias-names',
				JSON.stringify({
					eventName: 'gift-transfer.family_members',
					alias_list: [], // workspaces are trust accounts
					client: client,
					userid: authedUser,
					cookie: cookie,
				})
			)
			.then(function (res) {
				addRecipientBtn.classList.remove('text-secondary');
				addRecipientBtn.style.pointerEvents = null;
				addRecipientBtn.innerHTML = '<i class="material-icons align-middle mb-1 mr-05">person_add</i>Add Recipient';
				members = removeNthAdmins(res.data);

				// Family Filter for USV
				const FF = isUSV || client == 'onwardfe';
				const sliceFF = isUSV ? 1 : 2;
				const familyCode = userData.clients[client]?.tags?.[0]?.slice(0, sliceFF) || '';
				if (FF && fromUser && familyCode) members = members.filter((r) => r.tags?.[0]?.slice(0, sliceFF) === familyCode);

				members.forEach(function (i) {
					if (i.userid == giftUserId) return;
					if ((isBB && client != 'onwardfe') || i.user_name.includes('|')) holders.push(i.user_name);
					else holders.push(i.user_name + ' | ' + i.userid);
				});
				holders.sort();
				buildSelectRecipientOptions();
			})
			.catch(function (error) {
				console.error(error);
			});
	}

	function showTotals() {
		ttlAfter = 0;
		ttlBefore = 0;
		let totalBasisDollars = 0;
		let basisUnknown = false;
		let totalGainLoss = 0;
		let anyGainLossKnown = false;
		let len = giftTransactions.length;
		if (len < 1) return;
		const unitTotals = {}; // by denomination, so dollars and shares are never added together

		for (let i = 0; i < len; i++) {
			let row = giftTransactions[i];
			let beforeQty = Number(row.before);
			let afterQty = row.after != null ? Number(row.after) : Number(row.quantity);
			const unit = (unitTotals[getEffectiveDenomination(clientData, row.shareClass)] ||= { before: 0, after: 0, basisDollars: 0, basisUnknown: false });

			ttlBefore += beforeQty;
			ttlAfter += afterQty;
			unit.before += beforeQty;
			unit.after += afterQty;
			if (afterQty > 0) {
				let basisPerShare = isKnownNumber(row._effectiveTaxBasis) ? Number(row._effectiveTaxBasis) : null;
				if (basisPerShare === null) {
					basisUnknown = true;
					unit.basisUnknown = true;
				} else {
					totalBasisDollars += afterQty * basisPerShare;
					unit.basisDollars += afterQty * basisPerShare;
				}
			}
			// #6144: gross Gain/Loss total -- sums the same per-row estimate shown in
			// the (optional) Gain/Loss column for every currently-held (non-zero-qty) row.
			if (Number(row.quantity) !== 0) {
				const gl = computeGainLoss(row, clientData);
				if (gl) {
					totalGainLoss += gl.totalGain;
					anyGainLossKnown = true;
				}
			}
		}

		const mixedUnits = Object.keys(unitTotals).length > 1;
		const unitColumn = (pick) => mixedUnitsText(Object.fromEntries(Object.entries(unitTotals).map(([d, u]) => [d, pick(u)])), stringOptions, '<br>');
		document.getElementById('totalBefore').innerHTML = mixedUnits ? unitColumn((u) => u.before) : ttlBefore.toLocaleString('en-US', stringOptions);
		document.getElementById('totalAfter').innerHTML = mixedUnits ? unitColumn((u) => u.after) : ttlAfter.toLocaleString('en-US', stringOptions);
		document.getElementById('avgCostBasis').innerHTML = mixedUnits
			? DENOMINATION_ORDER.filter((d) => d in unitTotals && Math.abs(unitTotals[d].after) > 0.005)
					.map((d) =>
						unitTotals[d].basisUnknown || unitTotals[d].after <= 0
							? 'n.a.'
							: (unitTotals[d].basisDollars / unitTotals[d].after).toLocaleString('en-US', usd)
					)
					.join('<br>')
			: basisUnknown || ttlAfter <= 0
				? 'n.a.'
				: (totalBasisDollars / ttlAfter).toLocaleString('en-US', usd);
		const totalGainLossEl = document.getElementById('totalGainLoss');
		if (totalGainLossEl) {
			totalGainLossEl.innerHTML = !anyGainLossKnown
				? 'n.a.'
				: `<span class="${totalGainLoss >= 0 ? 'text-primary' : 'text-error'}">${totalGainLoss.toLocaleString('en-US', usd)}</span>`;
		}
	}

	function updateRecipientSearchHighlight() {
		const resultsDiv = document.getElementById('recipientSearchResults');
		if (!resultsDiv) return;

		const rows = resultsDiv.querySelectorAll('.recipient-search-row');
		rows.forEach((row, idx) => {
			row.classList.toggle('active', idx === recipientSearchIndex);
		});

		const activeRow = rows[recipientSearchIndex];
		if (activeRow) {
			activeRow.scrollIntoView({ block: 'nearest' });
		}
	}

	function closeRecipientSearchResults() {
		const resultsDiv = document.getElementById('recipientSearchResults');
		if (!resultsDiv) return;
		resultsDiv.classList.add('hidden');
		recipientSearchIndex = -1;
	}

	function selectRecipientSearchMatch(idx) {
		const match = recipientSearchMatches[idx];
		if (!match) return;

		let value;
		if (match.name === 'Issuer') value = 'Issuer';
		else if (isBB) value = match.name;
		else if (match.userid) value = match.name + ' | ' + match.userid;
		else value = match.name;

		selectRecipient.value = value;
		selectRecipient.dispatchEvent(new Event('change'));

		const input = document.getElementById('recipientSearchInput');
		if (input) input.value = '';

		closeRecipientSearchResults();
	}

	function buildRecipientSearchDataset() {
		recipientSearchData = [];

		const selectedRecipientIds = new Set(giftRecipients.map((r) => r.userid).filter(Boolean));

		let sourceRecipients = Array.isArray(members) && members.length ? members : Array.isArray(capTableData) ? capTableData : [];

		sourceRecipients.forEach((m) => {
			if (!m || m.userid === giftUserId) return;
			if (selectedRecipientIds.has(m.userid)) return;

			// Holders can't search other holders by ID number (#862 item 53).
			const idNumber = isAdmin ? m.clients?.[client]?.idNumber || '' : '';
			const name = m.user_name || '';
			const userid = m.userid || '';

			if (!name && !userid) return;

			recipientSearchData.push({
				idNumber,
				name,
				userid,
				searchText: `${idNumber} ${name} ${userid}`.toLowerCase(),
			});
		});

		recipientSearchData.unshift({
			idNumber: '',
			name: 'Issuer',
			userid: 'Cancellation or Redemption',
			searchText: 'issuer cancellation redemption',
		});
	}

	function initializeRecipientPickerShell() {
		const recipientPicker = document.getElementById('recipientPicker');
		const recipientSearchInput = document.getElementById('recipientSearchInput');
		const recipientSearchResults = document.getElementById('recipientSearchResults');

		if (!recipientPicker || !recipientSearchInput || !recipientSearchResults) return;

		recipientPicker.classList.remove('hidden');
		recipientSearchInput.value = '';
		recipientSearchResults.innerHTML = `
			<div class="recipient-search-empty text-xs text-secondary p-1">
				Start typing to search recipients.
			</div>
		`;
		recipientSearchResults.classList.add('hidden');

		if (!recipientPickerBound) {
			function openRecipientSearchResults() {
				const q = recipientSearchInput.value.trim().toLowerCase();
				if (q) {
					const matches = recipientSearchData.filter((r) => r.searchText.includes(q));
					renderRecipientSearchResults(matches, 25);
				} else {
					recipientSearchMatches = [];
					recipientSearchIndex = -1;
					recipientSearchResults.innerHTML = `
						<div class="recipient-search-empty text-xs text-secondary p-1">
							Start typing (Name, Email,or Id Number) to list matching potential recipients
						</div>
					`;
					recipientSearchResults.classList.remove('hidden');
				}

				recipientSearchIndex = -1;
				if (recipientSearchMatches.length) recipientSearchResults.classList.remove('hidden');
			}

			listen(recipientSearchInput, 'input', function () {
				const q = this.value.trim().toLowerCase();

				if (!q) {
					recipientSearchMatches = [];
					recipientSearchIndex = -1;
					recipientSearchResults.classList.add('hidden');
					return;
				}

				const matches = recipientSearchData.filter((r) => r.searchText.includes(q));
				renderRecipientSearchResults(matches, 25);
			});

			listen(recipientSearchInput, 'keydown', function (e) {
				const resultsOpen = !recipientSearchResults.classList.contains('hidden');

				if (e.key === 'ArrowDown') {
					if (!recipientSearchMatches.length) return;
					e.preventDefault();
					if (!resultsOpen) recipientSearchResults.classList.remove('hidden');
					recipientSearchIndex = Math.min(recipientSearchIndex + 1, recipientSearchMatches.length - 1);
					updateRecipientSearchHighlight();
					return;
				}

				if (e.key === 'ArrowUp') {
					if (!recipientSearchMatches.length) return;
					e.preventDefault();
					if (!resultsOpen) recipientSearchResults.classList.remove('hidden');
					recipientSearchIndex = Math.max(recipientSearchIndex - 1, 0);
					updateRecipientSearchHighlight();
					return;
				}

				if (e.key === 'Enter' || e.key === 'Tab') {
					if (resultsOpen && recipientSearchIndex >= 0 && recipientSearchMatches.length) {
						e.preventDefault();
						selectRecipientSearchMatch(recipientSearchIndex);
					}
					return;
				}

				if (e.key === 'Escape') {
					e.preventDefault();
					closeRecipientSearchResults();
					return;
				}
			});

			listen(recipientSearchInput, 'focus', function () {
				openRecipientSearchResults();
			});

			listen(recipientSearchInput, 'click', function () {
				openRecipientSearchResults();
			});

			listen(recipientSearchInput, 'blur', function () {
				setTimeout(() => {
					closeRecipientSearchResults();
				}, 150);
			});

			recipientPickerBound = true;
		}
	}

	function buildSelectRecipientOptions() {
		let recips = [];
		selectRecipient.options.length = 1;
		giftRecipients.forEach((r) => recips.push(r.user_name));
		selectRecipient.options[1] = new Option('Issuer | Cancellation or Redemption', 'Issuer');
		holders.forEach((h) => {
			if (!recips.includes(h)) selectRecipient.options[selectRecipient.options.length] = new Option(h, h);
		});
		let options = {
			dropdownOptions: {
				container: 'authed',
				coverTrigger: false,
			},
		};
		let selects = document.querySelectorAll('select');
		let instances = M.FormSelect.init(selects, options);
		selectRecipient.parentElement.style.display = 'none';
		const oldSelectWrapper = selectRecipientDiv.querySelector('.select-wrapper');
		if (oldSelectWrapper) oldSelectWrapper.classList.add('hidden');

		initializeRecipientPickerShell();
		buildRecipientSearchDataset();
	}

	function renderRecipientSearchResults(results, maxRows = 25) {
		const resultsDiv = document.getElementById('recipientSearchResults');
		if (!resultsDiv) return;
		resultsDiv.innerHTML = '';
		const sortedResults = results.slice().sort((a, b) => {
			if (a.name === 'Issuer') return -1;
			if (b.name === 'Issuer') return 1;
			return String(a.name || '').localeCompare(String(b.name || ''));
		});
		const visibleResults = sortedResults.slice(0, maxRows);
		recipientSearchMatches = visibleResults.slice();
		recipientSearchIndex = recipientSearchMatches.length ? 0 : -1;
		if (!visibleResults.length) {
			resultsDiv.innerHTML = `
				<div class="recipient-search-empty text-xs text-secondary p-1">
					No recipients found
				</div>
			`;
			resultsDiv.classList.remove('hidden');
			return;
		}

		visibleResults.forEach((r, idx) => {
			const row = document.createElement('div');
			row.className = 'recipient-search-row' + (idx === recipientSearchIndex ? ' active' : '');
			row.dataset.index = idx;
			row.innerHTML = `
				<div class="recipient-row-main">
					<span class="recipient-id">${safeHtml(r.idNumber || '')}</span>
					<span class="recipient-name">${r.name}</span>
				</div>
				<div class="recipient-row-sub">${r.userid}</div>
			`;

			row.addEventListener('mousedown', function (e) {
				e.preventDefault();
				selectRecipientSearchMatch(idx);
			});

			resultsDiv.appendChild(row);
		});

		resultsDiv.classList.remove('hidden');
	}

	function renderGiftRecipients() {
		let recipientsMarkup = '';
		let emptyState = `
			<div class="bg-grey-lightest rounded border-grey grey-text center-align p-1 mt-4">
				Recipients will appear here.
				<!-- <p class="mt-0"><a href="#addRecipient" class="modal-trigger">Add Recipient</a></p> -->
			</div>
		`;

		let index = giftRecipients.length - 1;
		if (index < 0) {
			recipientsMarkup = emptyState;
		} else {
			let recipOwned = 0;
			let recip = giftRecipients[index];
			let recipClientData = recip.clients ? recip.clients[client] : recip;
			let recipNotes = recip.clients ? recip.clients[client].notes : recip.notes;
			classes.forEach((c) => (recipOwned += Number(recipClientData.shares?.[c]?.owned || '0')));
			giftRecipients[index].before = recipOwned;
			giftRecipients[index].after = recipOwned;

			recipientsMarkup = `
				<div id="${recip.userid}|Div" class="recip-div card px-4 mt-4 mb-0 rounded">
					${
						BUY_BACK
							? ``
							: `
						<div class="row center-align mt-0 mb-0">
							<!-- <div class="col s12 m6 left-align mt-2" title="${
								recip.friendly_name ? recip.userid + ' [' + recip.friendly_name.replace(/_/g, ' ') + ']' : recip.userid
							}">${(recipNotes ? recipNotes + '\xa0\xa0|\xa0\xa0' : '') + recip.user_name}</div> -->
							<div class="col s12 m6 left-align mt-2" title="${recip.userid}">${safeHtml(recip.user_name)}</div>
							<div class="text-sm text-secondary col s12 m5 mt-2" style="display:flex;flex-wrap:nowrap;align-items:center;justify-content:space-between;">
								<label style="display:inline-flex;align-items:center;flex-shrink:0;white-space:nowrap;transform: scale(0.8);" title="Check only if this is an actual cash sale, not a gift/transfer -- Share Price will mirror Tax Basis for every gift/transfer to this recipient while checked.">
									<input type="checkbox" class="cash-sale-checkbox" id="${recip.userid}|CashSale" data-key="${recip.userid}" ${cashSaleRecipients[recip.userid] ? 'checked' : ''} />
									<span class="text-xs">Cash Sale</span>
								</label>
								${
									recip.clients // admins only: a holder doesn't see another holder's balance
										? `<span style="white-space:nowrap;">Shares Owned:&emsp;<span class="text-primary">${recip.before.toLocaleString()}</span>&nbsp;Before&emsp;
								<span id="${recip.userid}|After" class="text-primary">${recip.after.toLocaleString()}</span>&nbsp;After</span>`
										: ''
								}</div>
							<a href="#confirmRecipientRemove" class="col s12 m1 modal-trigger remove-gift-recipient waves-effect waves-red btn-flat red-text text-xxs right" data-key="${recip.userid}">Remove</a>
						</div>
					`
					}
					<div id="${recip.userid}Gifts"></div>
					<div class="flex center-align mb-0 hidden">
						<p class="text-xs mt-2 mb-0 left"><a id="${recip.userid}AddGift" href="#" class="add-recip text-sm" data-key="${recip.userid}">+ Add Gift/Transfer</a></p>
						<a href="#confirmRecipientRemove" class="col s12 m1 modal-trigger remove-gift-recipientHIDDEN waves-effect waves-red btn-flat p-0 mt-2 mb-0 right" data-key="${recip.userid}">Remove</a>
					</div>
				</div>
			`;
		}

		viewRecipients = document.getElementById('viewRecipients');
		// Assign when empty so reopen can't stack placeholders.
		if (index <= 0) viewRecipients.innerHTML = recipientsMarkup;
		else viewRecipients.innerHTML += recipientsMarkup;
		// viewRecipients.addEventListener('click', e => addRecipientBtn.dispatchEvent(new Event('click')));
	}

	function renderNewGiftListeners() {
		document.querySelectorAll('.add-recip').forEach((recip) => {
			// console.log("ADDED add-recip", recip);
			recip.addEventListener('click', function (e) {
				currentTranche = '';
				recipientId = e.target.dataset.key;
				let recipient = capTableData?.find((u) => u.userid == recipientId) || members?.find((u) => u.userid == recipientId); // for regular users, not admins
				recipientName = recipientId == 'issuer' ? clientData.company_name : recipient ? recipient.user_name : 'UNKNOWN';

				theseGifts = {};
				let giftArray = allGifts.filter((g) => g.receiver == recipientId);
				giftArray.forEach((g) => (theseGifts[g.tranche] = g));
				// theseGifts[" "] = ["","",,];
				if (!theseGifts.length) theseGifts[' '] = ['', '', ,];
				gifts = Object.keys(theseGifts);

				const HIDE_COST_BASIS = false; //BUY_BACK && isEDM;	<-- EO wants this displayed now (6-10-2026)
				const ce = BUY_BACK ? '' : 'contenteditable';
				const basisHeader = basisHeaderContent(!!cashSaleRecipients[recipientId]);
				const anyGiftCustomCols = listTransactionCustomColumns(clientData).filter(usesTransactionCustomColumnKeyStorage).length > 0;
				const baseColCount = 1 + (CLASS_COLUMN ? 1 : 0) + 1 + 1 + 1 + (HIDE_COST_BASIS ? 0 : 1) + 1;
				let giftTable = document.getElementById(recipientId + 'Gifts');
				let giftTableMarkup = `
					${
						gifts.length
							? `
					<div class="table-wrap mb-0">
						<div class="sm-hz-scroll-no-margin">
							<table id="recipientsGiftTable" class="recip-table highlight table-content">
								<thead>
									<tr class="text-xs">
										${anyGiftCustomCols ? `<th class="custom-toggle-col"></th>` : ``}
										<th class="center-align p-05">Tranche</th>
										${CLASS_COLUMN ? `<th class="center-align -05">Class</th>` : ``}
										<th class="center-align p-05">Date</th>
										<!-- <th class="sm-fixed-left p-05">Notes</th> -->
										<th class="sm-fixed-left p-05">Description</th>
										<th id="qtyHeader|${recipientId}" class="center-align p-05">Quantity</th>
										${HIDE_COST_BASIS ? `` : `<th id="basisHeader|${recipientId}" class="center-align p-05" title="${safeAttr(basisHeader.title)}">${basisHeader.label}</th>`}
										<th class="center-align p-05">Current Value</th>
									</tr>
								</thead>
								<tbody>
								${gifts
									.map((gift, index) => {
										// The not-yet-designated placeholder row (theseGifts[' '] = ['','',,])
										// has no tranche/shareClass yet -- nothing to attach a custom value to
										// until commitTranche() resolves it into a real gift, so skip the toggle.
										const isPlaceholderRow = Array.isArray(theseGifts[gift]);
										const giftCustomCols = isPlaceholderRow ? [] : applicableGiftCustomColumns(clientData, theseGifts[gift].shareClass);
										const toggleId = 'cf-' + gift + '-' + recipientId;
										const hasCustomValue = giftCustomCols.some(
											(col) => theseGifts[gift][col.key] !== undefined && theseGifts[gift][col.key] !== ''
										);
										return `
									<tr id="${gift + '|' + recipientId + '|'}Tranche" class="add-tranche">
										${
											anyGiftCustomCols
												? `
										<td class="custom-toggle-cell center-align p-05">
											${
												giftCustomCols.length
													? `<button type="button" id="${toggleId}-btn" class="custom-field-toggle${hasCustomValue ? ' has-value' : ''}" data-target="${toggleId}" title="Custom Fields">+</button>`
													: ``
											}
										</td>
										`
												: ``
										}
										<td id="${gift + '|' + recipientId + '|'}TrancheId"
											class="new-tranche text-xs center-align font-bold p-05" contenteditable style="${index === gifts.length - 1 ? 'outline: 1px solid rgba(0,0,0,0.1); outline-offset: -1px; background-color: #ffffcc;' : ''}">
											${gift}
										</td>
										${
											CLASS_COLUMN
												? `
										<td class="text-xs center-align p-05">
											${theseGifts[gift].shareClass ? theseGifts[gift].shareClass : ''}
										</td>
										`
												: ``
										}
										<td id="editableDate-${gift}" class="gift-date text-xs center-align p-05" ${ce} ${isAdmin ? '' : 'title="Must be within the next 60 days."'}>
											${
												theseGifts[gift].date
													? moment
															.unix(theseGifts[gift].date / 1000)
															.utc()
															.format('MMM D, YYYY')
													: ''
											}
										</td>
										<!-- <td id="editablePre-${gift + '|' + recipientId}" class="select-notes text-xs sm-fixed-left p-05" ${ce}>
											${theseGifts[gift].pre ? theseGifts[gift].pre : ''}
										</td> -->
										<td id="editableNotes-${gift}" class="text-xs sm-fixed-left p-05" title="${gift}" ${ce}>
											${safeHtml(theseGifts[gift].notes ? theseGifts[gift].notes.replace(/_/g, ' ') : '')}
										</td>
										<td id="${recipientId + '|' + gift}-tranche" class="gift-qty text-xs center-align p-05" contenteditable>
											${theseGifts[gift].quantity ? Number(theseGifts[gift].quantity).toLocaleString('en-US', stringOptions) : ''}
										</td>
										${
											HIDE_COST_BASIS
												? ``
												: `
											<td id="editableBasis-${gift}" class="select-gift text-xs center-align p-05" ${isAdmin ? ce : ''}>
											${
												isKnownNumber(theseGifts[gift].taxBasis)
													? Number(String(theseGifts[gift].taxBasis).replace(/[^-.\d]/g, '')).toLocaleString('en-US', usp)
													: isNA(theseGifts[gift].taxBasis)
														? theseGifts[gift].taxBasis
														: gift.trim()
															? 'n.a.'
															: ''
											}
										</td>
										`
										}
										<td id="currentValue-${gift + '|' + recipientId}" class="text-xs center-align p-05">
											${!theseGifts[gift].quantity ? '' : isNaN(currentPrice(theseGifts[gift].shareClass)) ? 'n.a.' : (Number(theseGifts[gift].quantity) * currentPrice(theseGifts[gift].shareClass)).toLocaleString('en-US', usd)}
										</td>
									</tr>
									${
										giftCustomCols.length
											? `
									<tr class="custom-fields-row" id="${toggleId}">
										<td colspan="${baseColCount + 1}">
											<div class="custom-fields-panel">
												${giftCustomCols
													.map(
														(col) => `
												<div class="custom-field">
													<label>${escapeGiftFieldAttr(col.label || col.key)}</label>
													<input type="text" class="custom-field-input" data-gift="${escapeGiftFieldAttr(gift)}" data-recipient="${escapeGiftFieldAttr(recipientId)}" data-col-key="${escapeGiftFieldAttr(col.key)}" value="${escapeGiftFieldAttr(theseGifts[gift][col.key] || '')}">
												</div>
												`
													)
													.join('')}
											</div>
										</td>
									</tr>
									`
											: ``
									}
									`;
									})
									.join('')}
								</tbody>
							</table>
						</div>
					</div>
					`
							: `
					<div class="bg-grey-lightest rounded border-grey grey-text center-align p-2">
						No gifts found
					</div>
					`
					}
				`;

				giftTable.innerHTML = giftTableMarkup;

				// scroll to bottom
				viewGiftTransferModal.scrollTop = viewGiftTransferModal.scrollHeight;

				renderTrancheListeners();

				let newTranche = document.getElementById(' |' + recipientId + '|Tranche');
				if (gifts.length > 1) newTranche.focus();
				// Should automatically inject new tranche if only one possibility, but...
				// else if (historicHoldings.length == 1) {
				// 	newTranche.children[0].innerHTML = "A";
				// 	let thisTranche = document.querySelectorAll('.new-tranche')[0];
				// 	thisTranche.dispatchEvent(new Event('keyup'));
				// }
				// .new-tranche, not children[0] -- the optional custom-toggle-cell can be the row's first <td>.
				else (newTranche.querySelector('.new-tranche') || newTranche).focus();
			});
		});

		// document.querySelectorAll('.recip-div').forEach(table => {
		// 	console.log("ADDED recip-div", table);
		// 	table.addEventListener('focusin', function(e) {
		// 		recipientId = e.target.id.split('|')[0];
		// 		console.log("table focus, recipientId = " + recipientId);
		// 	});
		// });

		// Cash Sale: all-or-nothing per recipient -- checking mirrors Share Price to
		// Tax Basis for every existing gift of theirs; unchecking reverts to 0.
		// commitTranche() and the 'editableBasis' handler below both consult
		// cashSaleRecipients so this stays in sync going forward too.
		document.querySelectorAll('.cash-sale-checkbox').forEach((checkbox) => {
			checkbox.addEventListener('change', function (e) {
				const thisRecipientId = e.target.dataset.key;
				cashSaleRecipients[thisRecipientId] = e.target.checked;

				// Relabel the Tax Basis/Share Price header live if this recipient's
				// gift table happens to be open right now (see basisHeaderContent()).
				const basisHeaderEl = document.getElementById('basisHeader|' + thisRecipientId);
				if (basisHeaderEl) {
					const { label, title } = basisHeaderContent(e.target.checked);
					basisHeaderEl.innerHTML = label;
					basisHeaderEl.title = title;
				}

				allGifts
					.filter((g) => g.receiver == thisRecipientId)
					.forEach((g) => {
						g.sharePrice = e.target.checked && isKnownNumber(g.taxBasis) ? Number(g.taxBasis).toString() : '0';
					});
			});
		});

		document.querySelectorAll('.remove-gift-recipient').forEach((remove) => {
			// console.log("ADDED remove-gift-recipient", remove);
			remove.addEventListener('click', function (e) {
				e.preventDefault();
				removeGiftId = e.target.getAttribute('data-key');
				let recipientRemoveName = document.getElementById('recipientRemoveName');
				recipientRemoveName.innerHTML = e.target.parentElement.innerText.split('Shares Owned:')[0].trim();
			});
		});

		function renderTrancheListeners() {
			document.querySelectorAll('.add-tranche').forEach((tranche) => {
				// .new-tranche, not children[0] -- the optional custom-toggle-cell can be the row's first <td>.
				const trancheCell = tranche.querySelector('.new-tranche') || tranche;
				const rowRecipientId = tranche.id.split('|')[1];

				function getAvailableTranches() {
					let priorTranches = priorTransfers.map((t) => t.tranche);
					let availableTranches = giftTransactions.filter((t) => Number(t.quantity) != 0).map((t) => t.tranche);

					availableTranches = availableTranches.filter((a) => {
						let thisHH = giftTransactions.find((h) => h.tranche == a);
						let balance = thisHH ? Number(thisHH.quantity) : 0;
						return !priorTranches.includes(a) || balance > 0;
					});

					return availableTranches;
				}

				function getApplicableTranches(typed) {
					return getAvailableTranches().filter((a) => a.startsWith(typed));
				}

				async function commitTranche(resolvedTranche) {
					let availableTranches = getAvailableTranches();
					let gifts = allGifts.filter((g) => g.receiver == rowRecipientId).map((g) => g.tranche);
					gifts.push(' ');

					if (gifts.includes(resolvedTranche) || !availableTranches.includes(resolvedTranche)) {
						trancheCell.innerHTML = '';
						return false;
					}

					trancheCell.innerHTML = resolvedTranche;

					gifts.splice(-1, 0, resolvedTranche);
					let thisTrans = giftTransactions.find((t) => t.tranche == resolvedTranche);
					if (!thisTrans) return false;

					let qtyTrans = thisTrans.quantity;
					let qtyAfterElem = document.getElementById('quantity' + resolvedTranche);
					let qtyAfter = qtyAfterElem ? qtyAfterElem.innerHTML : '';
					if (qtyAfter) qtyTrans = Number(qtyAfter.replace(/[^-.\d]/g, '')).toString();

					// Use prior quantity rather than remaining balance, if available and new recipient
					if (allGifts.length && rowRecipientId != allGifts[allGifts.length - 1].receiver) {
						let priorQty = Number(allGifts[allGifts.length - 1].quantity);
						let availQty = Number(qtyTrans);
						if (availQty >= priorQty) qtyTrans = priorQty.toString();
					}

					let newDate = allGifts.length ? allGifts[allGifts.length - 1].date : Date.now().toString();
					let newNotes = labelBB + ' (' + thisTrans.notes.trim() + ' dated ' + moment.unix(Number(thisTrans.date)).utc().format('M/D/YY') + ')';
					if (thisTrans.certNumber) newNotes = labelBB + ' (' + thisTrans.certNumber + ')';

					if (!BUY_BACK) {
						if (rowRecipientId != 'issuer') {
							if (CERT_COLUMN) {
								// #6440: some entry paths reach here before getNextCertNumber() ran.
								if (!nextCertNum) await getNextCertNumber();
								const transClass = thisTrans.shareClass;
								const nextCert = nextCertNum[transClass];
								const lastCert = lastCertNum[transClass];
								incrementNextCertNumber(transClass);
								(certIssueLog[transClass] ||= []).push({ receiver: rowRecipientId, tranche: resolvedTranche });
								const sourceCert = thisTrans.certNumber ? thisTrans.certNumber.replace(/^0+/, '') : '';
								newNotes = !nextCert
									? 'Transfer'
									: sourceCert
										? resolveDescriptionTemplate(
												clientData,
												'gift_cert_transfer',
												{ nextCert, sourceCert },
												'Transfer ({nextCert} from {sourceCert})'
											)
										: resolveDescriptionTemplate(clientData, 'gift_cert_transfer_no_source', { nextCert }, 'Transfer ({nextCert})');
								const lastCertNumber = document.getElementById('lastCertNumber');
								const originalLastCert = originalLastCertNum?.[transClass] || lastCertNum[transClass];
								lastCertNumber.innerHTML = lastCert
									? 'Last issued certificate number: ' +
										originalLastCert +
										'&emsp;&emsp;Next available certificate number: ' +
										nextCertNum[transClass]
									: '';
								lastCertNumber.classList.remove('hidden');
							} else {
								newNotes = resolveDescriptionTemplate(
									clientData,
									'gift_notes_transfer',
									{ sourceNotes: thisTrans.notes.trim(), sourceDate: moment.unix(Number(thisTrans.date)).utc().format('M/D/YY') },
									'Transfer ({sourceNotes} dated {sourceDate})'
								);
							}
						}
						if (isBB) {
							let firstWord = thisTrans.notes.split(' ')[0];
							let dateStart =
								Number.isInteger(Date.parse(firstWord)) ||
								['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].some((m) =>
									firstWord.toLowerCase().startsWith(m)
								);
							newNotes = resolveDescriptionTemplate(
								clientData,
								'gift_from_buyback',
								{
									senderName: giftUserData.user_name.includes('|')
										? giftUserData.user_name.split('|')[1].trim()
										: giftUserData.user_name.trim(),
									sourceDate: moment.unix(Number(thisTrans.date)).utc().format('M/D/YY'),
									dateSeparator: dateStart ? '| ' : '',
									sourceNotes: thisTrans.notes.trim(),
								},
								'Gift from {senderName} - {sourceDate} {dateSeparator}{sourceNotes}'
							);
						}
					}

					let effectiveTaxBasis = '';
					if (isKnownNumber(thisTrans.taxBasis)) effectiveTaxBasis = Number(thisTrans.taxBasis).toString();
					else if (isKnownNumber(thisTrans._effectiveTaxBasis)) effectiveTaxBasis = Number(thisTrans._effectiveTaxBasis).toString();

					// Cash Sale: a new tranche for a recipient whose box is already checked
					// starts with Share Price mirroring this tranche's Tax Basis (same rule
					// as the checkbox/editableBasis handlers); otherwise the normal 0 default.
					const cashSaleDefaultPrice = cashSaleRecipients[rowRecipientId] && isKnownNumber(effectiveTaxBasis) ? effectiveTaxBasis : '0';

					theseGifts[resolvedTranche] = {
						tranche: resolvedTranche,
						shareClass: thisTrans.shareClass,
						securityType: thisTrans.securityType,
						date: newDate,
						notes: newNotes,
						sender: shareholder,
						senderName: nameWithOverride(shareholder),
						receiver: rowRecipientId,
						receiverName: recipientName,
						quantity: qtyTrans,
						sharePrice: BUY_BACK ? currentPrice(thisTrans.shareClass).toString() : cashSaleDefaultPrice,
						taxBasis: effectiveTaxBasis,

						// NEW
						certNumber: thisTrans.certNumber || '',
						sourceCert: thisTrans.certNumber || '',
						sourceNotes: thisTrans.notes || '',
						sourceDate: String(thisTrans.date ?? ''), // stored as text, never a DynamoDB Number
					};

					// EXTERNAL_DATA admin views don't see the whole class in clientData.equity
					// (see getExternalClassTransactions) -- fetch it when next_cert_number needs it.
					// Session-suggested certs (customCertLog) aren't in either source yet, so
					// they're appended as extra candidates -- otherwise every recipient added
					// in one sitting would be offered the same "next" number, not sequential ones.
					const cls = thisTrans.shareClass;
					let classTransactions;
					if (applicableGiftCustomColumns(clientData, cls).some((c) => c.autoPopulate === 'next_cert_number')) {
						classTransactions = EXTERNAL_DATA ? await getExternalClassTransactions(clientData, cls) : clientData.equity?.[cls]?.transactions || [];
						// '#' prefix is harmless for Sheetz (optional there) and required for the
						// generic cert pattern, so it works either way when this gets re-scanned.
						classTransactions = classTransactions.concat((customCertLog[cls] || []).map((entry) => '#' + entry.value));
					}

					const customColAssignments = applyGiftCustomColumnDefaults(
						theseGifts[resolvedTranche],
						clientData,
						cls,
						newDate,
						thisTrans,
						{
							// Sheetz's real historical notes, not this file's own generated
							// "Transfer (X from Y)" text (that's parseCertificateRefs).
							source_notes_cert: (txn) => parseSheetzReceiverCertFromNotes(txn?.notes),
						},
						classTransactions
					);
					customColAssignments
						.filter((a) => a.autoPopulate === 'next_cert_number')
						.forEach((a) =>
							(customCertLog[cls] ||= []).push({ receiver: rowRecipientId, tranche: resolvedTranche, colKey: a.key, value: a.value })
						);

					allGifts.push(theseGifts[resolvedTranche]);
					updateConsolidatedTransferCheckbox();
					previewGiftTransfer.classList.remove('disabled');
					adjustBalances(Number(qtyTrans.replace(/[^-.\d]/g, '')), rowRecipientId, resolvedTranche);
					document.getElementById(rowRecipientId + 'AddGift').dispatchEvent(new Event('click'));
					if (isVERM) checkVermeerRule(rowRecipientId);
					return true;
				}

				tranche.addEventListener('focusin', function () {
					currentTranche = trancheCell.innerText.trim().toUpperCase();
					recipientId = rowRecipientId;
				});

				trancheCell.addEventListener('keydown', function (e) {
					let typed = trancheCell.innerText.trim().toUpperCase();
					let availableTranches = getAvailableTranches();
					let applicableTranches = getApplicableTranches(typed);

					if (e.key === 'Tab' || e.key === 'Enter') {
						e.preventDefault();

						if (!typed) return;

						// Prefer exact typed tranche, so "A" wins over AA/AB/...
						if (availableTranches.includes(typed)) {
							commitTranche(typed);
							return;
						}

						// Otherwise accept a unique prefix match
						if (applicableTranches.length === 1) {
							commitTranche(applicableTranches[0]);
							return;
						}
					}
				});

				tranche.addEventListener('keyup', function (e) {
					const isTrancheInput = e.target === trancheCell;
					const onTarget = e.target?.id?.endsWith('|TrancheId') ?? false;
					let thisTranche = trancheCell.innerText.trim().toUpperCase();

					recipientId = rowRecipientId;

					// Ignore bubbled tranche logic from non-tranche cells
					if (!isTrancheInput && onTarget) return;

					// Clear any pending auto-commit for this row
					const timerKey = tranche.id;
					if (trancheAutoTimers[timerKey]) {
						clearTimeout(trancheAutoTimers[timerKey]);
						delete trancheAutoTimers[timerKey];
					}

					if (!thisTranche && !onTarget) {
						e.target.innerText = '';
						return;
					}

					if (!thisTranche) {
						let thisQty = document.getElementById(rowRecipientId + '|' + currentTranche + '-tranche');
						thisQty = thisQty ? thisQty.innerText : null;
						if (thisQty) {
							adjustBalances(-Number(thisQty.replace(/[^-.\d]/g, '')), rowRecipientId, currentTranche);
							allGifts = allGifts.filter((g) => g.receiver != rowRecipientId || g.tranche != currentTranche);
							updateConsolidatedTransferCheckbox();
							document.getElementById(rowRecipientId + 'AddGift').dispatchEvent(new Event('click'));
							renderNewGiftListeners();
							if (isVERM) checkVermeerRule(rowRecipientId);
						}
						return;
					}

					if (onTarget) {
						let availableTranches = getAvailableTranches();
						let applicableTranches = getApplicableTranches(thisTranche);

						if (applicableTranches.length == 1) thisTranche = applicableTranches[0];
						else if (applicableTranches.length > 1 && e.key != 'Tab' && e.key != 'Enter') {
							trancheAutoTimers[timerKey] = setTimeout(() => {
								let pausedTranche = trancheCell.innerText.trim().toUpperCase();
								if (!pausedTranche) return;
								if (getAvailableTranches().includes(pausedTranche)) commitTranche(pausedTranche);
							}, 1000);
							return;
						}

						// For plain typing, auto-commit only when there is exactly one match
						if (availableTranches.includes(thisTranche) && applicableTranches.length == 1) {
							commitTranche(thisTranche);
						}
					} else {
						let i = allGifts.findIndex((g) => g.receiver == rowRecipientId && g.tranche == thisTranche);
						if (i < 0) return;

						switch (e.target.id.split('-')[0]) {
							case 'editableDate':
								allGifts[i].date = Date.parse(e.target.innerText).toString();
								break;
							// case 'editablePre': allGifts[i].pre = e.target.innerText; break;
							case 'editableNotes':
								allGifts[i].notes = e.target.innerText;
								break;
							case 'editableBasis':
								{
									const rawBasis = e.target.innerText.replace(/[^-.\d]/g, '');
									allGifts[i].taxBasis = rawBasis;
									// Cash Sale: mirror Share Price to Tax Basis live on every keystroke;
									// falls back to 0 mid-edit (e.g. "-" or "") before it's a real number.
									if (cashSaleRecipients[rowRecipientId]) {
										allGifts[i].sharePrice = isKnownNumber(rawBasis) ? Number(rawBasis).toString() : '0';
									}
								}
								break;
						}
					}
				});
			});

			document.querySelectorAll('.gift-date').forEach((dateElem) => {
				dateElem.addEventListener('blur', function () {
					const rawText = this.innerText.trim();

					let parsed = moment(rawText, ['MMMM D, YYYY', 'MMM D, YYYY', 'M/D/YYYY', 'M-D-YYYY', 'YYYY-MM-DD'], true);

					const today = moment.utc().startOf('day');
					const maxDate = moment.utc().add(60, 'days').startOf('day');

					// Replace junk or empty with today
					if (!parsed.isValid()) parsed = today;

					// Clamp to range
					if (!isAdmin) {
						if (parsed.isBefore(today)) parsed = today;
						if (parsed.isAfter(maxDate)) parsed = maxDate;
					}

					// Update display
					this.innerText = parsed.format('MMMM D, YYYY');
				});
			});

			let origQuantity;
			let newQuantity;
			document.querySelectorAll('.gift-qty').forEach((qty) => {
				// renderTrancheListeners() re-scans ALL .gift-qty cells every call, not just
				// the current recipient's. The old guard compared unrelated things and could
				// double-bind listeners (corrupting Before/After) or skip unbound cells.
				// Track binding on the element itself so each cell gets listeners exactly once.
				if (qty.dataset.giftQtyBound) return;
				qty.dataset.giftQtyBound = '1';
				qty.addEventListener('focus', function () {
					recipientId = this.id.split('|')[0];
					let thisElem = document.getElementById(this.id);
					origQuantity = Number(thisElem.innerHTML.replace(/[^-.\d]/g, ''));
					if (origQuantity == 0) thisElem.innerHTML = '';
				});
				qty.addEventListener('blur', function () {
					let thisElem = document.getElementById(this.id);
					origQuantity = Number(thisElem.innerHTML.replace(/[^-.\d]/g, ''));
					thisElem.innerHTML = origQuantity.toLocaleString('en-US', stringOptions);
				});
				qty.addEventListener('input', function () {
					let targetTranche = this.id.split('|')[1].replace('-tranche', '').trim();
					let foundTranche = giftTransactions.find((g) => g.tranche == targetTranche);
					let maxQuantity = Number(foundTranche ? foundTranche.quantity : 0);
					let thisElem = document.getElementById(this.id);
					let newText = thisElem.innerHTML.replace(/[^-.\d]/g, '');
					// Insert the leading zero live (".99" -> "0.99"); also keeps "." from
					// parsing as NaN, which fed the bug fixed below.
					newText = newText.replace(/^(-?)\./, '$10.');
					newQuantity = Number(newText);
					thisElem.innerHTML = newQuantity || newText.includes('.') || newText == '0' ? newText : '';
					document.getSelection().collapse(this, this.childNodes.length);
					if (newQuantity > maxQuantity || newQuantity < 0) {
						// Clamp to a valid share count; reassign newText too so the
						// displayed value matches (don't leave a stale negative on screen).
						newQuantity = newQuantity < 0 ? 0 : maxQuantity;
						newText = newQuantity.toString();
						thisElem.innerHTML = newQuantity ? newText : '';
						document.getSelection().collapse(this, this.childNodes.length);
					}
					// Skip on NaN (in-progress keystroke) instead of poisoning
					// origQuantity, which was silently dropping a step of the edit
					// from the running totals.
					if (!isNaN(newQuantity)) {
						let difference = newQuantity - origQuantity;
						adjustBalances(difference, recipientId, targetTranche);
						let thisGiftIndex = allGifts.findIndex((g) => g.receiver == recipientId && g.tranche == targetTranche);
						// GH #6139: guard against stale DOM referencing a tranche no longer in allGifts (-1 index).
						if (thisGiftIndex >= 0) {
							allGifts[thisGiftIndex].quantity = newQuantity.toString();
							let correntValueElem = document.getElementById('currentValue-' + targetTranche + '|' + recipientId);
							let newPrice = currentPrice(allGifts[thisGiftIndex].shareClass);
							if (correntValueElem) correntValueElem.innerHTML = isNaN(newPrice) ? 'n.a.' : (newQuantity * newPrice).toLocaleString('en-US', usd);
						}
						origQuantity = newQuantity;
					}
					if (isVERM) checkVermeerRule(recipientId);
				});
			});

			// One expand/collapse toggle per gift row for custom columns, instead of a
			// permanent column each -- mirrors ledgers.js's New/Edit Transaction modals.
			document.querySelectorAll('.custom-field-toggle').forEach((btn) => {
				if (btn.dataset.cfBound) return;
				btn.dataset.cfBound = '1';
				btn.addEventListener('click', function (e) {
					e.preventDefault();
					const row = document.getElementById(this.dataset.target);
					if (!row) return;
					const isOpen = row.classList.toggle('open');
					this.classList.toggle('open', isOpen);
					this.textContent = isOpen ? '×' : '+';
				});
			});

			// [data-gift] keeps this scoped to the recipient table; Ready for Import's
			// inputs are [data-import-index] and bound separately below.
			document.querySelectorAll('.custom-field-input[data-gift]').forEach((input) => {
				if (input.dataset.cfBound) return;
				input.dataset.cfBound = '1';
				input.addEventListener('blur', function () {
					const gift = this.dataset.gift;
					const rid = this.dataset.recipient;
					const colKey = this.dataset.colKey;
					const i = allGifts.findIndex((g) => g.receiver == rid && g.tranche == gift);
					if (i < 0) return;
					const raw = this.value.trim();
					allGifts[i][colKey] = raw;
					const toggleBtn = document.getElementById('cf-' + gift + '-' + rid + '-btn');
					if (toggleBtn) toggleBtn.classList.toggle('has-value', raw !== '');
				});
			});
		}
	}

	function adjustBalances(adjustment, recipient, tranche) {
		if (isNaN(adjustment)) adjustment = 0;
		// TO headline
		console.log('adjustBalances: ', adjustment, recipient, tranche);
		let thisRecip = giftRecipients.find((r) => r.userid == recipient);
		if (thisRecip) {
			thisRecip.after += adjustment;
			const afterElem = BUY_BACK ? null : document.getElementById(recipient + '|After');
			if (afterElem) afterElem.innerHTML = thisRecip.after.toLocaleString('en-US', stringOptions);
		}

		// FROM headline
		balanceAfter -= adjustment;
		const adjustedDenomination = denominationOfTranche(tranche);
		if (adjustedDenomination) headlineAfter[adjustedDenomination] = (headlineAfter[adjustedDenomination] || 0) - adjustment;
		let balanceAfterElem = document.getElementById('balanceAfterElem');
		balanceAfterElem.innerHTML = headlineAmount(balanceAfter, headlineAfter, stringOptions);
		if (headlineOverdrawn()) {
			balanceAfterElem.classList.add('red-text', 'font-bold');
		} else {
			balanceAfterElem.classList.remove('red-text', 'font-bold');
		}

		// FROM table
		let quantityTranche = document.getElementById('quantity' + tranche);
		if (quantityTranche) {
			let quantityBefore = Number(quantityTranche.innerHTML.replace(/[^-.\d]/g, ''));
			let quantityAfter = quantityBefore - adjustment;
			quantityTranche.innerHTML = quantityAfter.toLocaleString('en-US', stringOptions);
			if (quantityAfter < 0) {
				quantityTranche.classList.add('red-text');
				quantityTranche.classList.add('font-bold');
				previewGiftTransfer.classList.add('disabled');
			} else {
				quantityTranche.classList.remove('red-text');
				quantityTranche.classList.remove('font-bold');
				previewGiftTransfer.classList.remove('disabled');
			}
		}

		// FROM total footer
		if (toggle != 'Show Zeros') {
			ttlAfter -= adjustment;
			document.getElementById('totalAfter').innerHTML = ttlAfter.toLocaleString('en-US', stringOptions);
		}

		let thisGift = allGifts.find((g) => g.receiver === recipient && g.tranche === tranche);
		let thisShareClass = thisGift ? thisGift.shareClass : '';
		let price = currentPrice(thisShareClass);
		let totalCurrentValue = document.getElementById('totalCurrentValue'); // need re-rendered element
		if (!isNaN(price)) {
			if (!isKnownNumber(ttlCurrentValue)) ttlCurrentValue = 0;
			ttlCurrentValue += adjustment * price;
		}
		totalCurrentValue.innerHTML = isNaN(ttlCurrentValue) ? 'n.a.' : ttlCurrentValue.toLocaleString('en-US', usd);
		totalCurrentValueDiv.classList.remove('hidden');

		// resize modal
		document.getElementById('viewGiftTransferModal').style.overflowY = 'auto';
	}

	function currentPrice(shareClass) {
		// #6063: shareClass may not resolve to anything in clientData.equity (e.g. adjustBalances falls back to '').
		// Return NaN like any other "no price" case -- callers already check isNaN(price)/currentPrice(...)?.
		let classVals = clientData.equity[shareClass]?.valuations;
		if (!classVals) return NaN;
		classVals.sort((a, b) => Number(b.date) - Number(a.date)); // newest to oldest
		if (!classVals?.[0]) return 'n.a.';
		return Number(classVals[0].sharePrice.replace(/[^.\d]/g, ''));
	}

	async function getNextCertNumber() {
		if (!nextCertNum) {
			lastCertNum = {};
			nextCertNum = {};
			certIssueLog = {};
			if (EXTERNAL_DATA) {
				const dataFile = clientData.transactions_data ? clientData.transactions_data : '';
				if (!dataFile) return;
				let dataFileUrl = await getContentUrl(dataFile);
				try {
					const response = await fetch(dataFileUrl); // 🔹 Ensure fetch waits
					console.log(response);
					const results = await response.text(); // 🔹 Ensure text conversion waits
					// NOTE BENE (3/9/2025): External Data may or may not have a dedicated Certificate column. Painful...
					const data = Papa.parse(results).data;
					const certColumn = data[0][3].toLowerCase().startsWith('cert');
					CERT_COLUMN ||= data.some((row) => row[3]?.includes('#'));
					if (CERT_COLUMN || certColumn) {
						classes.forEach((c) => {
							nextCertNum[c] = '';
							let highestCertNum = 0;
							let classData = data.filter((d) => d[0] == c);
							classData = classData.filter((row) => row[3]?.includes('#') || row[4]?.includes('#'));
							classData.forEach((row) => {
								[3, 4].forEach((index) => {
									// Check only columns 3 and 4
									let cell = row[index];
									if (cell.includes('#')) {
										let certNumber = getCertNumber(cell);
										if (certNumber) {
											let certString = `#${certNumber}`; // Full matched cert (e.g., "#PA-22")
											// Extract numeric portion from certificate (e.g., "22" from "#PA-22")
											let numMatch = certString.match(/(\d+)/);
											if (numMatch) {
												let certNum = Number(numMatch[1], 10);
												if (certNum > highestCertNum) {
													highestCertNum = certNum;
													nextCertNum[c] = certString; // Store full certificate string
												}
											}
										}
									}
								});
							});
							incrementNextCertNumber(c);
							originalLastCertNum ||= {};
							originalLastCertNum[c] = lastCertNum[c];
						});
					}
				} catch (err) {
					console.warn('Something went wrong.', err);
				}
			} else {
				let clientEquity = clientData.equity;
				for (let c in clientEquity) {
					let transactions = clientEquity[c].transactions;
					nextCertNum[c] =
						'#' +
						transactions.reduce((maxCertNum, transaction) => {
							let match = transaction.notes.match(/\(#(\d+)\)/);
							let certNumber = match ? Number(match[1]) : 0;
							return Math.max(maxCertNum, certNumber);
						}, 0); // Start from 1 above the highest certificate number
					incrementNextCertNumber(c);
					originalLastCertNum ||= {};
					originalLastCertNum[c] = lastCertNum[c];
				}
			}
		}
	}

	function incrementNextCertNumber(c) {
		lastCertNum[c] = nextCertNum[c];
		const certNumber = getCertNumber(nextCertNum[c]);
		if (certNumber) {
			let newNumber;
			const suffix = certNumber.split('-')[1];
			if (suffix) newNumber = suffix.replace(/[^\d.]/g, '');
			else newNumber = certNumber.replace(/[^\d.]/g, '');
			if (isNaN(newNumber)) nextCertNum[c] = '';
			// Re-prefix with "#" -- nextCertNum[c] always carries a leading "#" by
			// convention (see its other assignments above/below), but getCertNumber()
			// never includes one in its return value; without re-adding it here, the
			// *next* getCertNumber(nextCertNum[c]) call on this value would find no match.
			else nextCertNum[c] = `#${certNumber.replace(newNumber, Number(newNumber) + 1)}`;
		} else nextCertNum[c] = '';
	}

	function checkVermeerRule(recipient) {
		let header = document.getElementById('qtyHeader|' + recipient);
		let recipGifts = allGifts.filter((g) => g.receiver == recipient);
		if (!recipGifts) {
			header.innerHTML = 'Quantity';
			return;
		}
		let qtyClassE = recipGifts.filter((g) => g.shareClass == 'E').reduce((a, b) => a + Number(b.quantity), 0);
		let qtyOther = recipGifts.filter((g) => ['V', 'NV'].includes(g.shareClass)).reduce((a, b) => a + Number(b.quantity), 0);
		if (qtyClassE == qtyOther) header.innerHTML = 'Quantity';
		else header.innerHTML = '<span class="orange-text" title="E != V + NV">⚠</span> Quantity';
		console.log('header.innerHTML = ' + header.innerHTML);
	}

	function updateConsolidatedTransferCheckbox() {
		const consolidateTransfers = document.getElementById('consolidateTransfers');
		if (!consolidateTransfers) return;

		const counts = {};
		allGifts.forEach((g) => {
			if (!g?.receiver || !g?.tranche) return;
			counts[g.receiver] = (counts[g.receiver] || 0) + 1;
		});

		const enable = Object.values(counts).some((n) => n > 1);
		consolidateTransfers.disabled = !enable;
		if (!enable) consolidateTransfers.checked = false;
	}

	function certFromTransferNotes(notes) {
		const m = String(notes || '').match(/\((#[^\s)]+)/);
		return m ? m[1] : '';
	}

	function rewindNextCertNumber(c, count) {
		while (count-- > 0) {
			const certNumber = getCertNumber(nextCertNum[c]);
			if (!certNumber) return;
			const suffix = certNumber.split('-')[1];
			const currentNumber = suffix ? suffix.replace(/[^\d.]/g, '') : certNumber.replace(/[^\d.]/g, '');
			if (isNaN(currentNumber)) return;
			// Re-prefix with "#" -- see incrementNextCertNumber's comment above.
			nextCertNum[c] = `#${certNumber.replace(currentNumber, Number(currentNumber) - 1)}`;
		}
	}

	async function getConsolidatedTransferGifts() {
		const out = [];
		const giftsByRecipient = {};
		await getNextCertNumber();

		allGifts.forEach((g) => {
			if (!g?.receiver) return;
			if (!giftsByRecipient[g.receiver]) giftsByRecipient[g.receiver] = [];
			giftsByRecipient[g.receiver].push(g);
		});

		Object.values(giftsByRecipient).forEach((gifts) => {
			if (gifts.length < 2) {
				out.push(...gifts);
				return;
			}

			const first = gifts[0];
			const qtyTotal = gifts.reduce((sum, g) => sum + Number(String(g.quantity || 0).replace(/[^-.\d]/g, '')), 0);

			const basisNumerator = gifts.reduce((sum, g) => {
				const qty = Number(String(g.quantity || 0).replace(/[^-.\d]/g, ''));
				const basis = isKnownNumber(g.taxBasis) ? Number(g.taxBasis) : isKnownNumber(g.sharePrice) ? Number(g.sharePrice) : 0;
				return sum + qty * basis;
			}, 0);

			// Cash Sale: Share Price mirrors Tax Basis per tranche, so it needs the
			// same quantity-weighted average as taxBasis above (no-op otherwise).
			const priceNumerator = gifts.reduce((sum, g) => {
				const qty = Number(String(g.quantity || 0).replace(/[^-.\d]/g, ''));
				const price = isKnownNumber(g.sharePrice) ? Number(g.sharePrice) : 0;
				return sum + qty * price;
			}, 0);

			const sourceLabel = compactSourceLabel(gifts);
			const newCertNum = CERT_COLUMN ? certFromTransferNotes(first.notes) : '';
			if (newCertNum && gifts.length > 1) rewindNextCertNumber(first.shareClass, gifts.length - 1);

			out.push({
				...first,
				tranche: gifts.map((g) => g.tranche).join('+'),
				quantity: qtyTotal.toString(),
				taxBasis: qtyTotal ? (basisNumerator / qtyTotal).toString() : first.taxBasis,
				sharePrice: qtyTotal ? (priceNumerator / qtyTotal).toString() : first.sharePrice,
				notes: CERT_COLUMN
					? resolveDescriptionTemplate(
							clientData,
							'consolidated_transfer',
							{ newCert: newCertNum, sourceLabel },
							'Transfer ({newCert} from {sourceLabel})'
						)
					: resolveDescriptionTemplate(clientData, 'consolidated_transfer_no_cert', { sourceLabel }, 'Transfer (from {sourceLabel})'),
				sourceTranches: gifts.map((g) => ({
					tranche: g.tranche,
					quantity: g.quantity,
					taxBasis: g.taxBasis,
					sharePrice: g.sharePrice,
					nrid: g.nrid,
					certNumber: g.certNumber || '',
					sourceNotes: g.sourceNotes || '',
					sourceDate: String(g.sourceDate ?? ''),
				})),
			});
		});

		return out;
	}

	function compactSourceLabel(gifts) {
		const certs = gifts
			.map((g) => {
				if (g.sourceCert || g.certNumber) return g.sourceCert || g.certNumber;
				const refs = parseCertificateRefs(g.notes);
				if (refs.sourceCerts?.length) return refs.sourceCerts[0];
				return '';
			})
			.filter(Boolean)
			.map((c) => String(c).replace(/^#/, '').replace(/^0+/, ''));
		if (certs.length) return certs.join(', ');
		return gifts
			.map((g) => g.tranche)
			.filter(Boolean)
			.join(', ');
	}

	function renderTransactionMapTable(giftsForPreview) {
		let rowErrors;
		giftSources = [];
		let badDataCounter = 0;
		let importTransCounter = 0;
		let pendingBatchRecords = [];
		let pendingBatchAdjustments = [];
		let newTransactionsOk = true;
		let importGiftTransferMarkup;
		// need new object
		let importTransMap = JSON.parse(JSON.stringify(giftsForPreview || allGifts));
		const CANCEL_REISSUE = CERT_COLUMN; //isVERM;	// need for Ausable and Etnyre too.  Maybe Lucas??

		function expandConsolidatedGifts(gifts) {
			const expanded = [];
			gifts.forEach((g) => {
				if (!Array.isArray(g.sourceTranches) || g.sourceTranches.length < 2) {
					expanded.push(g);
					return;
				}

				// Break back into source-level gifts
				g.sourceTranches.forEach((s) => {
					expanded.push({
						...g,
						tranche: s.tranche,
						quantity: s.quantity,
						taxBasis: s.taxBasis,
						sharePrice: s.sharePrice,
						sourceTranches: [s], // now single
						notes: g.notes, // keep consolidated note for UI
					});
				});
			});
			return expanded;
		}

		const stubCalcMap = expandConsolidatedGifts(importTransMap);

		function sourceTranchesForGift(gift) {
			if (Array.isArray(gift.sourceTranches) && gift.sourceTranches.length) {
				return gift.sourceTranches.map((s) => (typeof s === 'string' ? s : s.tranche)).filter(Boolean);
			}

			return String(gift.tranche || '')
				.split('+')
				.map((t) => t.trim())
				.filter(Boolean);
		}

		if (CANCEL_REISSUE) {
			let uniqueTranches = [...new Set(stubCalcMap.flatMap((item) => sourceTranchesForGift(item)).filter(Boolean))];

			uniqueTranches.forEach((t) => {
				let theseSources = giftTransactions.filter((g) => g.tranche == t);
				if (theseSources.length != 1) {
					console.log('Gift source issue:', t, theseSources);
					alert('Gift source issue.');
					return;
				}
				const giftSource = theseSources[0];
				if (giftSource._eventFunded) return; // no ledger record to cancel/reissue or update
				giftSources.push(giftSource);

				const giftDate = stubCalcMap.find((i) => sourceTranchesForGift(i).includes(t))?.date;
				const trancheSum = stubCalcMap.filter((i) => sourceTranchesForGift(i).includes(t)).reduce((acc, j) => acc + Number(j.quantity), 0);
				const stubAmount = Number(giftSource.quantity) - trancheSum;
				const nextCert = nextCertNum[giftSource.shareClass];

				if (stubAmount > 0) {
					// create two more transactions: a cancellation and a reissue
					let cancellation = {
						tranche: t,
						shareClass: giftSource.shareClass,
						securityType: giftSource.securityType,
						date: giftDate,
						notes: giftSource.certNumber
							? resolveDescriptionTemplate(
									clientData,
									'cancellation',
									{ reasonPrefix: '', sourceCert: giftSource.certNumber },
									'{reasonPrefix}Cancellation ({sourceCert})'
								)
							: 'Cancellation',
						sender: giftSource.receiver,
						senderName: giftSource.receiverName,
						receiver: '',
						receiverName: 'Issuer',
						quantity: stubAmount.toString(),
						sharePrice: giftSource.sharePrice || '',
						taxBasis: giftSource._effectiveTaxBasis != null ? giftSource._effectiveTaxBasis.toString() : '',
					};

					let reissuance = {
						tranche: t,
						shareClass: giftSource.shareClass,
						securityType: giftSource.securityType,
						date: giftDate,
						notes: nextCert
							? resolveDescriptionTemplate(
									clientData,
									'reissuance_stub',
									{ nextCert, sourceCert: giftSource.certNumber },
									'Reissuance ({nextCert} from {sourceCert})'
								)
							: 'Reissuance',
						sender: '',
						senderName: 'Issuer',
						receiver: giftSource.receiver,
						receiverName: giftSource.receiverName,
						quantity: stubAmount.toString(),
						sharePrice: giftSource.sharePrice || '',
						taxBasis: giftSource._effectiveTaxBasis != null ? giftSource._effectiveTaxBasis.toString() : '',
					};

					let insertPoint = 0;
					for (let i = importTransMap.length - 1; i >= 0; i--) {
						if (sourceTranchesForGift(importTransMap[i]).includes(t)) {
							insertPoint = i;
							break;
						}
					}

					importTransMap.splice(insertPoint + 1, 0, cancellation);
					importTransMap.splice(insertPoint + 2, 0, reissuance);
					if (nextCert) incrementNextCertNumber(giftSource.shareClass);
				}
			});
		}

		if (selectedTranches.length > 0) {
			let reason = ''; //selectedTranches.length > 1 ? 'Stock Certificate' : 'Stock Certificate';
			let grossQty = 0;
			let grossDollars = 0;
			importTransMap = [];
			selectedTranches.sort();
			selectedTranches.forEach((t) => {
				let giftSource = giftTransactions.find((g) => g.tranche == t);
				giftSources.push(giftSource);
				let consolTranche = {
					tranche: t,
					shareClass: giftSource.shareClass,
					securityType: giftSource.securityType,
					date: Date.now().toString(),
					notes: giftSource.certNumber
						? resolveDescriptionTemplate(
								clientData,
								'cancellation',
								{ reasonPrefix: reason, sourceCert: giftSource.certNumber },
								'{reasonPrefix}Cancellation ({sourceCert})'
							)
						: reason + 'Cancellation',
					sender: giftSource.receiver,
					senderName: giftSource.receiverName,
					receiver: '',
					receiverName: 'Issuer',
					quantity: giftSource.quantity,
					sharePrice: giftSource.sharePrice || '',
					taxBasis: giftSource._effectiveTaxBasis != null ? giftSource._effectiveTaxBasis.toString() : '',
				};
				let consolQty = Number(giftSource.quantity.replace(/[^.\d]/g, ''));
				grossQty += consolQty;
				grossDollars += consolQty * Number(isNaN(giftSource.sharePrice) ? 0 : giftSource.sharePrice.replace(/[^.\d]/g, ''));
				importTransMap.push(consolTranche);
			});

			let reissuance = JSON.parse(JSON.stringify(importTransMap[0]));
			const nextCert = nextCertNum[reissuance.shareClass];
			reissuance.tranche = 'NEW';
			reissuance.notes =
				nextCert && CERT_COLUMN
					? resolveDescriptionTemplate(
							clientData,
							'reissuance_correction',
							{ reasonPrefix: reason, nextCert },
							'{reasonPrefix} Reissuance ({nextCert})'
						)
					: reason + ' Reissuance';
			reissuance.receiver = reissuance.sender;
			reissuance.receiverName = reissuance.senderName;
			reissuance.sender = '';
			reissuance.senderName = 'Issuer';
			reissuance.quantity = grossQty.toString();
			reissuance.sharePrice = (grossDollars / grossQty).toLocaleString('en-US', m5);

			importTransMap.push(reissuance);
			// incrementNextCertNumber(reissuance.shareClass);
		}

		let importTransTarget = importTransMap.length;
		let headings = ['Tranche', 'Date', 'Sender', 'Receiver', 'Description', 'Quantity', 'Share Price', 'Tax Basis'];
		if (CLASS_COLUMN) headings = ['Tranche', 'Class', 'Date', 'Sender', 'Receiver', 'Description', 'Quantity', 'Share Price', 'Tax Basis'];

		// Same per-row toggle as the recipient gift table above, so custom columns
		// stay editable through the final review step.
		const anyImportCustomCols = listTransactionCustomColumns(clientData).filter(usesTransactionCustomColumnKeyStorage).length > 0;
		const importColCount = headings.length + 1 + (anyImportCustomCols ? 1 : 0); // +1 for Status

		// #6144: same Share Price/Tax Basis wording as the Ledgers transaction modals
		// (index.html #transactionSharePriceInput/#transactionTaxBasisInput labels, 2026-08-05
		// per Chris/Stuart) -- one explanation across the app instead of another bespoke one.
		const columnTooltips = {
			'Share Price':
				'Also called Price Paid: the price actually paid or received per\nshare/unit in this transaction. Often $0 for gifts and transfers.\nFor a cash sale, Tax Basis usually equals Share Price.',
			'Tax Basis':
				'Also called Cost Basis: the value used for tax-reporting purposes.\nMay differ from Share Price/Price Paid. For gifts and transfers,\nTax Basis often carries over from the prior owner.',
		};

		// Explicit break (not CSS auto-wrap) so the icon can sit glued to 'Price'/'Basis'
		// with no space to wrap away on.
		const headingBreaks = { 'Share Price': ['Share', 'Price'], 'Tax Basis': ['Tax', 'Basis'] };

		if (!importTransMap || !importTransMap.length) {
			importGiftTransferMarkup = `
			<div class="col s12">
				<div class="py-12 bg-grey-lightest border-blue-dotted rounded center-align">
					<p class="max-w-sm mx-auto">No transfer data found.</p>
				</div>
			</div>
			`;
		} else {
			rowErrors = new Array(importTransMap.length).fill('');
			importGiftTransferMarkup = `
				<div class="row">
					<div class="col s12">
					<h3 id="importGiftTransferHeading" class="text-lg center-align mb-0 font-bold">Ready for Import</h3>
					</div>
				</div>
				<div class="table-wrap border-grey" style="width: fit-content;">
					<div class="sm-hz-scroll-no-margin">
						<table id="importGiftTransferSortTable" class="highlight table-content">
							<thead>
								<tr>
									${anyImportCustomCols ? `<th class="custom-toggle-col"></th>` : ``}
									${headings
										.map(
											(h) => `
										<th class="text-xs center-align" title="${columnTooltips[h] || ''}">
							${
								headingBreaks[h]
									? `${headingBreaks[h][0]}${
											columnTooltips[h]
												? '<i class="material-icons" style="font-size: 10px; vertical-align: middle; color: #999; margin-left: 2px;">info_outline</i>'
												: ''
										}&ensp;&ensp;<br>${headingBreaks[h][1]}`
									: h
							}
						</th>
										`
										)
										.join('')}
									<th class="text-xs center-align">Status</th>
								</tr>
							</thead>
							<tbody>
							${importTransMap
								.map((t, i) => {
									const importCustomCols = applicableGiftCustomColumns(clientData, t.shareClass);
									const importToggleId = 'cf-import-' + i;
									const hasImportCustomValue = importCustomCols.some((col) => t[col.key] !== undefined && t[col.key] !== '');
									return `
								<tr class="import-tranche">
									${
										anyImportCustomCols
											? `
									<td class="custom-toggle-cell center-align">
										${
											importCustomCols.length
												? `<button type="button" id="${importToggleId}-btn" class="custom-field-toggle${hasImportCustomValue ? ' has-value' : ''}" data-target="${importToggleId}" title="Custom Fields">+</button>`
												: ``
										}
									</td>
									`
											: ``
									}
									<td>
										<p class="text-xs mb-0 mt-0 center-align">${t.tranche}</p>
									</td>
									${
										CLASS_COLUMN
											? `
									<td>
										<p class="text-xs mb-0 mt-0 center-align">${t.shareClass}</p>
									</td>`
											: ``
									}
									<td>
										<p id="editableImportDate-${i}" class="text-xs mb-0 mt-0 center-align" contenteditable>${moment
											.unix(Number(t.date) / 1000)
											.utc()
											.format('MMM D, YYYY')}</p>
									</td>
									<td>
										<p class="text-xs mb-0 mt-0 center-align">${safeHtml(t.senderName)}</p>
									</td>
									<td>
										<p class="text-xs mb-0 mt-0 center-align">${safeHtml(t.receiverName)}</p>
									</td>
									<td>
										<p id="editableImportNotes-${i}" class="text-xs mb-0 mt-0 center-align" contenteditable>${safeHtml(t.notes)}</p>
										<!-- <p class="text-xs mb-0 mt-0 center-align">${t.pre && t.notes ? t.pre + ' - ' + t.notes : t.pre ? t.pre : t.notes}</p> -->
									</td>
									<td>
										<p class="text-xs mb-0 mt-0 center-align">${Number(t.quantity).toLocaleString('en-US', stringOptions)}</p>
									</td>
									<td>
										<p id="editableImportSharePrice-${i}" class="text-xs mb-0 mt-0 center-align" contenteditable>${
											isKnownNumber(t.sharePrice)
												? Number(String(t.sharePrice).replace(/[^.\d]/g, '')).toLocaleString('en-US', usp)
												: isNA(t.sharePrice)
													? t.sharePrice
													: 'n.a.'
										}
										</p>
									</td>
									<td>
										<p id="editableImportBasis-${i}" class="text-xs mb-0 mt-0 center-align" contenteditable>${
											isKnownNumber(t.taxBasis)
												? Number(String(t.taxBasis).replace(/[^.\d]/g, '')).toLocaleString('en-US', usp)
												: isNA(t.taxBasis)
													? t.taxBasis
													: 'n.a.'
										}
										</p>
									</td>
									<td id="${'row-' + i}" class="center-align">${dataOkay(t, i) ? greenCheck : redClear}</td>
								</tr>
								${
									importCustomCols.length
										? `
								<tr class="custom-fields-row" id="${importToggleId}">
									<td colspan="${importColCount}">
										<div class="custom-fields-panel">
											${importCustomCols
												.map(
													(col) => `
											<div class="custom-field">
												<label>${escapeGiftFieldAttr(col.label || col.key)}</label>
												<input type="text" class="custom-field-input" data-import-index="${i}" data-col-key="${escapeGiftFieldAttr(col.key)}" value="${escapeGiftFieldAttr(t[col.key] || '')}">
											</div>
											`
												)
												.join('')}
										</div>
									</td>
								</tr>
								`
										: ``
								}
								`;
								})
								.join('')}
							</tbody>
						</table>
					</div>
				</div>
				<div class="flex justify-between items-center">
					<a id="continueEditing" href="#" class="modal-close waves-effect waves-light btn grey mt-6 left">Go Back</a>
	                  <label class="mt-4" title="If checked, cap table (or capital account) balances will be&#10;adjusted according to the allocations. Otherwise the&#10;cap table will remain unchanged." style="transform: scale(0.8);">
	                    <input type="checkbox" id="adjustCapTableAutomatically" checked>
	                    <span style="display:inline-table;">Automatically adjust cap table balances</span>
	                  </label>
					<p class="mr-4 mt-8 text-sm right"><a id="exportGiftTransferMap" href="" download="" target="_blank" rel="noopener noreferrer">Export to Excel</a></p>
					<button id="proceedWithGiftTransferImport" class="right waves-effect waves-light btn mt-6 ml-8 bg-grey-darker">Proceed with Import</button>
				</div>
			`;
		}

		let importGiftTransfer = document.getElementById('importGiftTransfer');
		let importGiftTransferTable = document.getElementById('importGiftTransferTable');
		importGiftTransferTable.innerHTML = importGiftTransferMarkup;
		importGiftTransferTable.classList.remove('hidden');

		let proceedWithGiftTransferImport = document.getElementById('proceedWithGiftTransferImport');
		let importGiftTransferHeading = document.getElementById('importGiftTransferHeading');
		let statementWaitState = document.getElementById('statementWaitState');
		let continueEditing = document.getElementById('continueEditing');
		let processHeader = document.getElementById('processHeader');

		function dataOkay(newTrans, index) {
			if (!capTableData) capTableData = NthUtil.sessionStore.readCapTable(client);
			let validUsers = [];
			capTableData.forEach((u) => validUsers.push(u.userid));
			if (clientData.treasury_account) {
				const t = clientData.treasury_account.split('|')[1]?.trim();
				if (t && !validUsers.includes(t)) validUsers.push(t);
			}
			badDataCounter++;
			let thisSender = capTableData.find((s) => s.userid == newTrans.sender);
			let senderShares = thisSender ? thisSender.clients[client].shares[newTrans.shareClass] : null;
			let senderApproved = senderShares ? senderShares.approved : '0';
			let timeStamp = Number(newTrans.date);
			let types = [
				'Ordinary Shares',
				'Restricted Shares',
				'RSUs',
				'Profits Interest',
				'SARs',
				'Options',
				'Warrants',
				'Convertibles',
				'Debt',
				'Capital Contribution',
				'Partnership Interest',
			];
			if (!classes.includes(newTrans.shareClass)) {
				rowErrors[index] = newTrans.shareClass + ' is not a Class in Company Profile / Equity.';
				return false;
			}
			if (!types.includes(newTrans.securityType)) {
				rowErrors[index] = newTrans.securityType + ' is not a recognized Security Type.';
				return false;
			}
			if (newTrans.strikePrice && newTrans.strikePrice <= 0) {
				rowErrors[index] = newTrans.strikePrice + ' is not a valid Strike Price.';
				return false;
			}
			if (newTrans.expDate && newTrans.expDate.toLowerCase() == 'invalid date') {
				rowErrors[index] = newTrans.expDate + ' is not a valid exp date.';
				return false;
			}
			if (newTrans.date.toLowerCase() == 'invalid date' || timeStamp < 0 || timeStamp > 1924992000000) {
				// December 31, 2030
				rowErrors[index] =
					moment
						.unix(Number(newTrans.date) / 1000)
						.utc()
						.format('MMM D, YYYY') + ' is not a valid date.';
				return false;
			}
			if (!validUsers.includes(newTrans.sender) && newTrans.senderName.toLowerCase() != 'issuer') {
				rowErrors[index] = newTrans.sender + ' is not a valid Sender.';
				return false;
			}
			if (!validUsers.includes(newTrans.receiver) && !['issuer', clientData.company_name.toLowerCase()].includes(newTrans.receiverName.toLowerCase())) {
				rowErrors[index] = newTrans.receiver + ' is not a valid Receiver.';
				return false;
			}
			if (Number(newTrans.quantity) <= 0) {
				rowErrors[index] = newTrans.quantity + ' is not a valid Quantity.';
				return false;
			}
			// if (thisSender && thisSender.userid.toLowerCase() != "issuer" && Number(senderApproved) < Number(newTrans[7])) {
			// 	rowErrors[index] = newTrans[nmCol] + " exceeds Approved balance.";
			// 	return false;
			// }
			if (Number(newTrans.sharePrice) < 0) {
				rowErrors[index] = newTrans.sharePrice + ' is not a valid Share Price.';
				return false;
			}
			if (Number(newTrans.taxBasis) < 0) {
				rowErrors[index] = newTrans.taxBasis + ' is not a valid Tax Basis.';
				return false;
			}
			badDataCounter--;
			return true;
		}

		let num = 'zero one two three four five six seven eight nine ten eleven twelve thirteen fourteen fifteen sixteen seventeen eighteen nineteen'.split(
			' '
		);
		let tens = 'twenty thirty forty fifty sixty seventy eighty ninety'.split(' ');
		function number2words(n) {
			if (n < 20) return num[n];
			let digit = n % 10;
			if (n < 100) return tens[~~(n / 10) - 2] + (digit ? '-' + num[digit] : '');
			if (n < 1000) return num[~~(n / 100)] + ' hundred' + (n % 100 == 0 ? '' : ' and ' + number2words(n % 100));
			return number2words(~~(n / 1000)) + ' thousand' + (n % 1000 != 0 ? ' ' + number2words(n % 1000) : '');
		}

		if (badDataCounter > 0) {
			rowErrors.forEach(function (err, i) {
				if (err.length)
					document.getElementById('row-' + i).innerHTML =
						`<span class="material-icons-outlined align-middle" style="color: red; font-size: 30px;" title="${err}">clear</span>`;
			});
			importGiftTransferHeading.innerHTML =
				capitalize(number2words(badDataCounter)) + (badDataCounter > 1 ? ' rows have' : ' row has') + ' bad data.  Please try again.';
			proceedWithGiftTransferImport.classList.add('disabled');
			// return false;
		} else if (importTransMap && importTransMap.length) {
			importGiftTransferHeading.innerHTML =
				capitalize(number2words(importTransMap.length)) + ' Transaction' + (Number(importTransMap.length) > 1 ? 's' : '') + ' Ready for Import';
			proceedWithGiftTransferImport.classList.remove('disabled');
		}

		let table = document.getElementById('importGiftTransferSortTable');
		if (table) {
			let sort = new Tablesort(table);
			document.getElementById('exportGiftTransferMap').addEventListener('click', function (e) {
				e.preventDefault();
				export_table_to_excel('importGiftTransferSortTable', 'Import Gift Transfer Map (' + new Date().toLocaleString() + ').csv');
			});
		}

		M.Modal.getInstance(viewGiftTransferModal).close();
		M.Modal.getInstance(importGiftTransfer).open();

		document.querySelectorAll('.import-tranche').forEach((tranche) => {
			tranche.addEventListener('keyup', function (e) {
				switch (e.target.id.split('-')[0]) {
					case 'editableImportDate':
						importTransMap[e.target.id.replace('editableImportDate-', '')].date = Date.parse(e.target.innerText).toString();
						break;
					case 'editableImportNotes':
						importTransMap[e.target.id.replace('editableImportNotes-', '')].notes = e.target.innerText;
						break;
					case 'editableImportSharePrice':
						importTransMap[e.target.id.replace('editableImportSharePrice-', '')].sharePrice = Number(
							e.target.innerText.replace(/[^-.\d]/g, '')
						).toLocaleString('en-US', usp);
						break;
					case 'editableImportBasis':
						importTransMap[e.target.id.replace('editableImportBasis-', '')].taxBasis = Number(
							e.target.innerText.replace(/[^-.\d]/g, '')
						).toLocaleString('en-US', usp);
						break;
				}
			});
		});

		// Same toggle pattern as the recipient gift table; edits here write straight
		// onto importTransMap so a value can still be corrected at the last step.
		document.querySelectorAll('.custom-field-toggle').forEach((btn) => {
			if (btn.dataset.cfBound) return;
			btn.dataset.cfBound = '1';
			btn.addEventListener('click', function (e) {
				e.preventDefault();
				const row = document.getElementById(this.dataset.target);
				if (!row) return;
				const isOpen = row.classList.toggle('open');
				this.classList.toggle('open', isOpen);
				this.textContent = isOpen ? '×' : '+';
			});
		});

		document.querySelectorAll('.custom-field-input[data-import-index]').forEach((input) => {
			if (input.dataset.cfBound) return;
			input.dataset.cfBound = '1';
			input.addEventListener('blur', function () {
				const idx = Number(this.dataset.importIndex);
				const colKey = this.dataset.colKey;
				if (!importTransMap[idx]) return;
				const raw = this.value.trim();
				importTransMap[idx][colKey] = raw;
				const toggleBtn = document.getElementById('cf-import-' + idx + '-btn');
				if (toggleBtn) toggleBtn.classList.toggle('has-value', raw !== '');
			});
		});

		if (continueEditing)
			continueEditing.addEventListener('click', function (e) {
				e.preventDefault();
				M.Modal.getInstance(importGiftTransfer).close();
				M.Modal.getInstance(viewGiftTransferModal).open();
			});

		if (proceedWithGiftTransferImport) {
			proceedWithGiftTransferImport.addEventListener('click', async function (e) {
				e.preventDefault();
				M.Modal.getInstance(importGiftTransfer).close();
				M.Modal.getInstance(statementWaitState).open();
				// Wait for axios call completion before continuing
				if (STD_EXT_DATA) compileNewTransactions();
				else importNewTransactions();
			});
		}

		// Rewritten (#5971 Task #38) to route through TransactionService.js's
		// queueExternalWrite/flushExternalQueue instead of the old per-file
		// modifyCSV/uploadCSV/sendToS3 pipeline. STD_EXT_DATA counterpart to
		// importNewTransactions()'s JSON path; same batching semantics.
		// Unlike the old modifyCSV(), this does NOT strip '#' from nrid (only
		// from notes) -- nrid is the row's lookup key (_findRowIndexByNrid),
		// so rewriting it was likely an unintentional side effect, not a fix.
		// Ledger write now happens before cap table balances are touched, so a
		// failed/no-op flush can't leave balances adjusted with no ledger entry (#6140).
		async function compileNewTransactions() {
			statementWaitHeader.innerHTML = 'Compiling ' + importTransMap.length + ' new transaction' + (importTransMap.length > 1 ? 's.' : '.');
			let adjust = document.getElementById('adjustCapTableAutomatically').checked;

			for (let i = 0; i < importTransMap.length; i++) {
				let r = importTransMap[i];

				let record = buildTransactionRecord({
					date: (Number(r.date) / 1000).toFixed(0), // r.date is milliseconds (Date.now()/Date.parse); buildTransactionRecord expects unix-seconds.
					notes: r.notes,
					securityType: r.securityType,
					shareClass: r.shareClass,
					sharePrice: r.sharePrice,
					quantity: r.quantity,
					sender: r.sender,
					receiver: r.receiver,
					senderName: r.senderName,
					receiverName: r.receiverName,
					taxBasis: r.taxBasis,
					// no expDate/strikePrice: gift-transfer tranches are plain
					// share reissuance/cancellation, never derivative
					// securities -- matches the old modifyCSV, which also
					// always left those two columns blank for these rows.
					customColumns: giftCustomColumnSnapshot(r, clientData),
				});
				queueExternalWrite({ record, action: 'create' });
			}

			for (let i = 0; i < giftSources.length; i++) {
				let s = giftSources[i];

				// GH: quantity comes from pristineQuantityByNrid, NOT s.quantity -- s is
				// the same giftTransactions object reference the matching engine may have
				// mutated in place to hold a partially-consumed lot's remaining balance.
				// This is a real full-record-replace write of the source transaction's own
				// row (action:'update' below), so sending s.quantity here would silently
				// overwrite its real persisted quantity with that analysis-computed
				// remainder -- same bug as the Tax Basis inline-edit fix above.
				let record = buildTransactionRecord({
					date: s.date,
					expDate: s.expDate,
					notes: s.notes.replace('#', ''),
					securityType: s.securityType,
					sharePrice: s.sharePrice,
					quantity: pristineQuantityByNrid.has(s.nrid) ? pristineQuantityByNrid.get(s.nrid) : s.quantity,
					sender: s.sender,
					receiver: s.receiver,
					senderName: s.senderName,
					receiverName: s.receiverName,
					strikePrice: s.strikePrice,
					taxBasis: s.taxBasis,
					shareClass: s.shareClass,
					// Full-record-replace endpoint -- omitting custom columns would blank them.
					customColumns: giftCustomColumnSnapshot(s, clientData),
				});
				record.nrid = s.nrid;
				queueExternalWrite({ record, action: 'update' });
			}

			let ok = false;
			try {
				ok = await flushExternalQueue({
					userid: authedUser,
					cookie,
					client,
					clientData,
					isACR: typeof isACR !== 'undefined' && isACR,
				});
			} catch (err) {
				console.error(err);
			}

			if (!ok) {
				statementWaitHeader.innerHTML = 'Import failed.';
				showToast('error-toast', 'Could not save these transactions to the ledger. Nothing was changed -- please try again.');
				M.Modal.getInstance(statementWaitState).close();
				return;
			}

			if (giftSources[0]) incrementNextCertNumber(giftSources[0].shareClass);

			if (adjust) {
				for (let i = 0; i < importTransMap.length; i++) {
					let r = importTransMap[i];
					const params = {
						shareClass: r.shareClass,
						quantity: r.quantity.replace(/[^-.\d]/g, ''),
						senderId: r.sender,
						receiverId: r.receiver,
					};
					// GH #6160: keep going past a failed/skipped tranche instead of
					// losing the rest of the batch, and surface any failures below.
					try {
						const result = await adjustCapTable({ client, userid: authedUser, cookie, capTableData, shareholderData, adjustParams: params });
						if (!result?.senderAdjusted) {
							console.error('adjustCapTable: sender-side cap table adjustment skipped for', params, result?.reason);
							showToast(
								'error-toast',
								`Ledger updated, but the cap table balance could not be fully adjusted (${result?.reason || 'see console'}). Please reconcile manually.`
							);
						}
					} catch (err) {
						console.error('adjustCapTable threw for', params, err);
						showToast('error-toast', 'Ledger updated, but the cap table balance could not be adjusted for one tranche. Please reconcile manually.');
					}
				}
			}

			document.dispatchEvent(new Event('RefreshSessionStorage'));
			clearPendingRequests('Processed');
		}

		async function importNewTransactions() {
			statementWaitHeader.innerHTML = 'Importing ' + importTransMap.length + ' new transaction' + (importTransMap.length > 1 ? 's.' : '.');

			// Import transactions sequentially
			for (let i = 0; i < importTransMap.length; i++) {
				await createNewTransaction(transParams(importTransMap[i]), false);
			}

			// createNewTransaction() already surfaced an error and left balances
			// untouched if the ledger write failed -- don't also consume gift
			// sources (reduce the sender's position) against transactions that
			// were never actually created.
			if (!newTransactionsOk) {
				M.Modal.getInstance(statementWaitState).close();
				return;
			}

			console.log('Finished importing transactions.');

			// Process gift sources sequentially -- routed through the shared service (#5971)
			// instead of a raw axios.patch, which (1) ignored EXTERNAL_DATA branching (an
			// Alera client is EXTERNAL_DATA but not STD_EXT_DATA) and (2) omitted
			// expDate/strikePrice, silently wiping them on derivative gift sources since this
			// hits a full-record-REPLACE endpoint. Both fixed: expDate/strikePrice are read
			// off giftSources[i] and included like every other field.
			for (let i = 0; i < giftSources.length; i++) {
				const source = giftSources[i];
				const shareClass = source.shareClass;
				const transaction = {
					date: source.date.toString(),
					expDate: source.expDate,
					notes: source.notes.replace('#', ''),
					securityType: source.securityType,
					sharePrice: (source.sharePrice || '').replace(/[^.\d]/g, ''),
					quantity: source.quantity.replace(/[^-.\d]/g, ''),
					sender: source.sender,
					receiver: source.receiver,
					senderName: source.senderName,
					receiverName: source.receiverName,
					strikePrice: source.strikePrice,
					nrid: source.nrid,
					// Negative Tax Basis not allowed (per Chris, 2026-07-30).
					taxBasis: (source.taxBasis || '').replace(/[^.\d]/g, ''),
					// Full-record-replace PATCH -- omitting custom columns would wipe them.
					...giftCustomColumnSnapshot(source, clientData),
				};

				try {
					if (EXTERNAL_DATA) {
						queueExternalWrite({ record: transaction, action: 'update' });
					} else {
						await patchTransactionRemote({ userid: authedUser, cookie, client, clientData, shareClass, transaction });
					}
				} catch (err) {
					console.error(err);
				}
			}

			// EXTERNAL_DATA clients: flush the whole batch once, same as
			// createNewTransaction's own batching above -- one CSV re-upload for
			// every gift source processed, not one per source.
			if (EXTERNAL_DATA && giftSources.length) {
				try {
					const ok = await flushExternalQueue({
						userid: authedUser,
						cookie,
						client,
						clientData,
						isACR: typeof isACR !== 'undefined' && isACR,
					});
					if (!ok) showToast('error-toast', 'New transactions saved, but updating the gift source(s) failed -- please check the ledger.');
				} catch (err) {
					console.error(err);
					showToast('error-toast', 'New transactions saved, but updating the gift source(s) failed -- please check the ledger.');
				}
			}

			document.dispatchEvent(new Event('RefreshSessionStorage'));
			console.log('Completed processing gift sources.');
			if (CERT_COLUMN) incrementNextCertNumber(giftSources[0]?.shareClass);
		}

		// Transaction parameters helper
		function transParams(row) {
			return {
				shareClass: row.shareClass,
				securityType: row.securityType,
				date: (Number(row.date) / 1000).toFixed(0),
				notes: row.notes,
				sender: row.sender,
				receiver: row.receiver,
				senderName: row.senderName,
				receiverName: row.receiverName,
				quantity: row.quantity.replace(/[^-.\d]/g, ''),
				sharePrice: String(row.sharePrice || '').replace(/[^.\d]/g, ''),
				// Negative Tax Basis not allowed (per Chris, 2026-07-30).
				taxBasis: String(row.taxBasis || '').replace(/[^.\d]/g, ''),
				customColumns: giftCustomColumnSnapshot(row, clientData),
			};
		}

		// Unified with Ledgers' own createNewTransaction (#5971) instead of a
		// hand-rolled axios.put() that duplicated _buildParams and never
		// branched on EXTERNAL_DATA (CSV clients wrote straight to JSON).
		// Also fixed: senderName/receiverName were only set in the "one side is
		// Issuer" branches, leaving both undefined in the regular case.
		// Records a created transaction's nrid against the originating transfer-request
		// execution (admin-review-design doc, Section 3 Change 3). Best-effort: log on
		// failure rather than throwing, since the transaction already exists either way.
		async function recordTransferExecutionTransaction(nrid) {
			if (!options.wfExecutionId || !nrid) return;
			try {
				await window.axios.post(
					wfExecutionsProgressURL,
					JSON.stringify({
						client,
						userid: authedUser,
						cookie,
						execution_id: options.wfExecutionId,
						action_type: 'transaction_created',
						reference_id: nrid,
					})
				);
			} catch (err) {
				console.error('[gift-transfer] recordTransferExecutionTransaction failed:', err);
			}
		}

		async function createNewTransaction(transParams, isJustOne) {
			if (!isJustOne) importTransCounter++;

			// Only the genuinely page-specific parts stay here: this function's
			// only caller (transParams(), from the bulk gift-CSV importer) already
			// hands over bare sender/receiver ids and separate name fields (no
			// "Name | id" pipe-format to split, unlike Ledgers' <select> values),
			// and already-unix-seconds date/expDate (converted from the CSV row
			// before this function ever sees them -- do NOT run these through
			// dateStringToUnix, which expects a date STRING and silently blanks
			// an already-numeric value; caught and reverted here 2026-07-30).
			let shareClass = DOMPurify.sanitize(transParams.shareClass);
			let quantity = DOMPurify.sanitize(transParams.quantity.replace(/[^-.\d]/g, ''));
			let sender = DOMPurify.sanitize(transParams.sender);
			let receiver = DOMPurify.sanitize(transParams.receiver);

			let senderId, receiverId, senderName, receiverName;
			if (sender.startsWith('Issuer')) {
				receiverId = receiver;
				senderName = 'Issuer';
				receiverName = DOMPurify.sanitize(transParams.receiverName || '');
			} else if (receiver.startsWith('Issuer')) {
				senderId = sender;
				receiverName = 'Issuer';
				senderName = DOMPurify.sanitize(transParams.senderName || '');
			} else {
				senderId = sender;
				receiverId = receiver;
				senderName = DOMPurify.sanitize(transParams.senderName || '');
				receiverName = DOMPurify.sanitize(transParams.receiverName || '');
			}

			// Sanitize + full-record shape now shared with Ledgers' own
			// createNewTransaction via TransactionService.js's
			// buildTransactionRecord() (#5971, Round Two, 2026-07-30) -- this used
			// to duplicate that logic independently and had drifted from it.
			let newTransaction = buildTransactionRecord({
				date: transParams.date,
				expDate: transParams.expDate,
				notes: transParams.notes,
				shareClass,
				securityType: transParams.securityType,
				strikePrice: transParams.strikePrice,
				sharePrice: transParams.sharePrice,
				taxBasis: transParams.taxBasis,
				quantity,
				sender: senderId,
				receiver: receiverId,
				senderName,
				receiverName,
				customColumns: transParams.customColumns,
			});

			const adjust = document.getElementById('adjustCapTableAutomatically').checked;
			const adjustParams = { shareClass, quantity, senderId, receiverId };

			// adjustCapTable() only runs once its ledger write is confirmed (#6140).
			// EXTERNAL_DATA flushes once per batch (last row), so each row's
			// adjustment is deferred in pendingBatchAdjustments until that succeeds.
			try {
				if (EXTERNAL_DATA) {
					queueExternalWrite({ record: newTransaction, action: 'create' });
					pendingBatchRecords.push(newTransaction);
					pendingBatchAdjustments.push(adjustParams);

					// Flush once per batch (last row), matching Ledgers' bulk-import
					// behavior, instead of one full CSV re-upload per row.
					if (isJustOne || importTransCounter >= importTransTarget) {
						const ok = await flushExternalQueue({
							userid: authedUser,
							cookie,
							client,
							clientData,
							isACR: typeof isACR !== 'undefined' && isACR,
						});

						if (!ok) {
							newTransactionsOk = false;
							showToast('error-toast', 'Could not save these transactions to the ledger. Nothing was changed -- please try again.');
							pendingBatchRecords = [];
							pendingBatchAdjustments = [];
							return;
						}

						// Record every row in the batch, not just the current one. Read .nrid
						// AFTER the flush resolves -- _applyCreate() assigns it in place, so
						// reading earlier (round 1 fix) silently recorded nothing.
						for (const rec of pendingBatchRecords) {
							await recordTransferExecutionTransaction(rec.nrid);
						}

						if (adjust) {
							for (const params of pendingBatchAdjustments) {
								const result = await adjustCapTable({
									client,
									userid: authedUser,
									cookie,
									capTableData,
									shareholderData,
									adjustParams: params,
								});
								if (!result?.senderAdjusted) {
									console.error('adjustCapTable: sender-side cap table adjustment skipped for', params, result?.reason);
									showToast(
										'error-toast',
										`Ledger updated, but the cap table balance could not be fully adjusted (${result?.reason || 'see console'}). Please reconcile manually.`
									);
								}
							}
						}

						pendingBatchRecords = [];
						pendingBatchAdjustments = [];
					}
				} else {
					const created = await createTransactionRemote({
						userid: authedUser,
						cookie,
						client,
						clientData,
						shareClass,
						transaction: newTransaction,
					});
					newTransaction.nrid = created.nrid;
					await recordTransferExecutionTransaction(created.nrid);

					if (adjust) {
						const result = await adjustCapTable({ client, userid: authedUser, cookie, capTableData, shareholderData, adjustParams });
						if (!result?.senderAdjusted) {
							console.error('adjustCapTable: sender-side cap table adjustment skipped for', adjustParams, result?.reason);
							showToast(
								'error-toast',
								`Ledger updated, but the cap table balance could not be fully adjusted (${result?.reason || 'see console'}). Please reconcile manually.`
							);
						}
					}
				}

				if (!isJustOne && importTransCounter >= importTransTarget) {
					clearPendingRequests('Processed');
				}
			} catch (err) {
				console.error(err);
				newTransactionsOk = false;
				showToast('error-toast', 'Could not save this transaction to the ledger. Please check the ledger before retrying.');
			}
		}
	}

	async function clearPendingRequests(newStatus) {
		// Get transfer requests for this user
		let requests = shareholderData.clients[client].transfer_requests;
		if (requests && Object.keys(requests).length > 0) {
			statementWaitHeader.innerHTML = 'Your browser will refresh. Stand by...';

			// Update all "Open" gifts' status to "Processed"
			Object.values(requests)
				.flat()
				.forEach((gift) => {
					if (gift.status === 'Open') gift.status = newStatus;
				});

			// Save updated data
			sessionStorage.setItem('shareholder.Data', JSON.stringify(shareholderData));

			try {
				const response = await axios.post(
					'https://api.nthround.io/portal/user/update-user-client-data',
					JSON.stringify({
						client: client,
						userid: authedUser,
						cookie: cookie,
						holder: shareholder,
						data: shareholderData.clients[client],
					})
				);
				console.log(response);
				showToast('success-toast', 'Data updated successfully!');
				document.dispatchEvent(new Event('RefreshSessionStorage'));
				setTimeout(() => {
					M.Modal.getInstance(statementWaitState).close();
					location.reload();
				}, 5000);
			} catch (error) {
				console.error(error);
			}
		} else {
			showToast('success-toast', 'Data updated successfully!');
			document.dispatchEvent(new Event('RefreshSessionStorage'));
			M.Modal.getInstance(statementWaitState).close();
			document.dispatchEvent(new CustomEvent('renderShareholderView', { detail: { shareholder: shareholder, refreshed: true } }));
		}
	}

	function sendThisRequest() {
		let timestamp = Date.now().toString();
		const waitingModal = document.getElementById('waitingModal');
		M.Modal.getInstance(viewGiftTransferModal).close();
		M.Modal.getInstance(waitingModal).open();
		// previewGiftTransfer.classList.add('disabled');
		// previewGiftTransfer.innerHTML = "One moment please...";
		let purpose = BUY_BACK ? labelBB + ' Request' : 'Share Transfer Request';
		let giftHtml =
			giftHeadline.innerHTML +
			'<br>' +
			balanceHeadline.innerHTML.replace('Shares Owned Now:', 'Shares Owned:') +
			'<br>' +
			userGiftTransferTable.innerHTML +
			'<br>' +
			totalCurrentValueDiv.innerHTML +
			'<br>' +
			viewRecipients.innerHTML;
		giftHtml = cleanAndFormatHtml(giftHtml);

		function cleanAndFormatHtml(giftHtml) {
			// Parse the raw HTML into a DOM object
			const parser = new DOMParser();
			const doc = parser.parseFromString(giftHtml, 'text/html');

			// Add professional heading at the top
			const headingDiv = document.createElement('div');
			const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
			headingDiv.innerHTML = `
				<h1 style="text-align: center; font-size: 20px; color: #2c3e50; margin: 34px 0 5px;">${purpose}</h1>
				<div style="text-align: center; font-size: 14px; color: #555; margin: 0 0 20px;">Made on ${today} by ${safeHtml(userData.user_name)}</div>
			`;

			doc.getElementById('showHidePriorsDiv')?.remove();

			// Inject the heading before the content
			const body = doc.body;
			body.insertBefore(headingDiv, body.firstChild);

			// Remove all hidden elements
			doc.querySelectorAll(".hidden, [style*='display: none']").forEach((el) => el.remove());

			// Remove "Remove" buttons
			doc.querySelectorAll('.remove-gift-recipient').forEach((el) => el.remove());

			// Remove checkboxes but retain the "Tranche" designation
			doc.querySelectorAll("input[type='checkbox']").forEach((checkbox) => {
				const td = checkbox.closest('td');
				if (td) {
					const trancheDesignation = td.querySelector('div.shareholder-img');
					if (trancheDesignation) {
						td.innerHTML = trancheDesignation.outerHTML; // Keep the tranche designation
					} else {
						td.innerHTML = ''; // Replace with empty content if no designation exists
					}
				}
			});

			// Add professional styles
			const style = `
				<style>
					body {
						font-family: 'Source Sans Pro', sans-serif;
						font-size: 12px;
						margin: 1in;
					}
					h1, h2, h5 {
						color: #2c3e50;
						text-align: center;
						margin: 0.1in 0 0.1in;
					}
					.table-heading {
						background-color: #2c3e50;
						color: white;
						font-weight: bold;
						text-align: center;
						padding: 4px;
						font-size: 12px; /* Larger heading size */
					}
					table {
						width: 100%;
						border-collapse: collapse;
						margin: 0.1in 0;
					}
					th, td {
						border: 1px solid #ddd;
						text-align: center;
						padding: 4px;
						font-size: 12px; /* Table font size remains consistent */
					}
					th {
						background-color: #f4f4f4;
						font-weight: bold;
						font-size: 13px; /* Slightly larger than table rows */
					}
					strong {
						font-weight: bold;
						font-size: 14px; /* Larger font size for names */
					}
					.ownership {
						font-weight: bold;
						font-size: 14px; /* Match label size for numerals */
						color: #555;
					}
					.recip-div {
						margin-top: 0.2in; /* Add vertical spacing between recipient sections */
					}
				</style>
			`;

			// Inject the styles into the document head
			const styleElement = document.createElement('style');
			styleElement.innerHTML = style;
			doc.head.appendChild(styleElement);

			// Add "To: " before each recipient's name and ensure font size matches sender's name
			doc.querySelectorAll('.recip-div .row .col.s12.m6').forEach((el) => {
				el.innerHTML = `To: <strong>${el.textContent.trim()}</strong>`;
				el.querySelector('strong').style.fontSize = '14px'; // Larger size for names
			});

			// Make sender's name bold and ensure proper size
			const senderElement = doc.querySelector('span.text-light');
			if (senderElement) {
				senderElement.innerHTML = `<strong>${senderElement.textContent.trim()}</strong>`;
				senderElement.querySelector('strong').style.fontSize = '14px'; // Match recipient font size
			}

			// Ensure before/after ownership numerals match label size
			doc.querySelectorAll('.text-primary').forEach((el) => {
				el.style.fontSize = '14px'; // Match the subheading labels
				el.style.fontWeight = 'bold'; // Ensure boldness
				el.style.color = '#555'; // Consistent coloring
			});

			// Remove empty rows at the bottom of each recipient's section
			doc.querySelectorAll('.recip-div table tbody').forEach((tbody) => {
				const rows = Array.from(tbody.querySelectorAll('tr'));
				rows.forEach((row) => {
					const isEmptyRow = Array.from(row.cells).every((cell) => !cell.textContent.trim());
					if (isEmptyRow) row.remove();
				});
			});

			// Reduce vertical spacing for p, .row, and tables
			doc.querySelectorAll('p, .row, table').forEach((el) => {
				el.style.marginTop = '0.1in';
				el.style.marginBottom = '0.1in';
			});

			// Return the cleaned and formatted HTML as a string
			return doc.documentElement.outerHTML;
		}

		const giftString = JSON.stringify(theseGifts);
		const giftBlob = new Blob([JSON.stringify(theseGifts)], { type: 'application/json' });
		const giftFile = new File([giftBlob], purpose.replace(/ /g, '_') + '_from_' + userData.userid + '.json', { type: 'application/json' });

		uploadGiftRequestToYourDocs();

		async function uploadGiftRequestToYourDocs() {
			// Prepare new document to be added to the user's documents
			let newDoc = {
				type: 'Call/Notice',
				tag: 'ALL',
				name: purpose,
				investor: userData.userid,
				description: 'From ' + userData.user_name,
				require2Factor: false,
				date: moment().format('MMM D, YYYY'),
				id: timestamp,
			};

			// Prepare parameters for the document upload API
			let params = {
				...newDoc,
				client: client,
				userid: authedUser,
				cookie: cookie,
				htmlResults: giftHtml,
				callOrigin: 'gift-transfer',
			};

			// Add transfer_request to User json
			let userTransferRequests = userData.clients[client].transfer_requests || {};

			// Initialize an array for the timestamp if not present
			userTransferRequests[timestamp] = userTransferRequests[timestamp] || [];

			// Push all gifts into the transfer_requests, each with a status
			allGifts.forEach((gift) => {
				userTransferRequests[timestamp].push({ ...gift, status: 'Open' });
			});

			// Update the userData object and save it to session storage
			userData.clients[client].transfer_requests = userTransferRequests;
			sessionStorage.setItem('user.Data', JSON.stringify(userData));

			// Prepare the payload for the DynamoDB update API
			let dynamoDbPayload = {
				userid: authedUser,
				client: client,
				cookie: cookie,
				holder: userData.userid,
				data: userData.clients[client],
			};

			// Call both APIs sequentially
			try {
				// 1. Update transfer_requests in DynamoDB
				const dynamoRes = await axios.post('https://api.nthround.io/portal/user/update-user-client-data', JSON.stringify(dynamoDbPayload));
				console.log('DynamoDB update response:', dynamoRes);

				// 2. Upload the document
				const docRes = await axios.post('https://api.nthround.io/portal/auth/upload-to-your-docs', params);
				console.log('upload-to-your-docs response:', docRes);
				if (AppState.activeRequestsCampaign?.send_emails) sendGiftRequestEmail(docRes.data.pdf_url);

				// If both succeed
				console.log('Transfer Request successfully uploaded and transfer_requests updated.');

				let holder = userData.userid; // preview breadcrumb
				document.dispatchEvent(new Event('RefreshSessionStorage'));
				setTimeout(() => {
					document.getElementById('waitingHeader').innerHTML = 'Success!!';
					document.getElementById('waitingImage').src = './../../resources/assets/celebrate-v5.svg';

					// previewGiftTransfer.classList.remove('disabled');
					// previewGiftTransfer.innerHTML = "Request These Transfers";

					M.Modal.getInstance(waitingModal).options.onCloseEnd = () => {
						if (sessionStorage.getItem('preview'))
							document.dispatchEvent(new CustomEvent('previewShareholder', { detail: { uid: holder, path: '' } }));
						else document.dispatchEvent(new Event('renderInvestorPortal'));
					};
				}, 2000);
			} catch (err) {
				// Log any errors from either API call
				console.error('Error during transfer request processing:', err);
			}
		}

		function sendGiftRequestEmail(pdfUrl) {
			const ending = `<br><br>${firstName(userData)}<br><br><a href="https://${client}.nthround.io/admin/users/edit/?email=${userData.userid}">View this ${purpose}</a>`;
			axios
				.post(
					'https://api.nthround.io/portal/auth/send-email',
					JSON.stringify({
						client: client,
						userid: authedUser,
						cookie: cookie,
						sender_email: 'messages@nthround.com',
						sender_name: userData.user_name,
						replyto_email: userData.userid,
						recipient: clientData.contact_email,
						cc_emails: [userData.userid],
						subject: purpose,
						message:
							'Hi--<br><br>Please review this ' +
							purpose +
							'.<br><br>I understand that you will reach out to me for confirmation prior to processing.<br><br>Thank you!' +
							ending +
							'<br><br>[Note: email replies will go to ' +
							userData.user_name +
							' &lt;' +
							userData.userid +
							'&gt;.]',
						attachment: pdfUrl,
					})
				)
				.then(function (res) {
					const btnGroupLink = document.getElementById('btnGroupLink');
					const leaveMessageLink = document.getElementById('leaveMessageLink');
					const leaveMessageModal = document.getElementById('leaveMessageModal');
					leaveMessageLink.classList.add('disabled');
					leaveMessageLink.innerHTML = purpose + ' Pending';
					btnGroupLink.classList.replace('bg-grey-darker', 'grey');
					leaveMessageModal.innerHTML = `
					<div class="p-2 center-align">
						<div class="mb-4"><img src="./../../resources/assets/celebrate-v5.svg" alt="Musicians celebrating" class="responsive-img px-12"/></div>
						<h3 class="text-lg mb-4 mt-4 font-bold">Your request has been sent!</h3>
						<p class="text-secondary text-sm mt-0 mb-0">We emailed a copy to ${userData.userid}.</p>
						<p class="text-secondary text-sm mt-0 mb-0">The company will reach out shortly to confirm your request.</p>
						<p class="text-secondary text-xs mt-0 mb-0">(On closing this modal your browser will refresh.)</p>
					</div>
				`;
				})
				.catch(function (err) {
					console.error(err);
				});
		}
	}

	function toNumber(str) {
		let num = Number(str);
		return isNaN(num) ? 1 : num; // assumed share price, zero is trouble mathwise!
	}

	function initModal() {}

	// Reinitialize M.Modal to include dynamically added instances
	// var modals = document.querySelectorAll('.modal');
	// let modalOptions = { onOpenStart: initModal };
	// var instances = M.Modal.init(modals, modalOptions);
}

export { giftTransfer, resetGiftTransferState };
